import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';
import * as XLSX from 'xlsx';
// 업무 상세 - 엑셀 내보내기(downloadExcel)는 인쇄화면(handlePrint)과 동일한 레이아웃(제목+정보표+
// 업무내용+히스토리)으로 서식(굵게/배경색/줄바꿈 등)을 입혀 만들어야 해서, 순수 데이터만 다루는
// xlsx(SheetJS) 대신 스타일 지정이 되는 ExcelJS를 함께 사용한다(Schedules.js와 동일한 방식).
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import { usePermissions } from '../hooks/usePermissions';
import { logDownload, logPrint } from '../utils/eventLog';
import { printedByLabel } from '../utils/userLabel';
import FileDropZone from '../components/FileDropZone';
import { getKstDateString } from '../utils/dateKst';
// "견적서" 버튼(회의록 버튼 왼쪽) — 영업관리(SalesActivities.js)에 이미 있는 견적서 목록/작성
// 모달을 그대로 재사용한다(업무관리 전용 견적서 기능을 새로 만들지 않고, Sales.js가
// SalesActivities의 DetailModal을 가져다 쓰는 것과 동일한 방식으로 화면만 공유).
import { QuotationListModal } from './SalesActivities';

const STATUS_COLOR   = { '진행중':'#185FA5','완료':'#0F6E56','보류':'#993556','대기':'#854F0B' };
const STATUS_BG      = { '진행중':'#E6F1FB','완료':'#E1F5EE','보류':'#FBEAF0','대기':'#FAEEDA' };
const PRIORITY_COLOR = { '높음':'#dc2626','보통':'#d97706','낮음':'#16a34a' };
const PRIORITY_BG    = { '높음':'#fee2e2','보통':'#fef3c7','낮음':'#dcfce7' };

// 구분(카테고리): 예전에는 이 파일에 하드코딩되어 있었는데(CAT_MAP 등), 이제는 "관리항목"
// 버튼(내보내기 ↔ 회의록 사이)에서 관리자가 직접 등록/수정/삭제/순서변경할 수 있도록
// 백엔드(task_categories 테이블)로 옮겼다. 아래 DEFAULT_TASK_CATEGORIES는 서버 응답이
// 오기 전(최초 렌더) 잠깐 보여줄 기본값이자, 최초 설치 시 DB에 동일하게 시드되는 값과 같다.
// 목록 필터에는 짧은 이름(3글자, short_label), 실제 저장값/등록폼에는 정식명칭(full_label) 사용.
const DEFAULT_TASK_CATEGORIES = [
  { id:-1, short_label:'일반', full_label:'일반업무', color:'#0EA5E9', bg:'#E0F2FE', sort_order:1 },
  { id:-2, short_label:'인증', full_label:'인증관리', color:'#D97706', bg:'#FEF3C7', sort_order:2 },
  { id:-3, short_label:'지원', full_label:'지원사업', color:'#059669', bg:'#D1FAE5', sort_order:3 },
  { id:-4, short_label:'개발', full_label:'제품개발', color:'#DB2777', bg:'#FCE7F3', sort_order:4 },
  { id:-5, short_label:'영업', full_label:'영업지원', color:'#DC2626', bg:'#FEE2E2', sort_order:5 },
  { id:-6, short_label:'납품', full_label:'제품납품', color:'#EA580C', bg:'#FFEDD5', sort_order:6 },
  { id:-7, short_label:'생산', full_label:'제품생산', color:'#7C3AED', bg:'#EDE9FE', sort_order:7 },
  { id:-8, short_label:'기타', full_label:'기타업무', color:'#64748B', bg:'#F1F5F9', sort_order:8 },
];
// task_categories 행 배열을 예전 CAT_SHORT_LIST/CAT_MAP/CAT_FULL_LIST/CAT_TAB_COLOR/CAT_TAB_BG/
// CAT_FULL_TO_SHORT 형태로 변환 — 아래 각 컴포넌트에서 이 결과(catMeta)를 그대로 예전 상수처럼 사용한다.
function buildTaskCatMeta(categories) {
  const cats = (categories && categories.length) ? categories : DEFAULT_TASK_CATEGORIES;
  return {
    list: cats,
    shortList: ['전체', ...cats.map(c => c.short_label), '회의록'],
    map: Object.fromEntries(cats.map(c => [c.short_label, c.full_label])),
    fullList: cats.map(c => c.full_label),
    tabColor: { '전체':'#475569', ...Object.fromEntries(cats.map(c => [c.short_label, c.color])) },
    tabBg: { '전체':'#E2E8F0', ...Object.fromEntries(cats.map(c => [c.short_label, c.bg])) },
    tabLabel: { '전체':'업무현황' },
    fullToShort: Object.fromEntries(cats.map(c => [c.full_label, c.short_label])),
  };
}
// 관리항목(구분) 목록을 불러와 위 형태로 가공해주는 공용 훅 — Tasks 화면과, 구분을 쓰는 하위
// 모달들(DetailModal/TaskForm/TaskRequestModal/MeetingMinutesModal) 각각에서 호출한다.
function useTaskCategories() {
  const [categories, setCategories] = useState(DEFAULT_TASK_CATEGORIES);
  const reloadCategories = () => API.get('/tasks/categories').then(r => { if (r.data && r.data.length) setCategories(r.data); }).catch(() => {});
  useEffect(() => { reloadCategories(); }, []);
  const catMeta = useMemo(() => buildTaskCatMeta(categories), [categories]);
  return { categories, catMeta, reloadCategories };
}

// 회의록의 "해당업무 선택" 목록 중, 일반업무 하위에 추가되는 특수 회의유형
// (선택 시 일반업무에 동일 제목의 업무가 자동 생성/연동되고, 최초 생성 시 일정관리에도 제목이 자동 등록됨)
const GENERAL_MEETING_TYPES = ['주간회의', '경영회의', '일반회의'];

// 목록 상단 빠른 상태 필터
const QUICK_STATUS = ['모두보기','진행중','완료','지연','보류'];
const QUICK_STATUS_LABEL = { '모두보기':'전체보기' }; // 표시 라벨만 변경, 내부 값('모두보기')은 그대로 사용

// 업무등록의 공개범위 옛 방식(4단계) — 새 등록/수정 폼에서는 더 이상 선택하지 않고
// 계정관리 - 공개범위 설정에서 만든 공개구분을 그대로 사용합니다(공지사항과 동일).
// 다만 이 값으로 저장된 기존 업무 데이터는 계속 정상 표시/필터링되도록 VisBadge/visLabel에서 하위호환합니다.
const VIS_LIST = [
  { val:'전체공개',   label:'전체공개',   desc:'모든 회원에게 공개', color:'#0F6E56', bg:'#E1F5EE' },
  { val:'관리자이상', label:'관리자이상', desc:'최고관리자 + 관리자만', color:'#185FA5', bg:'#E6F1FB' },
  { val:'협력사이상', label:'협력사이상', desc:'관리자 + 협력사까지', color:'#854F0B', bg:'#FAEEDA' },
  { val:'개인지정',   label:'개인지정',   desc:'특정 아이디만 선택', color:'#7C3AED', bg:'#EDE9FE' },
];
const visLabel = (val) => (VIS_LIST.find(v => v.val === val)?.label || (val || '전체공개'));

// ── 공개범위 설정(계정관리)에서 만든 공개구분을 선택 목록에 표시할 때 쓰는 형식 ──
// 공개구분명만 표시합니다. (상세 대상(회원구분/부서구분/직급구분)은 더 이상 괄호로 덧붙이지 않음)
const MEMBER_TYPE_LABEL = { staff: '사내임직원', partner: '협력사' };
function formatScopeLabel(scope) {
  return scope.name;
}

// 회의록 전용 공개범위 3단계 (업무의 공개범위(VIS_LIST)와는 별개 체계)
// - 부서공개는 선택한 부서 + 임원 등급 + 최고관리자를 항상 포함
// - 참조공개는 선택한 사람만 볼 수 있음
const MEETING_VIS_LIST = [
  { val:'전체공개', label:'전체공개', desc:'모든 회원에게 공개', color:'#0F6E56', bg:'#E1F5EE' },
  { val:'부서공개', label:'부서공개', desc:'선택한 부서 + 임원/최고관리자 공개', color:'#185FA5', bg:'#E6F1FB' },
  { val:'참조공개', label:'참조공개', desc:'선택한 사람만 볼 수 있음', color:'#7C3AED', bg:'#EDE9FE' },
];

// 두 날짜의 "일(day)" 차이만 계산(시:분:초는 무시) — to가 from보다 며칠 뒤인지(음수면 며칠 전인지)
function daysBetween(from, to) {
  const strip = (v) => {
    const d = new Date(v);
    if (isNaN(d)) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };
  const f = strip(from), t = strip(to);
  if (f === null || t === null) return null;
  return Math.round((t - f) / (1000*60*60*24));
}

// D-Day = 종료일 - 현재일(오늘) — 시간대/시각 차이로 하루씩 밀리는 문제를 없애기 위해 daysBetween 사용
function calcDday(dateStr) {
  if (!dateStr) return null;
  return daysBetween(new Date(), dateStr);
}

// created_at/latest_comment_at은 TEXT 컬럼인데, 값을 만드는 경로에 따라 표기 형식이 다르다
// ("2026-08-28 12:29:09" ↔ 회의록 자동등록 업무처럼 "2026. 08. 28. 12:29:09"). 두 형식이
// 섞이면 단순 문자열 비교(>/<)로는 실제 시간 순서가 보장되지 않아(점이 하이픈보다 아스키상
// 뒤라 회의록 업무가 항상 "최신"으로 잘못 판정됨) 실제 타임스탬프(ms)로 변환한 뒤 비교한다.
function toComparableTime(str) {
  if (!str) return 0;
  const m = String(str).match(/(\d{4})[.\-]\s*(\d{1,2})[.\-]\s*(\d{1,2})[.\s]+(\d{1,2}):(\d{1,2}):(\d{1,2})/);
  if (!m) return 0;
  const [, y, mo, d, h, mi, s] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s)).getTime();
}

// 업무 목록 기본 정렬 기준 — 등록일(created_at)과 최신 대응/업데이트 시각(latest_comment_at) 중
// 더 나중 시점. 방금 새로 등록되었거나 최근에 업데이트(대응이력 등)된 업무를 항상 맨 위로 올린다.
function taskLatestAt(t) {
  return Math.max(toComparableTime(t.created_at), toComparableTime(t.latest_comment_at));
}
// 목록 컬럼명 클릭 정렬(오름/내림차순 토글) 공용 정렬 함수 — 숫자면 숫자비교, 아니면 문자열비교.
// 정렬값이 동일한(동률) 행들은 등록순(id 오름차순)으로 고정 정렬한다 — 그렇지 않으면 JS의 안정
// 정렬(stable sort) 특성상 원래 배열 순서(백엔드가 내려주는 created_at DESC, 즉 최신순)가 그대로
// 유지되어, 회의록 버튼으로 방금 등록된 업무가 동률 그룹 맨 앞(=목록 맨 위)에 계속 남아있게 된다.
// 이 tie-break을 넣어야 회의록으로 등록된 업무도 일반 업무와 동일하게 선택한 규칙(컬럼)만 따르게 된다.
function sortRows(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  const copy = [...rows];
  copy.sort((a, b) => {
    let va = a ? a[sortKey] : null;
    let vb = b ? b[sortKey] : null;
    if (va === null || va === undefined) va = '';
    if (vb === null || vb === undefined) vb = '';
    let cmp;
    const na = Number(va), nb = Number(vb);
    if (va !== '' && vb !== '' && !isNaN(na) && !isNaN(nb)) cmp = na - nb;
    else cmp = String(va).localeCompare(String(vb), 'ko');
    if (cmp === 0) {
      const ia = Number(a?.id), ib = Number(b?.id);
      if (!isNaN(ia) && !isNaN(ib)) return ia - ib;
      return 0;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return copy;
}
// 목록 헤더 <span> — 컬럼명을 클릭할 때마다 오름차순/내림차순이 토글된다(활성 컬럼만 색상 강조).
function SortableTh({ label, sortKey, activeKey, dir, onClick, style }) {
  const active = activeKey === sortKey;
  return (
    <span onClick={() => onClick(sortKey)}
      title={active ? (dir === 'asc' ? '오름차순 (클릭시 내림차순)' : '내림차순 (클릭시 오름차순)') : '클릭하여 정렬'}
      style={{ ...style, cursor:'pointer', userSelect:'none', color: active ? '#185FA5' : (style?.color), fontWeight: active ? 700 : (style?.fontWeight) }}>
      {label}
    </span>
  );
}

// 완료가 아니면서 완료예정일이 지난 업무 = 지연
function isOverdue(t) {
  if (!t.end_date || t.status === '완료') return false;
  const end = new Date(t.end_date);
  const today = new Date(); today.setHours(0,0,0,0);
  return end < today;
}

// 아직 지연은 아니지만 마감 3일 이내 + 중요도 높음 = 긴급
function isUrgent(t) {
  if (!t.end_date || t.status === '완료') return false;
  const d = calcDday(t.end_date);
  return t.priority === '높음' && d !== null && d >= 0 && d <= 3;
}

// D-Day 표시 규칙:
//  1. 기본: 종료일 - 현재일로 D-Day를 계산
//  2. 완료 상태: 완료된 시점(completed_at, 없으면 예전 데이터 하위호환으로 updated_at) 기준으로 고정
//     - 완료 이후에 다시 수정해도(제목 오타 수정 등) completed_at은 서버에서 그대로 유지되므로 D-Day가 바뀌지 않음
//  3. 진행중 상태: 항상 오늘 기준으로 실시간 계산하고, 마감이 지났으면(D+) 빨간색으로 표시
// D-Day 값/문구 계산만 따로 뽑아둠 — 화면 배지(DdayBadge)와 목록 인쇄(별도 창)에서 동일한 로직을 재사용
function ddayInfo(date, status, completedAt, updatedAt) {
  if (!date) return null;
  let d;
  if (status === '완료') {
    const ref = completedAt || updatedAt || null;
    d = ref ? daysBetween(ref, date) : calcDday(date);
  } else {
    d = calcDday(date);
  }
  if (d === null || d === undefined || isNaN(d)) return null;
  const label = d<0?`${Math.abs(d)}일 초과`:d===0?'D-Day':`D-${d}`;
  return { d, label };
}

function DdayBadge({ date, status, completedAt, updatedAt }) {
  const info = ddayInfo(date, status, completedAt, updatedAt);
  if (!info) return null;
  const { d, label } = info;
  const color = d<0?'#fff':d<=3?'#d97706':'#16a34a';
  const bg    = d<0?'#dc2626':d<=3?'#fef3c7':'#dcfce7';
  return <span style={{ fontSize:13, padding:'3px 13px', borderRadius:20, background:bg, color, fontWeight:600 }}>{label}</span>;
}

// 이름 비교용 정규화: 앞뒤 공백뿐 아니라 이름 중간에 실수로 섞여들어간 공백/줄바꿈까지 모두 제거해서 비교
// (회원목록의 이름과 회의록/담당자란에 입력한 이름이 미세한 공백 차이로 매칭 실패하는 것을 방지)
const normName = (s) => String(s || '').replace(/\s+/g, '').trim();

// 담당자 표시: 회원목록에서 이름이 일치하는 사용자를 찾아 "직급 이름"으로 표시 (매칭 안되면 이름만)
// 2026-08: 최고관리자(superadmin) 계정은 다른 메뉴(출력자 표기 등)와 동일하게 실명 대신
// "최고관리자"로 표시한다 — 담당자/등록자란 등 이름을 그대로 노출하던 자리들이 이 함수를
// 거치도록(또는 아래 displayByName을 함께) 해서 실명이 새는 것을 막는다.
function formatManager(name, userList) {
  if (!name) return '';
  const u = (userList||[]).find(x => normName(x.name) === normName(name));
  if (u && u.role === 'superadmin') return '최고관리자';
  return (u && u.position) ? `${u.position} ${name}` : name;
}

// 이름 문자열만 있는 자리(예: 목록의 "날짜 · 등록자" 표기)에서도 최고관리자는 실명 대신
// "최고관리자"로 바꿔주는 경량 버전 (직급은 붙이지 않고 이름만 대체)
function displayByName(name, userList) {
  if (!name) return '';
  const u = (userList||[]).find(x => normName(x.name) === normName(name));
  if (u && u.role === 'superadmin') return '최고관리자';
  return name;
}

// 회의록 출력자 표시용 "부서 직급 이름" 문자열 생성 — 부서가 없는 임원 등 계정에서 부서란에도
// 직급명을 그대로 적어둔 경우처럼, 부서/직급 값이 우연히 같으면 직급이 두 번 표시되는 것을
// 막기 위해 동일한 값은 한 번만 남긴다.
function formatPrintedBy(u) {
  if (u && u.role === 'superadmin') return '최고관리자';
  const dept = (u && u.department) || '';
  const pos  = (u && u.position) || '';
  const name = (u && u.name) || '-';
  const parts = [dept, pos, name].filter((v, i, arr) => v && arr.indexOf(v) === i);
  return parts.join(' ');
}

// 담당부서 표시: 회원목록에서 이름이 일치하는 사용자를 찾아 그 사용자의 부서를 표시 (매칭 안되면 '-')
function formatDepartment(name, userList) {
  if (!name) return '';
  const u = (userList||[]).find(x => normName(x.name) === normName(name));
  return (u && u.department) ? u.department : '-';
}

// 업무내용은 이미지 붙여넣기를 지원하는 HTML로 저장되므로, 엑셀 내보내기처럼 순수 텍스트가
// 필요한 곳에서는 태그를 제거하고 텍스트만 추출해서 사용합니다. (이미지는 텍스트로 옮길 수 없음)
function htmlToText(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function VisBadge({ vis }) {
  const v = VIS_LIST.find(x => x.val === vis);
  if (v) {
    return (
      <span style={{ fontSize:12, padding:'3px 8px', borderRadius:20, fontWeight:600,
        background:v.bg, color:v.color }}>
        {v.label}
      </span>
    );
  }
  // 계정관리 - 공개범위 설정에서 만든 공개구분명은 VIS_LIST에 없으므로 이름 그대로 표시
  return (
    <span style={{ fontSize:12, padding:'3px 8px', borderRadius:20, fontWeight:600,
      background:'#F1F5F9', color:'#475569' }}>
      {vis || '전체공개'}
    </span>
  );
}

const navBtnStyle = { background:'none', border:'none', fontSize:13, color:'#888', cursor:'pointer', padding:'2px 4px' };

export default function Tasks() {
  const [list, setList]           = useState([]);
  const [selected, setSelected]   = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [editData, setEditData]   = useState(null);

  const [catFilter, setCatFilter]       = useState('전체');   // CAT_SHORT_LIST 값
  const [statusFilter, setStatusFilter] = useState('전체');
  const [overdueOnly, setOverdueOnly]   = useState(false);    // 지연 빠른필터

  const now = new Date();
  const [selYear, setSelYear]   = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth()+1);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(now.getFullYear());
  const monthPickerRef = useRef(null);
  // 전체보기/진행중/완료/지연/보류 버튼을 하나로 통합한 드롭다운 — 선택한 상태로 버튼 라벨이 바뀐다.
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef(null);

  const [pageSize, setPageSize] = useState('all');   // 10 / 30 / 50 / 100 / 'all'
  const [page, setPage]         = useState(1);
  // 목록 컬럼명 클릭 정렬 — null(기본)이면 "최신 등록/업데이트순"(taskLatestAt 내림차순)으로
  // 정렬해 방금 등록되거나 업데이트(대응 히스토리 등)된 업무가 항상 맨 위에 오도록 한다.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const [search, setSearch] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showMinutesModal, setShowMinutesModal] = useState(false); // 회의록 작성 모달(헤더 버튼)
  const [showResearchNotesModal, setShowResearchNotesModal] = useState(false); // 연구노트 목록 모달(헤더 버튼, 개발/지원 탭 전용)
  const [showRequestModal, setShowRequestModal] = useState(false);      // 업무요청 모달(헤더 버튼) — 요청내역이 먼저 뜨고, 그 안에서 "+ 업무요청"으로 등록폼을 연다
  // 상태바에서 업무요청 항목을 클릭해 들어온 경우, 요청내역 모달이 뜨자마자 이 id의 상세를
  // 자동으로 열어주기 위한 값(TaskRequestListModal에 openRequestId로 전달).
  const [pendingOpenRequestId, setPendingOpenRequestId] = useState(null);
  const [msg, setMsg]       = useState('');

  // 다른 화면에서 새 업무요청 알림(TaskRequestAlert)을 받고 "업무관리로 이동"을 눌렀을 때,
  // 이 화면으로 넘어오자마자 요청내역 모달이 바로 열리게 한다. state는 한 번 쓰고 지워서(replace)
  // 뒤로가기/새로고침 시 다시 열리지 않게 한다.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.state?.openTaskRequests) {
      setShowRequestModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line

  // 화면 하단 상태바(최신글/업데이트 표시)에서 업무관리 항목을 클릭해 들어온 경우, 그 항목의
  // 상세 모달을 바로 연다. list가 아직 로드되기 전(마운트 직후)일 수 있으므로 list가 채워질
  // 때까지 대기했다가 찾아서 연다. 한 번 열면 state를 지워서 새로고침/뒤로가기 시 다시 열리지 않게 한다.
  // subType이 'request'면 일반 업무(list)가 아니라 업무요청이므로, 요청내역 모달을 열고 그 안에서
  // 해당 요청의 상세(TaskRequestDetailModal)가 자동으로 열리도록 pendingOpenRequestId로 넘긴다.
  useEffect(() => {
    const ao = location.state?.activityOpen;
    if (!ao) return;
    if (ao.subType === 'request') {
      setPendingOpenRequestId(ao.itemId);
      setShowRequestModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    } else if (ao.subType === 'minutes') {
      // 회의록은 itemId가 업무(tasks)가 아니라 meeting_minutes 자신의 id이므로, 먼저 그 회의록을
      // 조회해 연결된 업무(task_id)가 있으면 그 업무 상세를 열어 회의록 목록이 보이게 한다.
      // 연결된 업무가 없는(임시저장 등) 회의록이면 특별히 열 화면이 없으므로 그냥 넘어간다.
      if (list.length) {
        API.get('/meeting-minutes/'+ao.itemId).then(r => {
          const linkedTaskId = r.data && r.data.task_id;
          if (!linkedTaskId) return;
          const found = list.find(t => String(t.id) === String(linkedTaskId));
          if (found) setSelected(found);
        }).catch(() => {});
        navigate(location.pathname, { replace: true, state: {} });
      }
    } else if (list.length) {
      const found = list.find(t => String(t.id) === String(ao.itemId));
      if (found) setSelected(found);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, list]); // eslint-disable-line

  const user    = JSON.parse(sessionStorage.getItem('user') || '{}');

  // 관리자/생산관리자/협력사 역할 개념이 없어졌으므로, 최고관리자 외에는 예외 없이
  // 계정관리 > 권한설정에서 부여한 쓰기 권한을 그대로 따릅니다.
  const { canWrite: canWriteMenu, hasDetail } = usePermissions();
  const canWriteTasks = canWriteMenu('tasks');
  // 2026-08: 업무등록을 별도 세부권한(register_allowed)으로 막던 것을 폐지 — 수정 가능한(쓰기권한이
  // 있는) 모든 회원이 등록도 할 수 있어야 한다는 요청에 따라 기본 쓰기권한과 동일하게 맞춘다.
  const canRegisterTask = canWriteTasks;
  const canManageCategory = hasDetail('tasks', 'category_manage');
  const canWriteMinutes = hasDetail('tasks', 'minutes_write_allowed');
  const canWriteResearchNotes = hasDetail('tasks', 'research_note_write_allowed');
  const canRequestTask  = hasDetail('tasks', 'request_write_allowed');
  // 견적서 버튼 — 영업관리 화면과 동일한 세부권한(quotation_*)을 그대로 따른다(업무관리 전용
  // 견적서 권한을 별도로 두지 않고, 영업관리 > 세부권한 설정 값을 그대로 공유).
  const canQuotationRegister = hasDetail('sales-activities', 'quotation_register_allowed');
  const canQuotationUpdate   = hasDetail('sales-activities', 'quotation_update_allowed');
  const canQuotationDelete   = hasDetail('sales-activities', 'quotation_delete_allowed');
  const [showQuotationList, setShowQuotationList] = useState(false);

  const [userList, setUserList] = useState([]); // 담당자를 "직급 이름"으로 표시하기 위한 회원목록(이름->직급 매칭용)

  // 구분(관리항목) — 상단 탭/필터/등록폼 드롭다운에서 함께 쓴다. "관리항목" 모달에서 등록/수정/
  // 삭제/순서변경을 하면 reloadCategories()로 다시 불러와 즉시 화면에 반영한다.
  const { catMeta, reloadCategories } = useTaskCategories();
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  // AI리포트(경영분석의 "주간회의 종합리포트" 기능을 모달로 바로 실행) — 별도 상세권한 없이
  // 업무관리 메뉴 접근권한만 있으면 버튼이 보이고, 실제 생성/조회는 백엔드 라우트
  // (POST /ai/reports/weekly/generate, requireMenuPermission('ai-assistant'))가 그대로
  // 권한을 검사하므로 경영분석 메뉴 권한이 없는 사용자는 생성 시 오류 메시지를 받는다.
  const [showAiReportModal, setShowAiReportModal] = useState(false);

  const load = () => API.get('/tasks').then(r => setList(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  // 담당자/등록자 표시용 회원목록은 업무관리 메뉴 접근권한만 있으면 누구나 조회 가능
  useEffect(() => { API.get('/tasks/users/list').then(r => setUserList(r.data)).catch(()=>{}); }, []);

  // "업무요청" 버튼 옆 건수 배지 + 깜빡임 — 예전에는 별도의 "요청내역" 헤더 버튼에 붙어있던
  // 기능인데, 요청내역 메뉴가 업무요청 모달 안으로 옮겨가면서 이 배지도 업무요청 버튼으로
  // 함께 옮겨왔다. 요청내역 모달과 동일한 기본 필터(내가 요청했거나 요청받은 내역, "업무요청
  // 전체 조회" 세부권한이 있으면 전체)로 건수를 세되, 아직 "확인완료" 처리되지 않은(=요청중이거나
  // 완료 후 확인대기 중인) 요청 중에서도 "내가 아직 한 번도 열어보지 않았거나, 열어본 뒤로
  // 새로운 변화(대응/수정요청 등)가 있었던" 것만 센다 — 상세보기를 한 번이라도 열어본 요청은
  // (그 이후 새 변화가 없는 한) 배지에서 사라진다(seen_map, PATCH /requests/:id/seen).
  // receivedRequestCount: 업무요청 모달 안쪽 "요청내역" 메뉴 옆에 표시하는 숫자 — 위 requestCount와
  // 달리 내가 "요청받은" 건만 센다(내가 보낸 요청 건수는 포함하지 않음).
  const [requestCount, setRequestCount] = useState(0);
  const [receivedRequestCount, setReceivedRequestCount] = useState(0);
  const canViewAllRequests = hasDetail('tasks', 'request_read_all');
  // 이 요청에 마지막으로 어떤 변화가 있었는지(생성/대응/수정요청/완료 등 모든 활동 중 가장 최근
  // 시각) — seen_map에 기록된 "마지막으로 확인한 시각"과 비교해서 "새 변화가 있었는지"를 판단한다.
  const lastActivityOf = (x) => {
    let hist = [];
    try { hist = JSON.parse(x.response_history || '[]'); } catch (e) { hist = []; }
    const times = [x.created_at, x.completed_at, x.confirmed_at, x.revision_at, ...hist.map(h => h.at)].filter(Boolean);
    return times.sort().pop() || x.created_at || '';
  };
  const isUnseenByMe = (x) => {
    let map = {};
    try { map = JSON.parse(x.seen_map || '{}'); } catch (e) { map = {}; }
    const seenAt = map[user.name];
    return !seenAt || seenAt < lastActivityOf(x);
  };
  // 요청을 보낸 사람(created_by===나)은 "아직 아무 대응/처리가 없는(막 등록한 직후)" 건은 배지에
  // 잡히지 않는다 — 자기가 방금 만든 요청에 스스로 숫자가 뜰 필요는 없고, 받은 쪽이 대응/확인요청/
  // 완료 등으로 뭔가 처리해서 lastActivityOf가 created_at 이후로 바뀌었을 때만 배지에 잡히게 한다.
  const isFreshUnrespondedByMe = (x) => x.created_by === user.name && lastActivityOf(x) === x.created_at;
  // 2026-09-01 — "요청내역이 없는데 업무요청 배지가 깜빡이고 클릭해도 안 사라짐" 버그 수정.
  // 원인: 배지 카운트는 request_read_all 권한이 있으면 전사(all=1) 범위로 집계했는데, 정작
  // 요청내역 모달(TaskRequestListModal)은 "전체보기" 체크박스가 항상 기본 꺼짐(showAll=false)
  // 상태로 열려서 "내 요청/내가 받은 요청"만 보여준다. 그 결과 배지에는 나와 무관한(전사) 미확인
  // 건이 잡혀서 숫자가 뜨는데, 정작 모달 기본화면에는 그 건이 안 보여 클릭해서 확인 처리할 방법이
  // 없었다(=깜빡임이 영원히 안 꺼짐). 배지 집계 범위를 모달의 기본 노출 범위와 동일하게 "내 것만"으로
  // 맞춰서, 배지에 잡히는 건은 항상 기본 목록에서 클릭 가능하도록 고정한다.
  const loadRequestCount = () => {
    API.get('/tasks/requests', { params: {} })
      .then(r => {
        const rows = r.data || [];
        setRequestCount(rows.filter(x => !x.confirmed_at && isUnseenByMe(x) && !isFreshUnrespondedByMe(x)).length);
        setReceivedRequestCount(rows.filter(x => x.request_manager === user.name && !x.confirmed_at && isUnseenByMe(x)).length);
      })
      .catch(() => {});
  };
  useEffect(() => {
    loadRequestCount();
    const t = setInterval(loadRequestCount, 60 * 1000); // 1분마다 자동 갱신
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  // 등록/수정/삭제 완료 메시지가 계속 남아있던 버그 수정: 일정 시간 후 자동으로 사라지게 함
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(''), 3000);
    return () => clearTimeout(t);
  }, [msg]);

  // 월 선택 팝오버가 열릴 때마다 팝오버 안 연도를 현재 선택연도로 초기화
  useEffect(() => { if (showMonthPicker) setPickerYear(selYear); }, [showMonthPicker, selYear]);

  // 팝오버 바깥 클릭 시 닫기
  useEffect(() => {
    if (!showMonthPicker) return;
    const onClickOutside = (e) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(e.target)) setShowMonthPicker(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showMonthPicker]);

  // 상태 드롭다운 바깥 클릭 시 닫기
  useEffect(() => {
    if (!showStatusDropdown) return;
    const onClickOutside = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) setShowStatusDropdown(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showStatusDropdown]);

  const shiftMonth = (delta) => {
    if (!selMonth) return; // '전체'(연간) 보기 중에는 좌우 이동 비활성화
    let m = selMonth + delta, y = selYear;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setSelMonth(m); setSelYear(y);
  };

  const filtered = list.filter(t => {
    const matchCat     = catFilter==='전체' ? true
      : catFilter==='회의록' ? (t.minutes_count > 0)
      : t.category === catMeta.map[catFilter];
    const matchStatus  = statusFilter==='전체' || t.status===statusFilter;
    const matchOverdue = !overdueOnly || isOverdue(t);
    // 월 필터 규칙
    //  1) 완료예정일이 없으면 항상 노출
    //  2) 완료예정일이 선택된 연/월 "이후"(그 달 포함)면 노출 — 아직 끝나지 않은 업무이므로 이전 달에서도 계속 보여야 함
    //  3) 완료예정일과 무관하게 상태가 진행중/지연이면 항상 노출
    const matchMonth = !t.end_date || t.status==='진행중' || isOverdue(t) || (() => {
      const ed = new Date(t.end_date);
      const endY = ed.getFullYear(), endM = ed.getMonth()+1;
      return selMonth ? (endY*12+endM) >= (selYear*12+selMonth) : endY >= selYear;
    })();
    const matchSearch  = t.title.includes(search)||(t.manager||'').includes(search)||(t.content||'').includes(search);
    return matchCat && matchStatus && matchOverdue && matchMonth && matchSearch;
  });

  // 목록 컬럼명 클릭 정렬 — 지정된 컬럼이 있으면 그 컬럼 기준, 없으면(기본) 최신 등록/업데이트순.
  const sortedFiltered_ = sortKey
    ? sortRows(filtered, sortKey, sortDir)
    : [...filtered].sort((a, b) => {
        const diff = taskLatestAt(b) - taskLatestAt(a);
        if (diff !== 0) return diff;
        // 시각이 완전히 동률이면 등록순(id 내림차순, 즉 최근 등록된 업무가 위)으로 고정한다.
        return Number(b?.id || 0) - Number(a?.id || 0);
      });
  // 완료된 업무는 선택된 정렬 기준과 무관하게 항상 리스트 맨 아래로 내린다. Array.sort는
  // 안정정렬(stable sort)이므로, 완료여부가 같은 항목끼리는(0 반환) 위에서 이미 정렬된 순서가
  // 그대로 유지되고 완료 그룹만 통째로 뒤로 밀려난다.
  const sortedFiltered = [...sortedFiltered_].sort((a, b) => {
    const da = a?.status === '완료' ? 1 : 0;
    const db = b?.status === '완료' ? 1 : 0;
    return da - db;
  });

  // 필터가 바뀌면 페이지 1로 리셋
  useEffect(() => { setPage(1); }, [catFilter, statusFilter, overdueOnly, selYear, selMonth, search, pageSize, sortKey, sortDir]);

  const totalPages = pageSize==='all' ? 1 : Math.max(1, Math.ceil(sortedFiltered.length / pageSize));
  const pagedList  = pageSize==='all' ? sortedFiltered : sortedFiltered.slice((page-1)*pageSize, page*pageSize);

  const stats = ['대기','진행중','완료','보류'].map(s => ({
    label:s, count:list.filter(t=>t.status===s).length
  }));
  const overdueCount = list.filter(isOverdue).length;
  const urgentCount  = list.filter(isUrgent).length;

  // 등록/수정한 항목이 현재 필터에 가려지지 않고 목록에 바로 보이도록 구분/상태/지연 필터만 초기화.
  // (완료예정일의 월로 화면이 이동해버리던 현상을 없애고, 보고 있던 월을 그대로 유지함)
  const revealItemInList = () => {
    setCatFilter('전체'); setStatusFilter('전체'); setOverdueOnly(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await API.delete('/tasks/'+id);
    setSelected(null); setMsg('삭제 완료!'); load();
  };

  // 엑셀 다운로드
  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();
    ['대기','진행중','완료','보류'].forEach(status => {
      const rows = list.filter(t=>t.status===status).map(t => ({
        '구분':       t.category||'',
        '중요도':     t.priority||'',
        '제목':       t.title||'',
        '내용':       htmlToText(t.content)||'',
        '담당자':     t.manager||'',
        '상태':       t.status||'',
        '공개범위':   t.visibility||'전체공개',
        '시작일':     t.start_date||'',
        '완료예정일': t.end_date||'',
        '등록자':     t.created_by||'',
        '등록일시':   t.created_at?t.created_at.slice(0,16):'',
        '수정자':     t.updated_by||'',
        '수정일시':   t.updated_at?t.updated_at.slice(0,16):'',
      }));
      if (!rows.length) rows.push({
        '구분':'','중요도':'','제목':'(없음)','내용':'','담당자':'',
        '상태':status,'공개범위':'','시작일':'','완료예정일':'',
        '등록자':'','등록일시':'','수정자':'','수정일시':''
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{wch:8},{wch:8},{wch:28},{wch:28},{wch:10},{wch:8},
        {wch:10},{wch:12},{wch:12},{wch:10},{wch:18},{wch:10},{wch:18}];
      XLSX.utils.book_append_sheet(wb, ws, status);
    });
    const exportFileName = 'IMS_업무관리_'+new Date().toISOString().slice(0,10)+'.xlsx';
    XLSX.writeFile(wb, exportFileName);
    logDownload('업무관리', exportFileName);
  };

  // 목록 인쇄 — 화면을 그대로 축소해서 찍는 방식(zoom+visibility 숨김) 대신, 상세보기 인쇄와 같은
  // 방식으로 별도 창에 실제 표(<table>)를 작성해서 인쇄합니다. 기존 방식은 화면에 보이는 축소된
  // 높이와 인쇄 페이지 분할 기준이 서로 달라서(브라우저가 축소 전 높이로 페이지를 나눔) 내용이
  // 없는 빈 두 번째 페이지가 함께 출력되는 문제가 있었는데, 실제 표로 작성하면 브라우저가 표
  // 내용에 맞춰 자연스럽게 페이지를 나누고 필요할 때만 다음 페이지로 넘어갑니다.
  // 목록 인쇄 — 화면에 인쇄창을 띄우는 동시에, 같은 내용을 서버에서 PDF로 변환해
  // D:\data_Storage\Tasks\00_inventory\<일자>-업무관리 목록.pdf 로 저장한다(서버에 PDF
  // 변환 도구가 없는 등 실패하면 예전처럼 브라우저 자체 인쇄로 대체).
  const handleListPrint = async () => {
    const now = new Date();
    const printedAt = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printedBy = printedByLabel(user); // 출력자: 부서 직급 이름 (최고관리자는 "최고관리자"로 표시)
    const esc = (s) => String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

    const rowsHtml = filtered.map(t => {
      const info = ddayInfo(t.end_date, t.status, t.completed_at, t.updated_at);
      return `<tr>
        <td>${esc(t.category)}</td>
        <td>${esc(t.priority)}</td>
        <td class="content-cell" title="${esc(t.title)}">${esc(t.title)}</td>
        <td>${esc(formatDepartment(t.manager, userList))}</td>
        <td>${esc(formatManager(t.manager, userList))}</td>
        <td>${esc(t.status)}</td>
        <td>${esc(info ? info.label : '-')}</td>
        <td>${esc(t.start_date)}</td>
        <td>${esc(t.end_date)}</td>
        <td class="nowrap-cell">${esc(t.visibility || '전체공개')}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>업무관리</title>
      <style>
        /* margin:0으로 두면 브라우저가 자동으로 붙이는 인쇄 헤더(시간)/푸터(제목,URL)를
           그릴 여백이 없어져 표시되지 않습니다. 대신 body에 padding을 줘서 기존과 동일한
           여백을 유지합니다. */
        @page { size: A4 landscape; margin: 0; }
        body { font-family: sans-serif; color:#222; padding:10mm; box-sizing:border-box; }
        h2 { font-size:18px; margin:0 0 10px; }
        /* table-layout:fixed + colgroup으로 각 열 너비를 고정해서, 업무내용 열이 남는 가로폭을
           모두 차지해 용지 폭에 꽉 차도록 하고(예전에는 내용 길이에 따라 열 너비가 들쭉날쭉해서
           표 전체가 용지보다 좁게 찍혔음), 내용이 칸보다 길면 줄바꿈 대신 줄임표(…)로 자른다. */
        table { width:100%; border-collapse:collapse; font-size:11px; table-layout:fixed; }
        th, td { border:1px solid #ccc; padding:5px 6px; text-align:center; }
        th { background:#f4f4f4; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; }
        td.content-cell { text-align:left; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        td.nowrap-cell { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .footer { display:flex; justify-content:space-between; margin-top:10px; padding-top:6px;
          border-top:1px solid #ddd; font-size:10px; color:#666; }
      </style></head><body>
      <h2>📋 업무관리</h2>
      <table>
        <colgroup>
          <col style="width:6%"><col style="width:5%"><col style="width:28%"><col style="width:6%">
          <col style="width:8%"><col style="width:5%"><col style="width:5%"><col style="width:9%">
          <col style="width:9%"><col style="width:19%">
        </colgroup>
        <thead><tr>
          <th>구분</th><th>중요도</th><th>업무내용</th><th>부서</th><th>담당자</th>
          <th>상태</th><th>D-Day</th><th>시작일</th><th>종료일</th><th>공개범위</th>
        </tr></thead>
        <tbody>${rowsHtml || `<tr><td colspan="10" style="color:#999;">표시할 업무가 없습니다.</td></tr>`}</tbody>
      </table>
      <div class="footer"><span>출력일시: ${printedAt}</span><span>출력자: ${printedBy}</span></div>
      </body></html>`;

    logPrint('업무관리', '업무관리 목록');
    try {
      const res = await API.post('/tasks/print/list-pdf', { html }, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
      iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = 'none';
      iframe.src = blobUrl;
      // iframe의 onload는 PDF뷰어(플러그인) 껍데기가 뜨는 시점일 뿐, 실제 페이지 크기에 맞춰
      // 확대/축소(fit-to-page)를 계산하는 내부 렌더링은 그 뒤에 비동기로 이어진다. onload되자마자
      // 바로 print()를 부르면 그 계산이 끝나기 전 상태(축소되기 전/덜 채워진 상태)가 그대로
      // 인쇄 미리보기에 잡혀버려서, 실제 저장된 PDF 파일은 정상인데 인쇄창 미리보기만 오른쪽에
      // 여백이 남아 보이는 원인이 된다. 약간의 지연을 줘서 PDF뷰어가 완전히 자리잡은 뒤 인쇄를
      // 호출한다.
      iframe.onload = () => { setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} }, 600); };
      document.body.appendChild(iframe);
    } catch (err) {
      // 서버에 PDF 변환 도구(puppeteer)가 없는 등 실패 시, 예전 방식(브라우저 자체 인쇄)으로 대체
      const pw = window.open('', '_blank', 'width=1200,height=800');
      pw.document.write(html);
      pw.document.close(); pw.focus();
      setTimeout(() => { pw.print(); pw.close(); }, 400);
    }
  };

  // 목록 상단 "모두보기/진행중/완료/지연/보류" 빠른 필터 클릭
  const handleQuickStatus = (s) => {
    if (s === '모두보기') { setStatusFilter('전체'); setOverdueOnly(false); }
    else if (s === '지연') { setOverdueOnly(v => !v); setStatusFilter('전체'); } // 지연/상태 필터는 동시에 켜지지 않도록 상호 배타 처리
    else { setStatusFilter(prev => prev===s ? '전체' : s); setOverdueOnly(false); }
  };
  // 통합 드롭다운 버튼에 표시할 현재 선택 상태(라벨/색상) — overdueOnly 켜져있으면 "지연",
  // statusFilter가 '전체'면 "전체보기", 그 외에는 선택된 상태명 그대로.
  const curStatusKey = overdueOnly ? '지연' : (statusFilter === '전체' ? '모두보기' : statusFilter);
  const curStatusLabel = QUICK_STATUS_LABEL[curStatusKey] || curStatusKey;
  const curStatusColor = curStatusKey === '지연' ? '#dc2626' : (curStatusKey === '모두보기' ? '#185FA5' : (STATUS_COLOR[curStatusKey] || '#185FA5'));

  // 연구노트 — "개발"/"지원" 탭에서만 헤더에 노출(요청사항). 이름이 바뀌어도(관리항목에서 개명)
  // full_label 기준으로 판단해 정확히 제품개발/지원사업 탭일 때만 보이도록 한다.
  const isDevOrSupportTab = catMeta.map[catFilter] === '제품개발' || catMeta.map[catFilter] === '지원사업';

  return (
    <div style={{ padding:24, maxWidth:1690 }}>
      <style>{`@keyframes taskReqBlink { 0%,100% { opacity:1; } 50% { opacity:.5; } }`}</style>

      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>📋 업무현황</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>사업·영업·기술 업무를 등록하고 진행상태를 관리합니다.</p>
        </div>
        <div className="hdr-actions" style={{ display:'flex', gap:8 }}>
          <button onClick={handleListPrint}
            style={{ background:'#475569', color:'#fff', border:'1px solid #ddd', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            🖨️ 인쇄
          </button>
          <button onClick={downloadExcel}
            style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            📥 내보내기
          </button>
          {/* 관리항목 — 상단 구분 탭(주요일정 아님, 업무구분)을 등록/수정/삭제/순서변경하는 모달.
              관리항목 등록 권한(category_manage)이 있는 사람만 사용할 수 있다. */}
          {canManageCategory && (
            <button onClick={() => setShowCategoryManager(true)}
              style={{ background:'#334155', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              🗂️ 관리항목
            </button>
          )}
          {/* 연구노트 — 요청사항: "개발"/"지원" 탭을 누르면 회의록 버튼 왼쪽에 노출.
              누르면 (이 탭 구분으로 필터된) 연구노트 목록 모달이 뜬다. */}
          {isDevOrSupportTab && (
            <button onClick={() => setShowResearchNotesModal(true)}
              style={{ background:'#0891B2', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              🧪 연구노트
            </button>
          )}
          {canWriteMinutes && (
            <button onClick={() => setShowMinutesModal(true)}
              style={{ background:'#7C3AED', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              📝 회의록
            </button>
          )}
          {/* 예전에는 "요청내역"(조회 전용) / "업무요청"(등록) 버튼이 따로 있었는데, 요청내역
              메뉴를 업무요청 모달 안(우측상단)으로 옮기면서 헤더에는 이 버튼 하나만 남았다.
              점등(깜빡임)+건수 배지도 그대로 이 버튼으로 옮겨왔다. 업무요청 등록 권한
              (request_write_allowed)이 없어도, 내가 받은 요청이 있으면(요청내역을 봐야 하므로)
              버튼은 계속 보이게 한다 — 등록 자체는 모달 안에서 권한에 따라 막힌다. */}
          {(canRequestTask || requestCount > 0) && (
            <button onClick={() => setShowRequestModal(true)}
              style={{ background:'#0F766E', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer',
                display:'flex', alignItems:'center', gap:6,
                animation: requestCount>0 ? 'taskReqBlink 1.4s ease-in-out infinite' : 'none' }}>
              📨 업무요청
              {requestCount>0 && (
                <span style={{ fontSize:11.5, fontWeight:800, color:'#fff', background:'#dc2626',
                  borderRadius:20, padding:'1px 7px', minWidth:16, textAlign:'center' }}>{requestCount}</span>
              )}
            </button>
          )}
          {canRegisterTask && (
            <button onClick={() => { setShowForm(true); setEditData(null); }}
              title={catFilter!=='전체' && catFilter!=='회의록' ? `구분: ${catMeta.map[catFilter]||catFilter} (현재 선택된 탭으로 자동 지정됨)` : undefined}
              style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              + 업무 등록
            </button>
          )}
        </div>
      </div>

      {msg && <div style={{ background:'#d1fae5', color:'#065f46', padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>}

      {/* 1. 통계 카드: 대기 / 진행중 / 완료 / 보류 / 지연·긴급 */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:24 }}>
        {/* 생산관리·영업관리 요약카드와 동일한 스타일: 항상 색상 배경 + 좌측 라벨/우측 숫자 (순수 표시용, 필터는 아래 빠른 필터에서) */}
        {stats.map(s => (
          <div key={s.label}
            style={{ background:STATUS_BG[s.label],
              border:`1px solid ${STATUS_COLOR[s.label]}30`,
              borderRadius:12, padding:'16px 20px',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:14, fontWeight:600, color:STATUS_COLOR[s.label] }}>{s.label}</span>
            <span style={{ fontSize:28, fontWeight:700, color:STATUS_COLOR[s.label] }}>{s.count}</span>
          </div>
        ))}
        <div style={{ background:'#fee2e2',
          border:'1px solid #dc262630',
          borderRadius:12, padding:'16px 20px',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:14, fontWeight:600, color:'#dc2626' }}>지연/긴급</span>
          <span style={{ fontSize:28, fontWeight:700, color:'#dc2626' }}>{overdueCount} / {urgentCount}</span>
        </div>
      </div>

      {/* 2~4. 한 줄로 통합: 좌측 = 검색 아이콘 + 구분 탭(밑줄+건수, 0건은 숫자 숨김) · 우측 = 모두보기~보류 + 연월 선택 + 페이지당 표시개수 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:16, paddingBottom:10, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
          <button onClick={() => setShowSearchModal(true)} title="검색"
            style={{ background:'none', border:'none', fontSize:17, cursor:'pointer', padding:'6px 8px', color:'#888' }}>
            🔍
          </button>
          {/* 회의록 탭은 이 구분 탭 그룹에서 빼서, 우측 "전체보기"(모두보기) 버튼 바로 왼쪽으로 이동했다
              (아래 회의록 필터 버튼 참고) — 필터링 동작(catFilter==='회의록')은 그대로다. */}
          {catMeta.shortList.filter(c => c !== '회의록').map(c => {
            // 탭 옆 건수는 완료된 업무를 제외한, 현재 유효한(진행중인) 업무만 센다(요청사항 —
            // 예전에는 완료 건까지 포함되어 실제 처리해야 할 건수보다 많아 보였음).
            const count = c==='전체' ? list.filter(t => t.status !== '완료').length
              : list.filter(t => t.category===catMeta.map[c] && t.status !== '완료').length;
            const active = catFilter===c;
            return (
              <button key={c} onClick={() => setCatFilter(c)}
                style={{ padding:'10px 18px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600,
                  borderBottom: active?'2px solid #185FA5':'2px solid #e5e7eb',
                  background:'none', color:active?'#185FA5':'#888' }}>
                {catMeta.tabLabel[c]||c}
                {count > 0 && (
                  <span style={{ fontSize:11, marginLeft:6, background:active?'#185FA5':'#e5e7eb',
                    color:active?'#fff':'#888', padding:'1px 7px', borderRadius:20 }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:6 }}>
          {/* 회의록 필터 버튼 — "전체보기"(모두보기) 왼쪽에 위치, 회의록이 등록된 업무만 필터링.
              (요청사항: 건수 배지는 제거하고 "회의록" 글자만 표시) */}
          {(() => {
            const active = catFilter==='회의록';
            return (
              <button onClick={() => setCatFilter(active ? '전체' : '회의록')}
                style={{
                  padding:'8px 14px', fontSize:13, fontWeight:600, borderRadius:8, cursor:'pointer',
                  border: active ? '1px solid #7C3AED' : '1px solid #ddd',
                  background: active ? '#7C3AED' : '#fff',
                  color: active ? '#fff' : '#555',
                }}>
                📝 회의록
              </button>
            );
          })()}
          {/* 모두보기/진행중/완료/지연/보류 5개 버튼을 하나로 통합 — 클릭하면 드롭다운이 열리고,
              고른 상태로 버튼 라벨/색이 바뀐다. */}
          <div ref={statusDropdownRef} style={{ position:'relative' }}>
            <button onClick={() => setShowStatusDropdown(v => !v)}
              style={{
                padding:'8px 14px', fontSize:13, fontWeight:600, borderRadius:8, cursor:'pointer',
                border:`1px solid ${curStatusColor}`, background:curStatusColor, color:'#fff',
                display:'flex', alignItems:'center', gap:5,
              }}>
              {curStatusLabel}<span style={{ fontSize:10 }}>▾</span>
            </button>
            {showStatusDropdown && (
              <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, background:'#fff',
                border:'1px solid #e5e7eb', borderRadius:8, boxShadow:'0 4px 14px rgba(0,0,0,0.12)',
                zIndex:20, minWidth:104, overflow:'hidden' }}>
                {QUICK_STATUS.map(s => {
                  const active = s==='모두보기' ? (statusFilter==='전체' && !overdueOnly)
                    : s==='지연' ? overdueOnly
                    : statusFilter===s;
                  const color = s==='지연' ? '#dc2626' : (STATUS_COLOR[s] || '#185FA5');
                  return (
                    <div key={s} onClick={() => { handleQuickStatus(s); setShowStatusDropdown(false); }}
                      style={{
                        padding:'9px 14px', fontSize:13, fontWeight:600, cursor:'pointer',
                        background: active ? `${color}18` : '#fff', color: active ? color : '#555',
                      }}>
                      {QUICK_STATUS_LABEL[s]||s}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* 연/월 선택 */}
        <div ref={monthPickerRef} style={{ position:'relative', display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={() => shiftMonth(-1)} disabled={!selMonth}
              style={{ background:'#fff', border:'1px solid #ddd', borderRadius:6, width:28, height:28,
                fontSize:13, cursor:selMonth?'pointer':'not-allowed', color:selMonth?'#555':'#ccc' }}>◀</button>
            <span onClick={() => setShowMonthPicker(v => !v)}
              style={{ fontSize:16, fontWeight:700, color:'#000', minWidth:100, textAlign:'center', cursor:'pointer' }}>
              {selYear}년 {selMonth ? `${selMonth}월` : '전체'}
            </span>
            <button onClick={() => shiftMonth(1)} disabled={!selMonth}
              style={{ background:'#fff', border:'1px solid #ddd', borderRadius:6, width:28, height:28,
                fontSize:13, cursor:selMonth?'pointer':'not-allowed', color:selMonth?'#555':'#ccc' }}>▶</button>
            {!(selYear===now.getFullYear() && selMonth===now.getMonth()+1) && (
              <button onClick={() => { setSelYear(now.getFullYear()); setSelMonth(now.getMonth()+1); }}
                style={{ fontSize:12, padding:'5px 10px', background:'#E6F1FB', color:'#185FA5',
                  border:'1px solid #185FA5', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                이번달
              </button>
            )}

            {showMonthPicker && (
              <div style={{ position:'absolute', top:'calc(100% + 6px)', right:0, background:'#fff',
                border:'1px solid #e5e7eb', borderRadius:10, boxShadow:'0 4px 14px rgba(0,0,0,0.12)',
                padding:12, zIndex:20, width:220 }}
                onClick={e => e.stopPropagation()}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <button onClick={() => setPickerYear(y=>y-1)} style={navBtnStyle}>◀</button>
                  <span style={{ fontWeight:600, fontSize:14 }}>{pickerYear}년</span>
                  <button onClick={() => setPickerYear(y=>y+1)} style={navBtnStyle}>▶</button>
                </div>
                <button
                  onClick={() => { setSelYear(pickerYear); setSelMonth(null); setShowMonthPicker(false); }}
                  style={{ width:'100%', padding:'6px 0', marginBottom:6, borderRadius:6, border:'1px solid',
                    borderColor: (pickerYear===selYear && !selMonth) ? '#185FA5' : '#ddd',
                    background:  (pickerYear===selYear && !selMonth) ? '#E6F1FB' : '#fff',
                    color:       (pickerYear===selYear && !selMonth) ? '#185FA5' : '#555',
                    fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  {pickerYear}년 전체보기
                </button>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
                  {Array.from({length:12}, (_,i)=>i+1).map(m => {
                    const active = pickerYear===selYear && m===selMonth;
                    return (
                      <button key={m}
                        onClick={() => { setSelYear(pickerYear); setSelMonth(m); setShowMonthPicker(false); }}
                        style={{ padding:'6px 0', borderRadius:6, border:'1px solid',
                          borderColor: active?'#185FA5':'#ddd',
                          background:  active?'#E6F1FB':'#fff',
                          color:       active?'#185FA5':'#555',
                          fontSize:14, cursor:'pointer' }}>
                        {m}월
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

        {/* 페이지당 표시개수 */}
        <select value={pageSize} onChange={e => setPageSize(e.target.value==='all' ? 'all' : Number(e.target.value))}
          style={{ padding:'7px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, color:'#555',
            outline:'none', cursor:'pointer', background:'#fff' }}>
          <option value="10">10개씩</option>
          <option value="30">30개씩</option>
          <option value="50">50개씩</option>
          <option value="100">100개씩</option>
          <option value="all">모두보기</option>
        </select>
        </div>
      </div>

      {/* 목록 */}
      {filtered.length===0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:'#aaa', fontSize:15 }}>
          등록된 업무가 없습니다.<br/>
          {canRegisterTask && <span style={{ fontSize:13 }}>상단 "+ 업무 등록" 버튼을 눌러 추가하세요.</span>}
        </div>
      ) : (
        <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e5e7eb', overflowX:'auto', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display:'grid', gridTemplateColumns:'90px 90px 1fr 80px 90px 90px 90px 90px 90px 160px',
            background:'#f8fafc', padding:'10px 10px', fontSize:14, fontWeight:600,
            color:'#555', borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>
            {/* 목록 컬럼명을 클릭하면 클릭할 때마다 오름차순/내림차순이 토글된다. 부서(파생값)/
                업무내용(자유서식)/D-Day(계산값)/공개범위는 정렬 대상에서 제외한다. */}
            <SortableTh label="구분" sortKey="category" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="중요도" sortKey="priority" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <span style={{textAlign:'left'}}>업무내용</span>
            <span>부서</span>
            <SortableTh label="담당자" sortKey="manager" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="상태" sortKey="status" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <span>D-Day</span>
            <SortableTh label="시작일" sortKey="start_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <SortableTh label="종료일" sortKey="end_date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
            <span>공개범위</span>
          </div>
          {pagedList.map((t,i) => (
            <div key={t.id} onClick={() => setSelected(t)}
              style={{ display:'grid', gridTemplateColumns:'90px 90px 1fr 80px 90px 90px 90px 90px 90px 160px',
                padding:'10px 10px', fontSize:14, textAlign:'center', alignItems:'center',
                borderBottom:i<pagedList.length-1?'1px solid #f0f0f0':'none',
                cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
              onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
              <span style={{ fontSize:13, padding:'3px 8px', borderRadius:20,
                background:catMeta.tabBg[catMeta.fullToShort[t.category]]||'#f0f0f0',
                color:catMeta.tabColor[catMeta.fullToShort[t.category]]||'#555', fontWeight:600 }}>{t.category}</span>
              <span>
                <span style={{ fontSize:13, padding:'3px 13px', borderRadius:20, fontWeight:600,
                  background:PRIORITY_BG[t.priority], color:PRIORITY_COLOR[t.priority] }}>
                  {t.priority}
                </span>
              </span>
              <span style={{ textAlign:'left', minWidth:0, overflow:'hidden' }}>
                <div style={{ fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title}</div>
                {(() => {
                  // 업무내용(등록시)과 최신 히스토리(업데이트) 중 더 최근 것을 표시
                  const hasComment = !!t.latest_comment_at;
                  const useComment = hasComment && (!t.content || t.latest_comment_at >= t.created_at);
                  const note = useComment
                    ? { at:t.latest_comment_at, by:t.latest_comment_by, text:t.latest_comment_content }
                    : (t.content ? { at:t.created_at, by:t.created_by, text:htmlToText(t.content) } : null);
                  if (!note) return null;
                  return (
                    <div style={{ fontSize:12, color:'#777', marginTop:1, minWidth:0,
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <span style={{ color:'#999' }}>{(note.at||'').slice(0,16)} · {displayByName(note.by, userList)||'-'}</span> {note.text}
                    </div>
                  );
                })()}
              </span>
              <span style={{ color:'#666', fontSize:13 }}>{formatDepartment(t.manager, userList) || '-'}</span>
              <span style={{ color:'#666', fontSize:13 }}>{formatManager(t.manager, userList) || '-'}</span>
              <span>
                <span style={{ fontSize:13, padding:'3px 13px', borderRadius:20, fontWeight:600,
                  background:STATUS_BG[t.status], color:STATUS_COLOR[t.status] }}>{t.status}</span>
              </span>
              <span><DdayBadge date={t.end_date} status={t.status} completedAt={t.completed_at} updatedAt={t.updated_at} /></span>
              <span style={{ color:'#666', fontSize:13 }}>{t.start_date||'-'}</span>
              <span style={{ color:'#666', fontSize:13 }}>{t.end_date||'-'}</span>
              <span><VisBadge vis={t.visibility} /></span>
            </div>
          ))}
        </div>
      )}

      {/* 페이지네이션 (모두보기가 아닐 때만) */}
      {pageSize!=='all' && totalPages>1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:14 }}>
          <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
            style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
              color: page===1?'#ccc':'#555', fontSize:14, cursor: page===1?'not-allowed':'pointer' }}>◀</button>
          <span style={{ fontSize:14, color:'#666' }}>{page} / {totalPages}</span>
          <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
            style={{ padding:'6px 12px', border:'1px solid #ddd', borderRadius:8, background:'#fff',
              color: page===totalPages?'#ccc':'#555', fontSize:14, cursor: page===totalPages?'not-allowed':'pointer' }}>▶</button>
        </div>
      )}

      {/* 상세 모달 */}
      {selected && (
        <DetailModal
          key={selected.id}
          task={selected}
          tasks={list}
          userList={userList}
          onTasksChanged={load}
          onClose={() => setSelected(null)}
          onEdit={t => { setEditData(t); setShowForm(true); setSelected(null); }}
          onDelete={handleDelete}
          onStatusChange={updated => { setSelected(updated); load(); revealItemInList(updated); }}
          onRequestSaved={loadRequestCount}
        />
      )}

      {/* 등록/수정 폼 */}
      {showForm && (
        <TaskForm
          editData={editData}
          // "업무등록" 버튼을 누른 시점에 특정 구분 탭이 선택되어 있었으면(전체/회의록 탭 제외),
          // 새 업무의 구분을 그 탭으로 미리 채워준다(요청사항 — 예전에는 항상 기본값(첫 항목)으로
          // 시작해서 매번 다시 골라야 했음). 수정 화면(editData 있음)에는 영향 없음.
          defaultCategory={!editData && catFilter!=='전체' && catFilter!=='회의록' ? catMeta.map[catFilter] : undefined}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onSaved={(saved) => { setShowForm(false); setEditData(null); load(); setMsg(editData?'수정 완료!':'등록 완료!'); revealItemInList(saved); }}
        />
      )}

      {/* 검색 모달 */}
      {showSearchModal && (
        <TaskSearchModal
          initialValue={search}
          onClose={() => setShowSearchModal(false)}
          onSearch={(v) => { setSearch(v); setShowSearchModal(false); }}
        />
      )}

      {/* 견적서 목록/작성 모달(헤더 버튼) — 영업관리와 동일한 모달을 그대로 사용 */}
      {showQuotationList && (
        <QuotationListModal
          user={user}
          canRegister={canQuotationRegister}
          canUpdate={canQuotationUpdate}
          canDelete={canQuotationDelete}
          onClose={() => setShowQuotationList(false)}
        />
      )}

      {/* 회의록 작성 모달(헤더 버튼) */}
      {showMinutesModal && (
        <MeetingMinutesModal
          tasks={list}
          userList={userList}
          onTasksChanged={load}
          onClose={() => setShowMinutesModal(false)}
          onSaved={() => setShowMinutesModal(false)}
        />
      )}

      {/* 연구노트 목록 모달(헤더 버튼) — "개발"/"지원" 탭에서만 노출(요청사항 1/2) */}
      {showResearchNotesModal && (
        <ResearchNotesListModal
          category={catMeta.map[catFilter]}
          canWrite={canWriteResearchNotes}
          title={(catMeta.map[catFilter] || '') + ' 연구노트'}
          onClose={() => setShowResearchNotesModal(false)}
        />
      )}

      {/* 업무요청 모달(헤더 버튼) — 요청내역이 먼저 뜨고, 상단 "+ 업무요청" 버튼으로 등록폼을 연다 */}
      {showRequestModal && (
        <TaskRequestListModal
          tasks={list}
          userList={userList}
          receivedCount={receivedRequestCount}
          openRequestId={pendingOpenRequestId}
          onClose={() => { setShowRequestModal(false); setPendingOpenRequestId(null); }}
          onChanged={loadRequestCount}
        />
      )}

      {/* 관리항목(구분) 등록/수정/삭제/순서변경 모달(헤더 버튼) */}
      {showCategoryManager && (
        <CategoryManagerModal
          categories={catMeta.list}
          onClose={() => setShowCategoryManager(false)}
          onChanged={() => { reloadCategories(); load(); }}
        />
      )}

      {/* AI리포트 모달(헤더 버튼) — 경영분석의 "주간회의 종합리포트" 기능 실행 */}
      {showAiReportModal && (
        <AiWeeklyReportModal onClose={() => setShowAiReportModal(false)} />
      )}

    </div>
  );
}

// ── 관리항목(구분) 관리 모달 — 업무관리 상단 탭(구분)을 등록/수정/삭제/순서변경한다.
// 정식명칭(full_label)을 바꾸면 서버가 기존 업무들의 category 값도 함께 바꿔주므로(cascade),
// 여기서는 저장 후 onChanged로 상위(Tasks) 목록/탭을 함께 새로고침해준다.
function CategoryManagerModal({ categories, onClose, onChanged }) {
  const [rows, setRows] = useState(categories);
  const [newShort, setNewShort] = useState('');
  const [newFull, setNewFull]   = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editShort, setEditShort] = useState('');
  const [editFull, setEditFull]   = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setRows(categories); }, [categories]);

  const inp = { padding:'8px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, outline:'none' };

  const handleAdd = async () => {
    if (!newShort.trim() || !newFull.trim()) return setErr('짧은 표기와 정식명칭을 모두 입력해주세요.');
    setSaving(true); setErr('');
    try {
      const res = await API.post('/tasks/categories', { short_label: newShort.trim(), full_label: newFull.trim() });
      setRows(r => [...r, res.data]);
      setNewShort(''); setNewFull('');
      onChanged();
    } catch (e) {
      setErr(e.response?.data?.message || '등록 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditShort(c.short_label); setEditFull(c.full_label); setErr(''); };
  const cancelEdit = () => setEditingId(null);
  const handleSaveEdit = async (id) => {
    if (!editShort.trim() || !editFull.trim()) return setErr('짧은 표기와 정식명칭을 모두 입력해주세요.');
    setSaving(true); setErr('');
    try {
      await API.put(`/tasks/categories/${id}`, { short_label: editShort.trim(), full_label: editFull.trim() });
      setRows(rs => rs.map(r => r.id === id ? { ...r, short_label: editShort.trim(), full_label: editFull.trim() } : r));
      setEditingId(null);
      onChanged();
    } catch (e) {
      setErr(e.response?.data?.message || '수정 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`'${c.full_label}' 관리항목을 삭제하시겠습니까?`)) return;
    setErr('');
    try {
      await API.delete(`/tasks/categories/${c.id}`);
      setRows(rs => rs.filter(r => r.id !== c.id));
      onChanged();
    } catch (e) {
      setErr(e.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  // 순서변경 — 위/아래 화살표로 sort_order를 맞바꾼다(간단하고 오류 위험이 적은 방식)
  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const next = rows.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setRows(next);
    try {
      await API.put('/tasks/categories-reorder', { ids: next.map(r => r.id) });
      onChanged();
    } catch (e) {
      setErr('순서 변경 중 오류가 발생했습니다.');
    }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2200 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:520, maxWidth:'92vw',
        maxHeight:'85vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <h3 style={{ fontSize:18, fontWeight:700, margin:0 }}>🗂️ 관리항목</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <p style={{ fontSize:12.5, color:'#888', margin:'0 0 16px' }}>
          상단 구분 탭을 등록/수정/순서변경합니다. 짧은 표기는 탭에, 정식명칭은 업무등록 시 구분값으로 사용됩니다.
        </p>

        {err && <div style={{ background:'#fee2e2', color:'#991b1b', padding:'8px 12px', borderRadius:8, fontSize:12.5, marginBottom:12 }}>{err}</div>}

        <div style={{ border:'1px solid #eee', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
          {rows.map((c, i) => (
            <div key={c.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px',
              borderBottom: i < rows.length - 1 ? '1px solid #f2f2f2' : 'none' }}>
              <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                <button onClick={() => move(i, -1)} disabled={i===0}
                  style={{ background:'none', border:'none', cursor:i===0?'default':'pointer', color:i===0?'#ddd':'#888', fontSize:11, lineHeight:1 }}>▲</button>
                <button onClick={() => move(i, 1)} disabled={i===rows.length-1}
                  style={{ background:'none', border:'none', cursor:i===rows.length-1?'default':'pointer', color:i===rows.length-1?'#ddd':'#888', fontSize:11, lineHeight:1 }}>▼</button>
              </div>
              <span style={{ width:12, height:12, borderRadius:4, background:c.color, flexShrink:0 }} />
              {editingId === c.id ? (
                <>
                  <input value={editShort} onChange={e => setEditShort(e.target.value)} style={{ ...inp, width:56 }} maxLength={3} />
                  <input value={editFull} onChange={e => setEditFull(e.target.value)} style={{ ...inp, flex:1 }} />
                  <button onClick={() => handleSaveEdit(c.id)} disabled={saving}
                    style={{ padding:'6px 10px', borderRadius:6, border:'none', background:'#185FA5', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>저장</button>
                  <button onClick={cancelEdit} style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #ddd', background:'#fff', fontSize:12, cursor:'pointer' }}>취소</button>
                </>
              ) : (
                <>
                  <span style={{ fontSize:13, fontWeight:600, width:56, color:'#333' }}>{c.short_label}</span>
                  <span style={{ fontSize:13, color:'#555', flex:1 }}>{c.full_label}</span>
                  <button onClick={() => startEdit(c)} style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #ddd', background:'#fff', fontSize:12, cursor:'pointer' }}>수정</button>
                  <button onClick={() => handleDelete(c)} style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #fecaca', background:'#fff', color:'#dc2626', fontSize:12, cursor:'pointer' }}>삭제</button>
                </>
              )}
            </div>
          ))}
          {!rows.length && <div style={{ padding:'20px 0', textAlign:'center', color:'#bbb', fontSize:13 }}>등록된 관리항목이 없습니다.</div>}
        </div>

        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <input placeholder="짧은표기(3자)" value={newShort} onChange={e => setNewShort(e.target.value)} style={{ ...inp, width:100 }} maxLength={3} />
          <input placeholder="정식명칭" value={newFull} onChange={e => setNewFull(e.target.value)} style={{ ...inp, flex:1 }} />
          <button onClick={handleAdd} disabled={saving}
            style={{ padding:'8px 14px', borderRadius:6, border:'none', background:'#16a34a', color:'#fff', fontSize:12.5, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
            + 추가
          </button>
        </div>
      </div>
    </div>
  );
}

// ── AI리포트 모달(헤더 버튼) ─────────────────────────────
// 2026-08 요청: 경영분석(구 AI 어시스턴트) 메뉴의 "주간회의 종합리포트" 기능을 업무관리 화면
// 안에서도 바로 실행할 수 있게 모달로 노출한다. 생성/저장 로직은 그쪽과 동일한 백엔드 라우트
// (POST /ai/reports/weekly/generate, GET /ai/reports)를 그대로 재사용하고, UI만 이 화면의
// 관리항목 모달과 같은 오버레이 스타일로 별도 구현했다(AiAssistant.js의 컴포넌트는 export되어
// 있지 않아 그대로 재사용할 수 없었음).
function AiWeeklyReportModal({ onClose }) {
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [openContent, setOpenContent] = useState('');

  const loadHistory = () => {
    API.get('/ai/reports', { params: { type: 'weekly', limit: 10 } }).then(r => setHistory(r.data)).catch(() => setHistory([]));
  };
  useEffect(() => { loadHistory(); }, []);

  const run = () => {
    setGenerating(true); setError(''); setResult(null);
    API.post('/ai/reports/weekly/generate').then(r => { setResult(r.data); loadHistory(); }).catch(err => {
      setError(err?.response?.data?.message || 'AI 리포트 생성에 실패했습니다. 경영분석 메뉴 권한 및 서버 AI 연결 상태를 확인해주세요.');
    }).finally(() => setGenerating(false));
  };

  const toggleOpen = (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id); setOpenContent('불러오는 중...');
    API.get(`/ai/reports/${id}`).then(r => setOpenContent(r.data.content)).catch(() => setOpenContent('불러오지 못했습니다.'));
  };

  const openPdf = async (id) => {
    try {
      const res = await API.get(`/ai/reports/${id}/pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch (e) {
      alert('PDF를 여는 중 오류가 발생했습니다.');
    }
  };

  const badge = (label, color, bg, value) => (
    <span style={{ background: bg, color, padding: '4px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600 }}>{label} {value ?? 0}건</span>
  );
  const reportBoxStyle = { whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 14, color: '#1f2937', background: '#F9FAFB', border: '1px solid #eee', borderRadius: 8, padding: 16 };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2200 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:640, maxWidth:'92vw',
        maxHeight:'85vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <h3 style={{ fontSize:18, fontWeight:700, margin:0 }}>🤖 AI리포트 - 주간회의 종합리포트</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <p style={{ fontSize:12.5, color:'#888', margin:'0 0 16px' }}>
          완료/진행중/지연/문제(출고·생산) 현황을 종합해 주간회의용 리포트를 생성합니다. 경영분석
          메뉴의 "주간회의 종합리포트"와 동일한 기능입니다(서버 컴퓨터에 설치된 로컬 AI로만 작동).
        </p>

        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
          <button onClick={run} disabled={generating}
            style={{ padding:'8px 16px', borderRadius:6, border:'none', cursor: generating ? 'default' : 'pointer',
              background: generating ? '#93C5FD' : '#2563EB', color:'#fff', fontSize:13, fontWeight:600 }}>
            {generating ? '생성 중...' : '지금 생성'}
          </button>
        </div>

        {error && <div style={{ background:'#fee2e2', color:'#991b1b', padding:'8px 12px', borderRadius:8, fontSize:12.5, marginBottom:12 }}>{error}</div>}

        {result && (
          <div style={{ marginBottom:16 }}>
            <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
              {badge('완료', '#059669', '#ECFDF5', result.stats?.doneCount)}
              {badge('진행중', '#374151', '#F3F4F6', result.stats?.inProgressCount)}
              {badge('지연', '#DC2626', '#FEF2F2', result.stats?.overdueCount)}
              {badge('문제(출고/생산)', '#B45309', '#FFFBEB', result.stats?.problemCount)}
            </div>
            <div style={reportBoxStyle}>{result.content}</div>
            {result.pdfPath && <div style={{ fontSize:11, color:'#9CA3AF', marginTop:8 }}>PDF로도 저장되었습니다.</div>}
          </div>
        )}

        {history.length > 0 && (
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:'#374151', marginBottom:8 }}>지난 리포트</div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {history.map(r => (
                <div key={r.id} style={{ border:'1px solid #eee', borderRadius:6, padding:'8px 12px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:12, color:'#6B7280' }}>
                    <span>{new Date(r.created_at).toLocaleString('ko-KR')} · {r.generated_by === 'scheduler' ? '자동생성' : (r.generated_by || '-')}</span>
                    <span>
                      <button onClick={() => toggleOpen(r.id)} style={{ border:'none', background:'none', color:'#2563EB', cursor:'pointer', marginRight:10 }}>
                        {openId === r.id ? '접기' : '보기'}
                      </button>
                      {r.pdf_path && (
                        <button onClick={() => openPdf(r.id)} style={{ border:'none', background:'none', color:'#059669', cursor:'pointer' }}>PDF</button>
                      )}
                    </span>
                  </div>
                  {openId === r.id && <div style={{ ...reportBoxStyle, marginTop:8 }}>{openContent}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── 상세 모달 ─────────────────────────────────────
// 영업관리 > 영업활동 상세의 "영업지원" 목록에서 항목을 클릭했을 때도 이 모달을 그대로 재사용한다
// (업무관리 목록에서 항목을 클릭한 것과 동일한 화면이 뜨도록) — SalesActivities.js에서 import한다.
export function DetailModal({ task, tasks, userList, onClose, onEdit, onDelete, onStatusChange, onTasksChanged, onRequestSaved }) {
  // 관리자/생산관리자/협력사 역할 개념이 없어졌으므로, 최고관리자 외에는 예외 없이
  // 계정관리 > 권한설정에서 부여한 값을 그대로 따릅니다.
  const { hasDetail, canWrite: canWriteMenu } = usePermissions();
  const canDeleteTask   = hasDetail('tasks', 'delete_allowed');
  const canChangeStatus = hasDetail('tasks', 'status_change_allowed');
  const canWriteMinutes = hasDetail('tasks', 'minutes_write_allowed');
  const canWriteResearchNotes = hasDetail('tasks', 'research_note_write_allowed');
  const canRequestTask  = hasDetail('tasks', 'request_write_allowed');
  const canDownload     = hasDetail('tasks', 'download_allowed');
  const canWriteTasks = canWriteMenu('tasks');
  const { catMeta } = useTaskCategories();
  const [comments, setComments]     = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentFile, setCommentFile] = useState(null); // 히스토리(댓글) 작성 시 함께 첨부할 파일
  const [current, setCurrent]       = useState(task);
  const [minutes, setMinutes]       = useState([]);           // 이 업무에 연결된 회의록 목록
  const [showMinutesModal, setShowMinutesModal] = useState(false);
  const [editMinute, setEditMinute] = useState(null);
  const [viewMinute, setViewMinute] = useState(null);          // 회의록 목록 클릭 시 먼저 보여줄 읽기전용 보기
  const [showLinkedRequests, setShowLinkedRequests] = useState(false); // 이 업무에 걸린 업무요청 목록(+새 요청 작성)
  const [showResearchNotes, setShowResearchNotes] = useState(false); // 이 업무에 연결된 연구노트 목록(요청사항 3/4)
  // 연구노트 버튼은 이 업무의 실제 구분(제품개발/지원사업)일 때만 노출 — 목록에서 어느 탭이 열려있었는지와 무관
  const isTaskDevOrSupport = current.category === '제품개발' || current.category === '지원사업';
  const contentViewRef = useRef(null);

  const loadComments = () =>
    API.get('/tasks/'+task.id+'/comments').then(r => setComments(r.data));
  useEffect(() => { loadComments(); }, [task.id]);

  // 다중 첨부파일(2026-09) — 등록/수정 화면에서 여러 개를 첨부할 수 있게 되어, 상세보기에서도
  // (예전 단일첨부 file_name과 별개로) task_attachments 목록을 함께 보여준다.
  const [attachments, setAttachments] = useState([]);
  useEffect(() => {
    API.get('/tasks/'+task.id+'/attachments').then(r => setAttachments(r.data || [])).catch(() => {});
  }, [task.id]);

  // 업무내용에 붙여넣어진 스크린샷 이미지는 인증이 필요하므로, 렌더링 후 다시 인증된 URL로 채워넣음
  useEffect(() => {
    if (!contentViewRef.current) return;
    const imgs = Array.from(contentViewRef.current.querySelectorAll('img[data-filename]'));
    imgs.forEach(async (img) => {
      const filename = img.getAttribute('data-filename');
      if (!filename) return;
      try {
        const res = await API.get('/tasks/image/' + filename, { responseType: 'blob' });
        img.src = window.URL.createObjectURL(res.data);
      } catch (e) { /* 이미지를 불러오지 못해도 나머지 내용은 그대로 둠 */ }
    });
    // current.id도 의존성에 넣는 이유: 회의록 미리보기 모달(MeetingMinuteViewModal)에서 이미
    // 겪었던 것과 같은 종류의 버그 — 이 상세 모달 컴포넌트가 언마운트되지 않은 채 다른 업무의
    // task로 재사용되는 경로(예: 목록에서 연달아 다른 항목 클릭)가 생기면, content 문자열만으로는
    // 이미지 교체가 누락될 수 있다. id를 명시적으로 넣어 업무가 바뀔 때마다 항상 다시 교체되게 한다.
  }, [current.id, current.content]);

  const loadMinutes = () =>
    API.get('/meeting-minutes/task/'+task.id).then(r => setMinutes(r.data)).catch(()=>{});
  useEffect(() => { loadMinutes(); }, [task.id]);

  const addComment = async () => {
    if (!newComment.trim() && !commentFile) return;
    if (commentFile) {
      const fd = new FormData();
      fd.append('content', newComment);
      fd.append('file', commentFile);
      await API.post('/tasks/'+task.id+'/comments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } else {
      await API.post('/tasks/'+task.id+'/comments', { content: newComment });
    }
    setNewComment(''); setCommentFile(null); loadComments();
  };
  const deleteComment = async (cid) => {
    await API.delete('/tasks/'+task.id+'/comments/'+cid);
    loadComments();
  };
  // 첨부파일(업무 파일, 댓글 파일 공통) 인증 다운로드 — <a href>는 Authorization 헤더가
  // 실리지 않아(브라우저 순수 이동이라 axios 인터셉터를 거치지 않음) 로그인이 필요합니다가
  // 뜨던 버그를 막기 위해, blob으로 받아 임시 객체URL을 만들어 강제 다운로드시킨다.
  const handleFileDownload = async (url, displayName) => {
    try {
      const res = await API.get(url, { responseType: 'blob' });
      const objUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = objUrl; a.download = displayName || 'download';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(objUrl);
    } catch (e) {
      alert('파일을 다운로드할 수 없습니다.');
    }
  };
  const changeStatus = async (newStatus) => {
    await API.patch('/tasks/'+task.id+'/status', { status: newStatus });
    const updated = { ...current, status: newStatus };
    setCurrent(updated); onStatusChange(updated);
  };

  const visText = visLabel(current.visibility);

  // 상세 내용 전체(히스토리 포함)를 별도 창으로 세로 인쇄 — 동시에 같은 내용을 서버에서 PDF로
  // 변환해 업무구분에 맞는 폴더(D:\data_Storage\Tasks\01_gen~08_etc, 회의록과 동일한 폴더체계)에
  // 저장한다(서버에 PDF 변환 도구가 없는 등 실패하면 예전처럼 브라우저 자체 인쇄로 대체).
  const handlePrint = async () => {
    let includeFile = true;
    if (current.file_name) {
      includeFile = window.confirm('첨부파일 정보를 인쇄 내용에 포함하시겠습니까?\n(취소를 누르면 첨부파일 없이 인쇄합니다)');
    }

    const printUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const now = new Date();
    const printedAt = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printedBy = printedByLabel(printUser); // 출력자: 부서 직급 이름 (최고관리자는 "최고관리자"로 표시)

    const infoRows = [
      ['구분', current.category],
      ['중요도', current.priority],
      ['상태', current.status],
      ['공개범위', visText],
      ['담당자', formatManager(current.manager, userList)],
      ['등록자', formatManager(current.created_by, userList)],
      ['시작일', current.start_date],
      ['완료예정일', current.end_date],
    ].filter(([,v]) => v);

    const infoCell = ([label,val]) => `
        <td style="color:#888;padding:6px 10px;border-bottom:1px solid #eee;">${label}</td>
        <td style="padding:6px 10px;border-bottom:1px solid #eee;font-weight:600;">${val}</td>`;
    const infoPairs = [];
    for (let i=0;i<infoRows.length;i+=2) infoPairs.push([infoRows[i], infoRows[i+1]]);
    const infoHtml = infoPairs.map(([a,b]) =>
      `<tr>${infoCell(a)}${b ? infoCell(b) : '<td colspan="2"></td>'}</tr>`
    ).join('');

    // 업무내용은 이제 이미지 붙여넣기를 지원하는 HTML이므로 이스케이프하지 않고 그대로 출력합니다.
    // (단, 붙여넣은 스크린샷은 이 인쇄용 새 창에서는 인증된 URL로 다시 채워지지 않아 보이지 않을 수 있습니다)
    const contentHtml = current.content
      ? `<div style="padding:12px;background:#f8fafc;border-radius:8px;font-size:13px;white-space:pre-wrap;line-height:1.7;margin-bottom:16px;">${current.content}</div>`
      : '';

    const fileHtml = (includeFile && current.file_name)
      ? `<div style="margin-bottom:16px;font-size:13px;">📎 첨부파일: ${current.file_ori||current.file_name}</div>`
      : '';

    const historyHtml = comments.length===0
      ? `<div style="color:#999;padding:16px 0;">등록된 히스토리가 없습니다.</div>`
      : comments.map(c =>
          `<div style="padding:10px 12px;background:#fafafa;border-radius:8px;margin-bottom:8px;">
             <div style="font-size:13px;white-space:pre-wrap;line-height:1.6;">${String(c.content||'').replace(/</g,'&lt;')}</div>
             <div style="font-size:11px;color:#aaa;margin-top:4px;">${displayByName(c.created_by, userList)||''} · ${(c.created_at||'').slice(0,16)}</div>
           </div>`
        ).join('');

    const html = `<!DOCTYPE html><html><head><title>업무 상세 - ${current.title}</title>
      <style>
        @page { size: A4 portrait; margin: 0; }
        html, body { width:100%; }
        body { font-family: sans-serif; color:#222; padding: 15mm 15mm 20mm; box-sizing:border-box; }
        h2 { font-size:20px; margin-bottom:16px; }
        h3 { font-size:15px; margin:20px 0 10px; }
        /* table-layout:fixed + colgroup으로 라벨/값 4열 너비를 % 고정해서, 값이 짧은 항목이
           많아도 표가 용지 가로폭 전체를 꽉 채우도록 한다(예전에는 auto 레이아웃이라 내용이
           짧으면 표 오른쪽에 빈 공간이 남는 문제가 있었음). */
        table { border-collapse: collapse; width:100%; table-layout:fixed; margin-bottom:16px; }
        table col.lbl { width:15%; }
        table col.val { width:35%; }
        td { word-break:break-word; }
        .footer { display:flex; justify-content:space-between; margin-top:14px; padding-top:8px;
          border-top:1px solid #ddd; font-size:11px; color:#555; }
      </style></head><body>
      <h2>📋 ${current.title}</h2>
      <table><colgroup><col class="lbl"><col class="val"><col class="lbl"><col class="val"></colgroup>${infoHtml}</table>
      ${contentHtml}
      ${fileHtml}
      <h3>💬 히스토리 (${comments.length})</h3>
      ${historyHtml}
      <div class="footer"><span>출력일시: ${printedAt}</span><span>출력자: ${printedBy}</span></div>
      </body></html>`;

    logPrint('업무관리', current.title || '업무 상세');
    try {
      const res = await API.post(`/tasks/${current.id}/print/detail-pdf`, { html }, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
      iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = 'none';
      iframe.src = blobUrl;
      // iframe의 onload는 PDF뷰어(플러그인) 껍데기가 뜨는 시점일 뿐, 실제 페이지 크기에 맞춰
      // 확대/축소(fit-to-page)를 계산하는 내부 렌더링은 그 뒤에 비동기로 이어진다. onload되자마자
      // 바로 print()를 부르면 그 계산이 끝나기 전 상태(축소되기 전/덜 채워진 상태)가 그대로
      // 인쇄 미리보기에 잡혀버려서, 실제 저장된 PDF 파일은 정상인데 인쇄창 미리보기만 오른쪽에
      // 여백이 남아 보이는 원인이 된다. 약간의 지연을 줘서 PDF뷰어가 완전히 자리잡은 뒤 인쇄를
      // 호출한다.
      iframe.onload = () => { setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} }, 600); };
      document.body.appendChild(iframe);
    } catch (err) {
      // 서버에 PDF 변환 도구(puppeteer)가 없는 등 실패 시, 예전 방식(브라우저 자체 인쇄)으로 대체
      const pw = window.open('', '_blank', 'width=800,height=1000');
      pw.document.write(html);
      pw.document.close(); pw.focus();
      setTimeout(() => { pw.print(); pw.close(); }, 400);
    }
  };

  // 업무 상세 내용을 엑셀로 내보내기 — 인쇄화면(handlePrint)과 동일한 구성(제목 → 정보표 →
  // 업무내용 → 첨부파일 → 히스토리 → 출력일시/출력자)으로 한 시트에 그대로 옮겨 담는다.
  // (예전에는 "업무정보/히스토리" 두 시트에 단순 표 형태로만 담아서 인쇄물과 형태가 달랐음)
  const thinBorder = { style:'thin', color:{argb:'FFE5E7EB'} };
  const estimateLines = (text, charsPerLine) => {
    const s = String(text||'');
    if (!s) return 1;
    return s.split('\n').reduce((sum, line) => sum + Math.max(1, Math.ceil((line.length||1)/charsPerLine)), 0);
  };

  const downloadExcel = async () => {
    const exportUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    const now = new Date();
    const printedAt = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printedBy = printedByLabel(exportUser); // 출력자: 부서 직급 이름 (최고관리자는 "최고관리자"로 표시)

    // 인쇄화면(handlePrint)과 동일한 항목 구성 — 값이 있는 것만, 2개씩 짝지어 4열 표로 배치
    const infoRows = [
      ['구분', current.category],
      ['중요도', current.priority],
      ['상태', current.status],
      ['공개범위', visText],
      ['담당자', formatManager(current.manager, userList)],
      ['등록자', formatManager(current.created_by, userList)],
      ['시작일', current.start_date],
      ['완료예정일', current.end_date],
    ].filter(([,v]) => v);
    const infoPairs = [];
    for (let i=0;i<infoRows.length;i+=2) infoPairs.push([infoRows[i], infoRows[i+1]]);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('업무 상세');
    ws.columns = [{ width:16 },{ width:34 },{ width:16 },{ width:34 }];

    let r = 1;
    ws.mergeCells(r,1,r,4);
    const titleCell = ws.getCell(r,1);
    titleCell.value = `📋 ${current.title||''}`;
    titleCell.font = { size:16, bold:true, color:{argb:'FF1A1A1A'} };
    ws.getRow(r).height = 26;
    r++;

    // 정보표(구분/중요도/상태/공개범위/담당자/등록자/시작일/완료예정일) — 라벨칸은 회색 글자,
    // 값칸은 굵게, 인쇄화면과 동일하게 2쌍씩 한 행에 배치
    infoPairs.forEach(([a,b]) => {
      const cells = [ws.getCell(r,1), ws.getCell(r,2), ws.getCell(r,3), ws.getCell(r,4)];
      cells.forEach(c => { c.border = { bottom:thinBorder }; });
      cells[0].value = a[0]; cells[0].font = { color:{argb:'FF888888'} };
      cells[1].value = a[1]||''; cells[1].font = { bold:true };
      if (b) {
        cells[2].value = b[0]; cells[2].font = { color:{argb:'FF888888'} };
        cells[3].value = b[1]||''; cells[3].font = { bold:true };
      }
      ws.getRow(r).height = 20;
      r++;
    });
    r++; // 빈 줄

    // 업무내용 — 인쇄화면과 동일하게 연한 회색 배경 박스로 표시
    const contentText = htmlToText(current.content)||'';
    if (contentText) {
      const contentStartRow = r;
      ws.mergeCells(r,1,r,4);
      const cell = ws.getCell(r,1);
      cell.value = contentText;
      cell.alignment = { wrapText:true, vertical:'top' };
      cell.font = { size:11, color:{argb:'FF222222'} };
      ws.getRow(r).height = Math.min(400, Math.max(24, estimateLines(contentText, 95)*15 + 10));
      for (let c=1;c<=4;c++) ws.getCell(contentStartRow,c).fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFF8FAFC'} };
      r += 2;
    }

    // 첨부파일
    if (current.file_ori || current.file_name) {
      ws.mergeCells(r,1,r,4);
      ws.getCell(r,1).value = `📎 첨부파일: ${current.file_ori||current.file_name}`;
      ws.getCell(r,1).font = { size:11 };
      r += 2;
    }

    // 히스토리
    ws.mergeCells(r,1,r,4);
    const histHeadCell = ws.getCell(r,1);
    histHeadCell.value = `💬 히스토리 (${comments.length})`;
    histHeadCell.font = { size:13, bold:true };
    ws.getRow(r).height = 20;
    r++;

    if (!comments.length) {
      ws.mergeCells(r,1,r,4);
      ws.getCell(r,1).value = '등록된 히스토리가 없습니다.';
      ws.getCell(r,1).font = { color:{argb:'FF999999'} };
      r++;
    } else {
      comments.forEach(c => {
        const text = c.content||'';
        const meta = `${displayByName(c.created_by, userList)||''} · ${(c.created_at||'').slice(0,16)}`;
        ws.mergeCells(r,1,r,4);
        const cell = ws.getCell(r,1);
        cell.value = { richText: [
          { font:{ size:11, color:{argb:'FF222222'} }, text: text + '\n' },
          { font:{ size:9, color:{argb:'FFAAAAAA'} }, text: meta },
        ]};
        cell.alignment = { wrapText:true, vertical:'top' };
        for (let c2=1;c2<=4;c2++) ws.getCell(r,c2).fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFFAFAFA'} };
        ws.getRow(r).height = Math.min(300, Math.max(30, (estimateLines(text, 95)+1)*15 + 8));
        r++;
      });
    }
    r++;

    // 출력일시 / 출력자
    ws.getCell(r,1).value = `출력일시: ${printedAt}`;
    ws.getCell(r,1).font = { size:10, color:{argb:'FF555555'} };
    ws.getCell(r,4).value = `출력자: ${printedBy}`;
    ws.getCell(r,4).font = { size:10, color:{argb:'FF555555'} };
    ws.getCell(r,4).alignment = { horizontal:'right' };

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type:'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const exportFileName = `업무_${(current.title||'상세').replace(/[\\/:*?"<>|]/g,'_')}_${new Date().toISOString().slice(0,10)}.xlsx`;
    const a = document.createElement('a');
    a.href = url; a.download = exportFileName;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    logDownload('업무관리', exportFileName);
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, width:720, maxWidth:'92vw', maxHeight:'90vh', overflowY:'auto' }}
        onClick={e=>e.stopPropagation()}>

        {/* 헤더 */}
        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #f0f0f0' }}>
          {/* 버그수정: 예전에는 뱃지+제목을 담은 열과 버튼(내보내기/인쇄/닫기) 열을 좌우로 나란히
              배치했다(flex row, justifyContent:space-between) — 그러면 제목이 버튼 열 폭만큼
              줄어든 너비를 기준으로 줄바꿈되어, 제목이 둘째 줄로 넘어가면 그 줄의 오른쪽(버튼이
              차지하던 폭)은 비어 있는데도 줄바꿈이 일어나는 것처럼 보였다("우측에 빈공간이
              있음에도 줄바꿈"). 버튼 줄과 제목 줄을 아예 분리된 두 줄로 쌓아, 제목이 모달
              전체 너비를 그대로 쓰도록 한다. */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
              <span style={{ fontSize:12, padding:'3px 8px', borderRadius:20, fontWeight:600,
                background:catMeta.tabBg[catMeta.fullToShort[current.category]]||'#f0f0f0',
                color:catMeta.tabColor[catMeta.fullToShort[current.category]]||'#555' }}>{current.category}</span>
              <span style={{ fontSize:12, padding:'3px 8px', borderRadius:20, fontWeight:600,
                background:PRIORITY_BG[current.priority], color:PRIORITY_COLOR[current.priority] }}>
                {current.priority}
              </span>
              <span style={{ fontSize:12, padding:'3px 8px', borderRadius:20, fontWeight:600,
                background:STATUS_BG[current.status], color:STATUS_COLOR[current.status] }}>
                {current.status}
              </span>
              {/* 공개범위 뱃지 */}
              <VisBadge vis={current.visibility} />
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
              {/* 2026-10 요청: "업무상세에서 업무요청 버튼의 위치를 내보내기 버튼 왼쪽으로 이동"
                  — 예전엔 아래쪽 상태변경 줄 우측에 있었는데, 헤더의 내보내기/인쇄/닫기 버튼
                  줄로 옮기고 내보내기 버튼 바로 왼쪽에 배치한다. */}
              {canRequestTask && (
                <button onClick={() => setShowLinkedRequests(true)}
                  style={{ background:'#fff', color:'#0F766E', border:'1px solid #0F766E', borderRadius:6,
                    padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  📨 업무요청
                </button>
              )}
              <button onClick={downloadExcel}
                style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:6,
                  padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                📥 내보내기
              </button>
              <button onClick={handlePrint}
                style={{ background:'#475569', color:'#fff', border:'none', borderRadius:6,
                  padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                🖨️ 인쇄
              </button>
              <button onClick={onClose}
                style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
            </div>
          </div>
          {/* 요청사항: "상세페이지 내 제목 위아래 마진적용" — 제목 위/아래에 20px 여백을 준다. */}
          <h3 style={{ fontSize:18, fontWeight:700, margin:'20px 0' }}>{current.title}</h3>

          {/* 상태 빠른 변경("상태변경" 세부권한 필요) + 연구노트(제품개발/지원사업 구분일 때만) —
              둘 중 하나라도 켜져 있으면 이 줄을 보여주고, 상태 버튼은 왼쪽에 / 연구노트 버튼은
              이 줄 가장 우측에 배치한다(예전에는 "상태 변경:" 라벨 글자가 있었으나 삭제함).
              2026-10 요청: 업무요청 버튼은 헤더의 내보내기 버튼 왼쪽으로 이동했다. */}
          {(canChangeStatus || isTaskDevOrSupport) && (
            <div style={{ display:'flex', gap:6, marginTop:12, alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              {canChangeStatus && ['대기','진행중','완료','보류'].map(s => (
                <button key={s} onClick={() => changeStatus(s)}
                  style={{ padding:'4px 8px', borderRadius:6, border:'1px solid', fontSize:12, cursor:'pointer', fontWeight:600,
                    borderColor:current.status===s?STATUS_COLOR[s]:'#ddd',
                    background:current.status===s?STATUS_BG[s]:'#fff',
                    color:current.status===s?STATUS_COLOR[s]:'#999' }}>{s}</button>
              ))}
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
              {/* 연구노트 — 이 업무가 제품개발/지원사업 구분일 때만 노출(요청사항 3/4).
                  목록은 누구나 볼 수 있고, 작성 버튼만 세부권한(연구노트 작성)으로 게이팅한다. */}
              {isTaskDevOrSupport && (
                <button onClick={() => setShowResearchNotes(true)}
                  style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #0891B2', fontSize:12,
                    cursor:'pointer', fontWeight:600, background:'#fff', color:'#0891B2' }}>
                  🧪 연구노트
                </button>
              )}
              </div>
            </div>
          )}
        </div>

        {/* 상세 정보 */}
        <div style={{ padding:'16px 24px', borderBottom:'1px solid #f0f0f0' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px', marginBottom:12 }}>
            {[
              ['담당부서',  formatDepartment(current.manager, userList)],
              // 버그수정: 여기서만 담당자(manager)가 아니라 등록자(created_by)를 잘못 참조하고
              // 있었다 — 등록폼에서 담당자를 다른 사람(예: 이해성)으로 입력해도 상세화면에는
              // 항상 로그인한 등록자 본인(예: 김동일)이 담당자로 표시되던 원인. 목록(리스트)의
              // "담당자" 컬럼과 아래 "등록/수정 정보"의 등록자 표기는 원래부터 각각 manager/
              // created_by를 올바르게 참조하고 있어 문제가 없었다.
              ['담당자',    formatManager(current.manager, userList)],
              ['협력부서 담당자', current.co_manager],
              ['시작일',    current.start_date],
              ['완료예정일',current.end_date],
            ].map(([label,val]) => val ? (
              <div key={label} style={{ fontSize:14 }}>
                <span style={{ color:'#888', marginRight:8 }}>{label}</span>
                <span style={{ fontWeight:600 }}>
                  {val} {label==='완료예정일' && <DdayBadge date={current.end_date} status={current.status} completedAt={current.completed_at} updatedAt={current.updated_at} />}
                </span>
              </div>
            ) : null)}
          </div>

          {/* 등록/수정 정보 */}
          <div style={{ borderTop:'1px solid #f0f0f0', paddingTop:10, marginTop:4 }}>
            <div style={{ display:'flex', gap:6, fontSize:12, color:'#aaa', flexWrap:'wrap' }}>
              <span>✍️ 등록: {formatManager(current.created_by, userList)} · {current.created_at?.slice(0,16)}</span>
              {current.updated_at && (
                <span style={{ color:'#d97706' }}>
                  ✏️ 수정: {current.updated_by} · {current.updated_at?.slice(0,16)}
                </span>
              )}
            </div>
          </div>

          {current.content && (
            <div ref={contentViewRef}
              style={{ marginTop:12, padding:'12px 14px', background:'#f8fafc', borderRadius:8,
                fontSize:13, color:'#222', lineHeight:1.7, whiteSpace:'pre-wrap',
                wordBreak:'break-word', overflowWrap:'anywhere' }}
              dangerouslySetInnerHTML={{ __html: current.content }} />
          )}

          {current.file_name && (
            <div style={{ marginTop:12, padding:'12px 14px', background:'#f0f6ff', borderRadius:8,
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:13, color:'#185FA5' }}>📎 {current.file_ori}</span>
              {canDownload && (
                <a
                  href="#"
                  onClick={e => { e.preventDefault(); handleFileDownload('/tasks/download/' + current.file_name, current.file_ori || current.file_name); }}
                  style={{ background:'#185FA5', color:'#fff', padding:'6px 14px',
                    borderRadius:6, fontSize:13, textDecoration:'none', fontWeight:600 }}>
                  다운로드
                </a>
              )}
            </div>
          )}

          {/* 다중 첨부파일(2026-09 추가분) — 위 단일첨부(file_name)와 별개로 여러 개를 보여준다 */}
          {attachments.map(att => (
            <div key={att.id} style={{ marginTop:8, padding:'12px 14px', background:'#f0f6ff', borderRadius:8,
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:13, color:'#185FA5' }}>📎 {att.orig_name}</span>
              {canDownload && (
                <a
                  href="#"
                  onClick={e => { e.preventDefault(); handleFileDownload('/tasks/download/' + att.file_name, att.orig_name || att.file_name); }}
                  style={{ background:'#185FA5', color:'#fff', padding:'6px 14px',
                    borderRadius:6, fontSize:13, textDecoration:'none', fontWeight:600 }}>
                  다운로드
                </a>
              )}
            </div>
          ))}
        </div>

        {/* 회의록 */}
        <div style={{ padding:'16px 24px', borderBottom:'1px solid #f0f0f0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <span style={{ fontSize:14, fontWeight:700, color:'#1a1a1a' }}>📝 회의록 ({minutes.length})</span>
            {canWriteMinutes && (
              <button onClick={() => { setEditMinute(null); setShowMinutesModal(true); }}
                style={{ fontSize:12, padding:'5px 10px', background:'#EDE9FE', color:'#7C3AED',
                  border:'1px solid #7C3AED', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                + 새 회의록
              </button>
            )}
          </div>
          {minutes.length===0 ? (
            <div style={{ fontSize:13, color:'#aaa', textAlign:'center', padding:'10px 0' }}>등록된 회의록이 없습니다.</div>
          ) : (
            minutes.map(m => (
              <div key={m.id} onClick={() => setViewMinute(m)}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'8px 10px', borderRadius:8, marginBottom:6, background:'#f8fafc', cursor:'pointer' }}
                onMouseEnter={e=>e.currentTarget.style.background='#f0f6ff'}
                onMouseLeave={e=>e.currentTarget.style.background='#f8fafc'}>
                <span style={{ fontSize:13 }}>
                  <span style={{ fontWeight:600 }}>{m.title}</span>
                  <span style={{ color:'#aaa', marginLeft:8 }}>{m.meeting_date}</span>
                </span>
                <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, fontWeight:600,
                  background: m.status==='완료' ? '#E1F5EE' : '#FAEEDA',
                  color:      m.status==='완료' ? '#0F6E56' : '#854F0B' }}>
                  {m.status}
                </span>
              </div>
            ))
          )}
        </div>

        {viewMinute && (
          <MeetingMinuteViewModal
            minute={viewMinute}
            userList={userList}
            onClose={() => setViewMinute(null)}
            onEdit={() => { setEditMinute(viewMinute); setViewMinute(null); setShowMinutesModal(true); }}
            onDeleted={() => { setViewMinute(null); loadMinutes(); }}
          />
        )}

        {showMinutesModal && (
          <MeetingMinutesModal
            tasks={tasks || []}
            userList={userList}
            editData={editMinute}
            presetTask={!editMinute ? { id: current.id, category: current.category, title: current.title } : null}
            onTasksChanged={onTasksChanged}
            onClose={() => { setShowMinutesModal(false); setEditMinute(null); }}
            onSaved={() => { loadMinutes(); }}
          />
        )}

        {/* 이 업무에 걸린 업무요청 목록(+ 새 요청 작성) — 새 요청을 보내거나 기존 요청을 완료/확인
            처리하면 서버가 이 업무의 히스토리에도 자동으로 남기므로 저장 후 히스토리를 다시 불러온다 */}
        {showLinkedRequests && (
          <TaskLinkedRequestsModal
            linkedTask={current}
            onClose={() => setShowLinkedRequests(false)}
            onChanged={() => { loadComments(); onRequestSaved && onRequestSaved(); }}
          />
        )}

        {/* 이 업무에 연결된 연구노트 목록(+ 새 연구노트 작성) — 요청사항 3/4 */}
        {showResearchNotes && (
          <ResearchNotesListModal
            taskId={current.id}
            presetTask={{ id: current.id, category: current.category, title: current.title }}
            canWrite={canWriteResearchNotes}
            title={(current.title || '업무') + ' - 연구노트'}
            onClose={() => setShowResearchNotes(false)}
          />
        )}

        {/* 히스토리 */}
        <div style={{ padding:'16px 24px' }}>
          <div style={{ fontSize:14, fontWeight:700, marginBottom:12, color:'#1a1a1a' }}>💬 히스토리 ({comments.length})</div>
          <div style={{ maxHeight:180, overflowY:'auto', marginBottom:12 }}>
            {comments.length===0 && (
              <div style={{ fontSize:13, color:'#aaa', textAlign:'center', padding:'16px 0' }}>첫 번째 히스토리를 남겨보세요!</div>
            )}
            {/* 화면에서는 최신 히스토리가 위로 오도록 정렬(인쇄/엑셀 내보내기는 원래 순서 그대로 유지) */}
            {[...comments].reverse().map(c => {
              // 최고관리자(superadmin) 계정은 실명 대신 "최고관리자"로 표시 (등록자/담당자 등
              // 다른 자리와 동일한 규칙 — displayByName이 회원목록에서 role을 확인해 치환)
              const dispBy = displayByName(c.created_by, userList) || c.created_by;
              return (
              <div key={c.id} style={{ display:'flex', gap:10, marginBottom:10 }}>
                <div style={{ width:30, height:30, borderRadius:'50%', background:'#E6F1FB', color:'#185FA5',
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, flexShrink:0 }}>
                  {dispBy?.[0]||'?'}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:13, fontWeight:600 }}>{dispBy}</span>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <span style={{ fontSize:12, color:'#aaa' }}>{c.created_at?.slice(0,16)}</span>
                      {canWriteTasks && (
                        <button onClick={() => deleteComment(c.id)}
                          style={{ background:'none', border:'none', fontSize:12, color:'#dc2626', cursor:'pointer' }}>삭제</button>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize:13, color:'#444', marginTop:3, lineHeight:1.5 }}>{c.content}</div>
                  {c.file_name && (
                    <div style={{ marginTop:6, display:'inline-flex', alignItems:'center', gap:6,
                      background:'#f0f6ff', borderRadius:6, padding:'4px 10px' }}>
                      <span style={{ fontSize:12, color:'#185FA5' }}>📎 {c.file_ori || c.file_name}</span>
                      {canDownload && (
                        <button
                          onClick={() => handleFileDownload('/tasks/comments/download/' + c.file_name, c.file_ori || c.file_name)}
                          style={{ background:'none', border:'none', color:'#185FA5', fontSize:12,
                            fontWeight:600, cursor:'pointer', textDecoration:'underline' }}>다운로드</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {commentFile && (
              <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#185FA5' }}>
                <span>📎 {commentFile.name}</span>
                <button onClick={() => setCommentFile(null)}
                  style={{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:12 }}>제거</button>
              </div>
            )}
            <div style={{ display:'flex', gap:8 }}>
              <input value={newComment} onChange={e=>setNewComment(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&addComment()}
                placeholder="히스토리 내용 입력 후 Enter..."
                style={{ flex:1, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, outline:'none' }} />
              <label style={{ padding:'9px 12px', background:'#f1f5f9', color:'#444', border:'1px solid #ddd',
                borderRadius:8, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center' }}>
                📎
                <input type="file" style={{ display:'none' }}
                  onChange={e => setCommentFile(e.target.files?.[0] || null)} />
              </label>
              <button onClick={addComment}
                style={{ padding:'9px 16px', background:'#185FA5', color:'#fff',
                  border:'none', borderRadius:8, fontSize:14, cursor:'pointer', fontWeight:600 }}>등록</button>
            </div>
          </div>
        </div>

        {/* 수정/삭제 */}
        {(canWriteTasks || canDeleteTask) && (
          <div style={{ padding:'0 24px 20px', display:'flex', gap:8 }}>
            {canWriteTasks && (
              <button onClick={() => onEdit(current)}
                style={{ flex:1, padding:'11px', background:'#E6F1FB', color:'#185FA5',
                  border:'1px solid #185FA5', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
                ✏️ 수정
              </button>
            )}
            {canDeleteTask && (
              <button onClick={() => onDelete(current.id)}
                style={{ flex:1, padding:'11px', background:'#fee2e2', color:'#dc2626',
                  border:'1px solid #dc2626', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
                🗑 삭제
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 등록/수정 폼 ──────────────────────────────────
// salesSupportContext: 영업관리 > 영업활동 상세의 "🤝 영업지원" 버튼으로 열렸을 때만 전달되는
// { customerCompany, partnerCompany, activityId } — 있으면 이 폼은 "업무등록" 폼을 그대로 쓰되
// (1) 구분=영업지원/상태=진행중/중요도=높음을 기본값으로 미리 채우고, (2) 제목을 "영업지원
// ㅣ 영업사 ㅣ 제목" 3단으로 입력받아 조합하고, (3) 저장 시 linked_activity_id를
// 함께 보내 그 영업활동에 연결하고(등록되면 서버가 영업활동의 "영업사항 업데이트 이력"에도 자동
// 기록), (4) 하단 버튼 라벨을 "영업지원 등록"으로 표시한다.
export function TaskForm({ editData, onClose, onSaved, salesSupportContext, defaultCategory }) {
  const isSalesSupport = !!salesSupportContext;
  const { catMeta } = useTaskCategories();
  const [form, setForm] = useState({
    category:   editData?.category   || defaultCategory || (isSalesSupport ? '영업지원' : '일반업무'),
    status:     editData?.status     || '진행중',
    priority:   editData?.priority   || (isSalesSupport ? '높음' : '보통'),
    title:      editData?.title      || '',
    content:    editData?.content    || '',
    manager:    editData?.manager    || '',
    co_manager: editData?.co_manager || '', // 협력부서 담당자 (선택, 비워둘 수 있음)
    start_date: editData?.start_date || getKstDateString(), // 신규 등록 시 기본값=오늘(수정 가능)
    end_date:   editData?.end_date   || '',
    visibility: editData?.visibility || '',
    // 📅 일정관리 등록 체크박스: 신규 등록은 기본 체크, 수정은 저장된 값을 그대로 따름
    // (일정관리에서 이 업무의 일정을 직접 삭제하면 서버에서 자동으로 0으로 되돌려줍니다)
    add_to_schedule: (editData?.add_to_schedule ?? 1) ? '1' : '0',
  });
  // 영업지원 전용: 제목 3단(고객사 영업지원 / 영업사명 또는 고객사명 / 제목) — 앞 두 칸은 그
  // 활동의 정보로 미리 채우고(수정 가능), 세 번째만 사용자가 새로 입력한다. form.title은 이
  // 세 값이 바뀔 때마다 아래 useEffect에서 자동으로 다시 조합된다.
  const [supportCompanyPart, setSupportCompanyPart] = useState(
    salesSupportContext ? (salesSupportContext.partnerCompany || salesSupportContext.customerCompany || '') : ''
  );
  const [supportTitlePart, setSupportTitlePart] = useState('');
  useEffect(() => {
    if (!isSalesSupport) return;
    const composed = ['영업지원', supportCompanyPart, supportTitlePart].filter(Boolean).join(' ㅣ ');
    setForm(f => (f.title === composed ? f : { ...f, title: composed }));
    // eslint-disable-next-line
  }, [isSalesSupport, supportCompanyPart, supportTitlePart]);
  const [scopeOptions, setScopeOptions] = useState([]);
  const [loading, setLoading]   = useState(false);
  // 첨부파일 — 2026-09부터 여러 개(다중) 첨부를 지원한다(자산관리/견적서 등 다른 메뉴와 동일한
  // 방식). 기존 단일첨부(file_name/file_ori)는 과거 데이터 호환을 위해 그대로 남겨두고, 새로
  // 올리는 파일들은 모두 task_attachments 테이블에 저장된다.
  const [files, setFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]); // 수정화면: 이미 첨부된 파일 목록
  // "(예전 단일첨부) 현재: ..." 라벨에 표시할 값 — editData.file_ori를 그대로 쓰면, 그 파일을
  // 목록에서 삭제해도 라벨은 그대로 남아 마치 삭제가 안 된 것처럼 보였다(실제로는 서버의
  // tasks.file_name/file_ori는 지워진 첨부와 별개로 남아있던 값). 삭제 시 서버가 돌려주는
  // 최신 대표파일 값으로 이 상태를 갱신해서 화면에 바로 반영한다.
  const [legacyFile, setLegacyFile] = useState({ name: editData?.file_name || null, ori: editData?.file_ori || null });
  useEffect(() => {
    if (!editData?.id) return;
    API.get(`/tasks/${editData.id}/attachments`).then(r => setExistingAttachments(r.data || [])).catch(() => {});
  }, [editData?.id]);
  const removeExistingAttachment = (att) => {
    if (!window.confirm(`"${att.orig_name}" 첨부파일을 삭제할까요?`)) return;
    API.delete(`/tasks/attachments/${att.id}`)
      .then((res) => {
        setExistingAttachments(list => list.filter(a => a.id !== att.id));
        // 삭제한 파일이 예전 단일첨부(대표파일)였다면, 서버가 알려준 다음 대표파일(또는 없음)로 갱신
        if ('legacy_file_name' in (res.data || {})) {
          setLegacyFile({ name: res.data.legacy_file_name, ori: res.data.legacy_file_ori });
        }
      })
      .catch(e => alert(e.response?.data?.message || '삭제 중 오류가 발생했습니다.'));
  };
  // "(예전 단일첨부)" 항목 자체를 삭제 — 다중첨부 목록(existingAttachments)과는 별개의
  // tasks.file_name/file_ori 필드라서, 위 removeExistingAttachment로는 지울 수 없었다.
  const removeLegacyAttachment = () => {
    if (!editData?.id || !legacyFile.ori) return;
    if (!window.confirm(`"${legacyFile.ori}" 첨부파일을 삭제할까요?`)) return;
    API.delete(`/tasks/${editData.id}/legacy-attachment`)
      .then(() => setLegacyFile({ name: null, ori: null }))
      .catch(e => alert(e.response?.data?.message || '삭제 중 오류가 발생했습니다.'));
  };
  // <a href>는 Authorization 헤더가 실리지 않아 로그인이 필요합니다가 뜨므로(DetailModal의
  // handleFileDownload와 동일한 이유), blob으로 받아 강제 다운로드시킨다.
  const downloadExistingAttachment = async (att) => {
    try {
      const res = await API.get('/tasks/download/' + att.file_name, { responseType: 'blob' });
      const objUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = objUrl; a.download = att.orig_name || att.file_name;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(objUrl);
    } catch (e) {
      alert('파일을 다운로드할 수 없습니다.');
    }
  };

  // 공개범위: 계정관리 - 공개범위 설정에서 만든 공개구분 목록을 그대로 사용(공지사항과 동일)
  // 신규 등록일 때만 기본값으로 지정된 공개구분을 자동 선택합니다.
  useEffect(() => {
    API.get('/disclosure/list').then(r => {
      const scopes = r.data.scopes || [];
      setScopeOptions(scopes);
      if (!editData) {
        const def = scopes.find(s => s.is_default);
        if (def) setForm(f => (f.visibility ? f : { ...f, visibility: def.name }));
      }
    }).catch(() => {});
  }, []);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // 업무내용 편집기 (이미지 붙여넣기 지원) — form.content 에는 HTML이 저장됨(이미지 태그 포함 가능)
  const contentRef = useRef(null);
  const contentLoadedRef = useRef(false);
  // 클립보드로 붙여넣은 이미지의 서버 업로드가 아직 끝나지 않은 채로 저장 버튼을 눌러버리면,
  // 방금 붙인 <img>에 data-filename이 붙기 전 상태(죽은 blob: 주소만 있는 상태)로 그대로
  // 저장되어 버려서, 다시 열어보면(또는 미리보기에서) 이미지가 깨진 아이콘으로 보이는
  // 경쟁조건이 있었다. 붙여넣기마다 만들어지는 업로드 Promise를 여기 모아뒀다가, 저장 직전에
  // 전부 끝날 때까지 기다린 뒤 최신 DOM 내용을 기준으로 저장하도록 한다.
  const pendingImageUploadsRef = useRef([]);

  useEffect(() => {
    if (contentLoadedRef.current || !contentRef.current) return;
    contentLoadedRef.current = true;
    contentRef.current.innerHTML = form.content || '';
    hydrateContentImages();
    // eslint-disable-next-line
  }, []);

  const hydrateContentImages = async () => {
    if (!contentRef.current) return;
    const imgs = Array.from(contentRef.current.querySelectorAll('img[data-filename]'));
    for (const img of imgs) {
      const filename = img.getAttribute('data-filename');
      if (!filename) continue;
      try {
        const res = await API.get('/tasks/image/' + filename, { responseType: 'blob' });
        img.src = window.URL.createObjectURL(res.data);
      } catch (e) { /* 이미지를 불러오지 못해도 나머지 내용은 그대로 둠 */ }
    }
  };

  const handleContentInput = () => {
    if (contentRef.current) setForm(f => ({ ...f, content: contentRef.current.innerHTML }));
  };

  // 클립보드에 이미지가 있으면 바로 삽입(로컬 미리보기) 후 서버에 업로드해서 나중에도 다시 불러올 수 있게 함
  const handleContentPaste = (e) => {
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    let imageFile = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.startsWith('image/')) {
        imageFile = items[i].getAsFile();
        break;
      }
    }
    if (!imageFile) return; // 이미지가 아니면 기본 붙여넣기 동작 사용

    e.preventDefault();
    const localUrl = window.URL.createObjectURL(imageFile);
    document.execCommand('insertImage', false, localUrl);

    const imgs = contentRef.current ? contentRef.current.querySelectorAll(`img[src="${localUrl}"]`) : [];
    const insertedImg = imgs[imgs.length - 1];
    if (insertedImg) insertedImg.style.maxWidth = '100%';

    const uploadPromise = (async () => {
      try {
        const fd = new FormData();
        fd.append('image', imageFile, imageFile.name || 'pasted.png');
        const res = await API.post('/tasks/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (insertedImg && res.data.filename) insertedImg.setAttribute('data-filename', res.data.filename);
      } catch (err) {
        // 업로드에 실패해도 화면에는 미리보기가 남아있음(단, 나중에 다시 열면 안 보일 수 있음)
      }
      handleContentInput();
    })();
    pendingImageUploadsRef.current.push(uploadPromise);
  };

  const handleSubmit = async () => {
    if (isSalesSupport && !supportTitlePart.trim()) return alert('제목을 입력해주세요.');
    if (!form.title) return alert('제목을 입력해주세요.');
    if (!form.visibility) return alert('공개범위를 선택해주세요.');
    // 방금 붙여넣은 이미지의 업로드가 아직 진행 중이면(data-filename이 아직 안 붙었을 수 있음)
    // 끝날 때까지 기다린 뒤, 편집기의 최신 DOM 내용을 기준으로 저장한다(위 pendingImageUploadsRef 설명 참조).
    if (pendingImageUploadsRef.current.length) {
      await Promise.all(pendingImageUploadsRef.current);
      pendingImageUploadsRef.current = [];
    }
    const latestContent = contentRef.current ? contentRef.current.innerHTML : form.content;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, content: latestContent }).forEach(([key, val]) => {
        fd.append(key, val ?? '');
      });
      if (isSalesSupport && salesSupportContext.activityId) {
        fd.append('linked_activity_id', salesSupportContext.activityId);
      }
      files.forEach(f => fd.append('files', f));

      if (editData) await API.put('/tasks/'+editData.id, fd, { headers:{ 'Content-Type':'multipart/form-data' } });
      else          await API.post('/tasks', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
      onSaved(form);
    } catch(err) { alert(err.response?.data?.message || '오류 발생'); }
    finally { setLoading(false); }
  };

  const inp = { width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:8,
    fontSize:14, boxSizing:'border-box', marginBottom:12, outline:'none' };
  const lbl = { fontSize:13, color:'#333', fontWeight:600, display:'block', marginBottom:6 };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:540,
        maxWidth:'92vw', maxHeight:'90vh', overflowY:'auto' }}
        onClick={e=>e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <h3 style={{ fontSize:18, fontWeight:700, margin:0 }}>
              {isSalesSupport ? '🤝 영업지원 업무 등록' : (editData?'업무 수정':'업무 등록')}
            </h3>
            <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:'#555',
              fontWeight:500, cursor:'pointer' }}>
              📅 일정관리 등록
              <input type="checkbox" checked={form.add_to_schedule==='1'}
                onChange={e => setForm({ ...form, add_to_schedule: e.target.checked ? '1' : '0' })}
                style={{ accentColor:'#185FA5' }} />
            </label>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {/* 구분/상태/중요도 */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
          <div>
            <label style={lbl}>구분</label>
            <select name="category" value={form.category} onChange={handleChange} style={inp}>
              {catMeta.fullList.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>상태</label>
            <select name="status" value={form.status} onChange={handleChange} style={inp}>
              {['대기','진행중','완료','보류'].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>중요도</label>
            <select name="priority" value={form.priority} onChange={handleChange} style={inp}>
              {['높음','보통','낮음'].map(p=><option key={p}>{p}</option>)}
            </select>
          </div>
        </div>

        {isSalesSupport ? (
          <>
            <label style={lbl}>제목 * <span style={{ fontSize:11, color:'#aaa', fontWeight:400 }}>(영업지원 ㅣ 영업사 ㅣ 제목 순서로 저장됩니다)</span></label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1.4fr', gap:8, marginBottom:6 }}>
              <input value="영업지원" disabled
                style={{ ...inp, marginBottom:0, background:'#f8fafc', color:'#666' }} />
              <input value={supportCompanyPart} onChange={e => setSupportCompanyPart(e.target.value)}
                placeholder="영업사" style={{ ...inp, marginBottom:0 }} />
              <input value={supportTitlePart} onChange={e => setSupportTitlePart(e.target.value)}
                placeholder="제목을 입력하세요" style={{ ...inp, marginBottom:0 }} />
            </div>
            <div style={{ fontSize:12, color:'#185FA5', marginBottom:12 }}>미리보기: {form.title || '-'}</div>
          </>
        ) : (
          <>
            <label style={lbl}>제목 *</label>
            <input name="title" placeholder="업무 제목" value={form.title} onChange={handleChange} style={inp} />
          </>
        )}

        <label style={lbl}>
          내용 <span style={{ fontSize:11, color:'#aaa', fontWeight:400 }}>(스크린샷을 복사해서 바로 붙여넣기(Ctrl+V) 할 수 있습니다)</span>
        </label>
        <div
          ref={contentRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleContentInput}
          onPaste={handleContentPaste}
          style={{ ...inp, height:90, overflowY:'auto', lineHeight:1.6, cursor:'text' }}
        />

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={lbl}>담당자</label>
            <input name="manager" placeholder="담당자 이름" value={form.manager} onChange={handleChange} style={inp} />
          </div>
          <div>
            <label style={lbl}>협력부서 담당자 <span style={{ fontSize:11, color:'#aaa', fontWeight:400 }}>(선택)</span></label>
            <input name="co_manager" placeholder="비워둘 수 있습니다" value={form.co_manager} onChange={handleChange} style={inp} />
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={lbl}>시작일</label>
            <input name="start_date" type="date" value={form.start_date} onChange={handleChange} style={inp} />
          </div>
          <div>
            <label style={lbl}>완료예정일</label>
            <input name="end_date" type="date" value={form.end_date} onChange={handleChange} style={inp} />
          </div>
        </div>
        {form.add_to_schedule==='1' && (
          <div style={{ fontSize:11, color:'#aaa', marginTop:-8, marginBottom:12 }}>
            ※ 일정관리 등록이 켜져 있으면 완료예정일이 일정관리 캘린더에 자동으로 등록됩니다.
          </div>
        )}

        {/* 공개범위 — 계정관리 - 공개범위 설정에서 만든 공개구분을 그대로 사용(공지사항과 동일) */}
        <label style={lbl}>공개범위</label>
        <select name="visibility" value={form.visibility} onChange={handleChange} style={inp}>
          <option value="">공개범위를 선택하세요</option>
          {scopeOptions.map(s => <option key={s.id} value={s.name}>{formatScopeLabel(s)}</option>)}
        </select>

        {/* 첨부파일 — 여러 개(다중) 첨부 가능 */}
        <label style={lbl}>첨부파일</label>
        {editData && legacyFile.ori && (
          <div style={{ marginBottom:8 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
              fontSize:13, color:'#185FA5', padding:'4px 8px', background:'#EFF6FF', borderRadius:6, marginBottom:4 }}>
              <span>📎 (예전 단일첨부) {legacyFile.ori}</span>
              <span onClick={removeLegacyAttachment}
                style={{ color:'#dc2626', cursor:'pointer', fontWeight:700, marginLeft:8 }}>✕</span>
            </div>
          </div>
        )}
        {existingAttachments.length > 0 && (
          <div style={{ marginBottom:8 }}>
            {existingAttachments.map(att => (
              <div key={att.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                fontSize:13, color:'#185FA5', padding:'4px 8px', background:'#EFF6FF', borderRadius:6, marginBottom:4 }}>
                <span onClick={() => downloadExistingAttachment(att)} style={{ cursor:'pointer' }}>📎 {att.orig_name}</span>
                <span onClick={() => removeExistingAttachment(att)}
                  style={{ color:'#dc2626', cursor:'pointer', fontWeight:700, marginLeft:8 }}>✕</span>
              </div>
            ))}
          </div>
        )}
        <FileDropZone multiple fileNames={files.map(f => f.name)}
          onFiles={fs => setFiles(prev => [...prev, ...fs])} />

        <button onClick={handleSubmit} disabled={loading}
          style={{ width:'100%', padding:'13px', background:loading?'#aaa':'#185FA5', color:'#fff',
            border:'none', borderRadius:10, fontSize:15, fontWeight:700,
            cursor:loading?'not-allowed':'pointer', marginTop:4 }}>
          {loading?'저장 중...':(isSalesSupport?'영업지원 등록':(editData?'수정 완료':'등록 완료'))}
        </button>
      </div>
    </div>
  );
}


// ── 업무요청 작성 모달 ──────────────────────────────
// 업무관리 헤더의 "업무요청" 버튼(독립적인 요청, linkedTask 없음) 또는 업무 상세화면의 "업무요청"
// 버튼(linkedTask 있음 — 저장되면 서버가 그 업무의 히스토리에도 자동으로 함께 남김) 둘 다 사용.
// linkedTask가 없는 경우(헤더에서 독립적으로 연 경우)에는 "업무목록 선택"(구분→업무내용)으로
// 특정 업무를 직접 골라 연결할 수도 있다 — 고르면 linkedTask가 있을 때와 동일하게 그 업무의
// 히스토리에도 자동 기록됨(서버가 linked_task_id 유무로 판단).
// 입력 항목: (업무목록 선택 — linkedTask 없을 때만) / 요청부서 / 담당자(등록된 회원 드롭다운,
// "직급 이름" 형식으로 표시) / 제목 + 완료일 / 요청내용 / 첨부파일.
function TaskRequestModal({ linkedTask, tasks, onClose, onSaved }) {
  const { catMeta } = useTaskCategories();
  const [departments, setDepartments] = useState([]);
  const [userList, setUserList] = useState([]);
  const [form, setForm] = useState({
    request_department:'', request_manager:'', title:'', content:'',
    request_date: getKstDateString(), // 요청일 기본값: 작성 당일(수정 가능)
    due_date:'',
  });
  const [selCat, setSelCat] = useState('');       // 업무목록 선택 - 구분(짧은 이름)
  const [selTaskId, setSelTaskId] = useState('');  // 업무목록 선택 - 업무내용(tasks.id)
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/org/public').then(r => setDepartments(r.data.departments || [])).catch(() => {});
    API.get('/tasks/users/list').then(r => setUserList(r.data || [])).catch(() => {});
  }, []);

  // 요청부서를 고르면(또는 바꾸면), 그 부서 소속이 아닌 담당자를 선택해둔 상태로 남지 않도록
  // 담당자 선택을 초기화한다. 담당자 목록 자체는 아래 managerOptions에서 부서로 걸러서 보여준다.
  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'request_department') {
      setForm(f => ({ ...f, request_department: value, request_manager: '' }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  // 업무목록 선택에서 고를 수 있는 업무내용 — 구분을 골랐으면 그 구분의 업무만, 아니면 전체
  const taskOptions = (tasks || []).filter(t => !selCat || t.category === catMeta.map[selCat]);
  // 담당자 목록 — 요청부서를 골랐으면 그 부서 소속 회원만(계정관리에 등록된 부서 기준), 아니면 전체
  const managerOptions = userList.filter(u => !form.request_department || u.department === form.request_department);

  const handleSubmit = async () => {
    if (!form.title.trim()) return alert('제목을 입력해주세요.');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      const effectiveLinkedId = linkedTask ? linkedTask.id : (selTaskId || '');
      if (effectiveLinkedId) fd.append('linked_task_id', effectiveLinkedId);
      if (file) fd.append('file', file);
      await API.post('/tasks/requests', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved && onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const inp = { width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:8,
    fontSize:14, boxSizing:'border-box', marginBottom:12, outline:'none' };
  const lbl = { fontSize:13, color:'#333', fontWeight:600, display:'block', marginBottom:6 };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:480, maxWidth:'92vw',
        maxHeight:'90vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ fontSize:18, fontWeight:700, margin:0 }}>
            📨 업무요청
            {linkedTask && <span style={{ fontSize:13, fontWeight:500, color:'#888', marginLeft:8 }}>· {linkedTask.title}</span>}
          </h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        {!linkedTask && (
          <>
            <label style={lbl}>업무목록 선택</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
              <select value={selCat} onChange={e => { setSelCat(e.target.value); setSelTaskId(''); }} style={{ ...inp, marginBottom:0 }}>
                <option value="">구분</option>
                {/* 목록에는 "일반/인증/지원..." 같은 짧은 값 대신 실제 업무구분 전체 명칭
                    ("일반업무/인증관리/지원사업...")을 보여준다 — value는 그대로 짧은 키를
                    써서 아래 taskOptions 필터(CAT_MAP[selCat])와 서버 저장값(카테고리)이
                    계속 맞물리게 한다. */}
                {catMeta.shortList.filter(c => c !== '전체').map(c => <option key={c} value={c}>{catMeta.map[c]}</option>)}
              </select>
              <select value={selTaskId} onChange={e => setSelTaskId(e.target.value)} style={{ ...inp, marginBottom:0 }}>
                <option value="">업무내용 선택 안 함</option>
                {taskOptions.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </div>
          </>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={lbl}>요청부서</label>
            <select name="request_department" value={form.request_department} onChange={handleChange} style={inp}>
              <option value="">선택 안 함</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>담당자</label>
            <select name="request_manager" value={form.request_manager} onChange={handleChange} style={inp}>
              <option value="">선택 안 함</option>
              {managerOptions.map(u => <option key={u.name} value={u.name}>{u.position ? `${u.position} ${u.name}` : u.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label style={lbl}>요청일</label>
            <input type="date" name="request_date" value={form.request_date} onChange={handleChange} style={inp} />
          </div>
          <div>
            <label style={lbl}>완료일</label>
            <input type="date" name="due_date" value={form.due_date} onChange={handleChange} style={inp} />
          </div>
        </div>

        <label style={lbl}>제목 *</label>
        <input name="title" placeholder="요청 제목" value={form.title} onChange={handleChange} style={inp} />

        <label style={lbl}>요청내용</label>
        <textarea name="content" placeholder="요청 내용을 입력해주세요" value={form.content} onChange={handleChange}
          style={{ ...inp, height:100, resize:'vertical', fontFamily:'inherit' }} />

        <label style={lbl}>첨부파일</label>
        <FileDropZone fileName={file?.name} onFile={setFile} />

        <button onClick={handleSubmit} disabled={saving}
          style={{ width:'100%', padding:'13px', background:saving?'#aaa':'#0F766E', color:'#fff',
            border:'none', borderRadius:10, fontSize:15, fontWeight:700,
            cursor:saving?'not-allowed':'pointer' }}>
          {saving?'등록 중...':'업무요청 등록'}
        </button>
      </div>
    </div>
  );
}

// 업무요청 진행상태 5단계 — 대기/보류(요청받은 쪽이 아직 대응 작성 중) → 확인요청(요청받은 쪽이
// 대응내용 작성 후 확인요청 처리, 요청한 쪽 처리 대기) → 수정요청(요청한 쪽이 다시 돌려보냄,
// 요청받은 쪽이 재작성) 또는 완료(요청한 쪽이 최종 승인). status 컬럼이 없는 옛 데이터는
// completed_at/confirmed_at 유무로 대략 환산해서 보여준다. 목록/상세에서 공통으로 사용.
function reqStatus(r) {
  return r.status || (r.confirmed_at ? '완료' : r.completed_at ? '확인요청' : '대기');
}
const REQ_STATUS_STYLE = {
  '대기':     { label:'대기',     color:'#854F0B', bg:'#FAEEDA' },
  '보류':     { label:'보류',     color:'#993556', bg:'#FBEAF0' },
  '확인요청': { label:'확인요청(확인대기)', color:'#185FA5', bg:'#E6F1FB' },
  '수정요청': { label:'수정요청', color:'#B45309', bg:'#FEF3C7' },
  '완료':     { label:'완료',     color:'#0F6E56', bg:'#E1F5EE' },
};
function reqStatusInfo(r) {
  return REQ_STATUS_STYLE[reqStatus(r)] || REQ_STATUS_STYLE['대기'];
}
function ReqStatusBadge({ r }) {
  const s = reqStatusInfo(r);
  return (
    <span style={{ fontSize:11, padding:'2px 9px', borderRadius:20, fontWeight:700, background:s.bg, color:s.color, whiteSpace:'nowrap' }}>
      {s.label}
    </span>
  );
}

// ── 업무요청 상세 모달 ──────────────────────────────
// 요청사항(제목/부서/담당자/요청일/완료일/내용/첨부) + 진행상태를 보여주고, 상태에 따라
// 요청받은 쪽의 "요청완료 작성"(대응내용 입력) 또는 요청한 쪽의 "확인" 액션을 제공한다.
// 요청내역(TaskRequestListModal)과 업무별 요청목록(TaskLinkedRequestsModal) 양쪽에서 공용으로 사용.
function TaskRequestDetailModal({ request, onClose, onUpdated, onDelete }) {
  const { hasDetail } = usePermissions();
  const canRequestTask = hasDetail('tasks', 'request_write_allowed');
  const canDownload = hasDetail('tasks', 'download_allowed');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [r, setR] = useState(request);
  const [responseText, setResponseText] = useState(r.response_content || '');
  const [revisionNote, setRevisionNote] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const status = reqStatusInfo(r);
  const curStatus = reqStatus(r);

  const isRequester = user.name === r.created_by;
  const canConfirm = isRequester || hasDetail('tasks', 'request_read_all');
  const canDelete = isRequester || user.role === 'superadmin' || user.role === 'admin';
  const isRecipientTurn = ['대기','보류','수정요청'].includes(curStatus);
  const isRequesterTurn = curStatus === '확인요청';
  const isFinal = curStatus === '완료';

  // 상세보기를 열면(마운트되면) 확인(열람) 기록을 남긴다 — 업무관리 헤더 "업무요청" 버튼의
  // 점등/숫자 배지는 이 기록을 기준으로, 한 번이라도 열어본 요청은(그 이후 새 변화가 없는 한)
  // 더 이상 잡지 않는다. onUpdated로 부모(목록/헤더 카운트)도 함께 갱신해준다.
  useEffect(() => {
    API.patch(`/tasks/requests/${r.id}/seen`).then(() => { onUpdated && onUpdated(); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    if (!window.confirm('이 업무요청을 삭제하시겠습니까? 삭제하면 되돌릴 수 없습니다.')) return;
    setDeleting(true);
    try {
      await API.delete(`/tasks/requests/${r.id}`);
      onDelete && onDelete(r.id);
    } catch (err) {
      alert(err.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const stampNow = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  };

  // 요청받은 쪽: 보류/대기/확인요청 — 대응내용(+첨부파일)을 함께 저장한다.
  const handleRecipientAction = async (targetStatus) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('status', targetStatus);
      fd.append('response_content', responseText);
      if (file) fd.append('file', file);
      await API.patch(`/tasks/requests/${r.id}/status`, fd, { headers: { 'Content-Type':'multipart/form-data' } });
      const patch = { status: targetStatus, response_content: responseText };
      if (file) patch.response_file_ori = file.name;
      if (targetStatus === '확인요청') { patch.completed_by = user.name; patch.completed_at = stampNow(); }
      setR({ ...r, ...patch });
      setFile(null);
      onUpdated && onUpdated();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 요청한 쪽: 수정요청/완료
  const handleRequesterAction = async (targetStatus) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('status', targetStatus);
      if (targetStatus === '수정요청') fd.append('revision_note', revisionNote);
      await API.patch(`/tasks/requests/${r.id}/status`, fd, { headers: { 'Content-Type':'multipart/form-data' } });
      const now = stampNow();
      const patch = targetStatus === '완료'
        ? { status: targetStatus, confirmed_by: user.name, confirmed_at: now }
        : { status: targetStatus, revision_note: revisionNote, revision_by: user.name, revision_at: now };
      setR({ ...r, ...patch });
      setRevisionNote('');
      onUpdated && onUpdated();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 첨부파일은 <a href>로 그냥 열게 두면 브라우저가 파일 종류(이미지/PDF 등)에 따라 새 탭에
  // 미리보기로 열어버릴 수 있다 — blob으로 받아서 임시 <a download> 요소로 클릭시키면 파일
  // 종류와 무관하게 항상 다운로드된다(회의록 엑셀 다운로드 등 이 앱 다른 곳과 동일한 방식).
  const handleDownload = async (fname, displayName) => {
    if (!fname) return;
    try {
      const res = await API.get(`/tasks/requests/download/${fname}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = displayName || fname;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('파일을 다운로드할 수 없습니다.');
    }
  };
  const row = { fontSize:14, color:'#888', marginBottom:4 };

  // 대응사항 히스토리 — 예전에는 response_content 컬럼 하나만 있어서 "수정요청 → 재작성"이
  // 반복되면 이전 대응내용이 사라졌는데, 이제 매 처리(대응/확인요청/수정요청/완료)마다
  // response_history(JSON 배열)에 누적되어 전체 이력을 그대로 볼 수 있다. 예전 데이터라
  // response_history가 비어있으면(이번 개편 이전에 이미 진행된 요청) 기존 방식대로
  // response_content/revision_note 단일 필드로 대체 표시한다.
  let history = [];
  try { history = JSON.parse(r.response_history || '[]'); } catch (e) { history = []; }
  if (history.length === 0) {
    if (r.revision_note) history.push({ type:'수정요청', content:r.revision_note, by:r.revision_by, at:r.revision_at });
    if (r.response_content || r.response_file_ori) {
      history.push({ type: curStatus === '완료' || curStatus === '확인요청' ? '확인요청' : curStatus,
        content:r.response_content, file_name:r.response_file_name, file_ori:r.response_file_ori,
        by:r.completed_by, at:r.completed_at });
    }
  }
  const HIST_LABEL = { '대기':'⏳ 대기', '보류':'⏸ 보류', '확인요청':'✔ 확인요청', '수정요청':'↩ 수정요청', '완료':'✅ 완료' };
  const HIST_COLOR = { '대기':'#854F0B', '보류':'#993556', '확인요청':'#185FA5', '수정요청':'#B45309', '완료':'#0F6E56' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2200 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:26, width:480, maxWidth:'92vw',
        maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <ReqStatusBadge r={r} />
          {/* 삭제 버튼(요청을 등록한 사람 또는 최고관리자만) — 닫기(✕) 버튼 옆에 나란히 배치.
              제목은 아래 별도 줄에 두어, 제목이 길어져 줄바꿈되어도 이 버튼줄에는 영향이 없다. */}
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {canDelete && (
              <button onClick={handleDelete} disabled={deleting} title="삭제"
                style={{ padding:'5px 10px', borderRadius:8, border:'1px solid #dc2626',
                  background:'#fff', color:'#dc2626', fontSize:12, fontWeight:600,
                  cursor: deleting ? 'not-allowed' : 'pointer' }}>
                🗑️ 삭제
              </button>
            )}
            <button onClick={onClose}
              style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>
        <h3 style={{ fontSize:17, fontWeight:700, margin:'0 0 14px' }}>{r.title}</h3>

        {/* 담당부서/담당자(요청을 받는 쪽) · 요청일/완료일 순으로 표기 — 누가 작성했는지(요청자)는
            목록/알림에서 이미 확인 가능하므로 상세 요약줄에서는 생략한다. */}
        <div style={row}>담당부서 <b style={{ color:'#333' }}>{r.request_department || '-'}</b> · 담당자 <b style={{ color:'#333' }}>{r.request_manager || '-'}</b></div>
        <div style={row}>요청일 <b style={{ color:'#333' }}>{r.request_date || (r.created_at||'').slice(0,10)}</b>
          {r.due_date && <> · 완료일 <b style={{ color:'#dc2626' }}>{r.due_date}</b></>}
        </div>

        {r.content && (
          <div style={{ marginTop:10, padding:'10px 12px', background:'#f8fafc', borderRadius:8,
            fontSize:14, color:'#333', whiteSpace:'pre-wrap', lineHeight:1.6 }}>
            {r.content}
          </div>
        )}

        {r.file_ori && (
          canDownload ? (
            <a onClick={() => handleDownload(r.file_name, r.file_ori)}
              style={{ display:'inline-block', marginTop:10, fontSize:13, color:'#185FA5', fontWeight:600,
                textDecoration:'none', cursor:'pointer' }}>
              📎 {r.file_ori}
            </a>
          ) : (
            <span style={{ display:'inline-block', marginTop:10, fontSize:13, color:'#888', fontWeight:600 }}>
              📎 {r.file_ori}
            </span>
          )
        )}

        {/* 대응사항: 지금까지의 대응/확인요청/수정요청/완료 처리 히스토리를 시간순으로 먼저 보여주고,
            지금 내가 처리할 차례가 있으면 그 아래(맨 아래)에 처리 폼을 이어서 보여준다. */}
        <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid #f0f0f0' }}>
          <div style={{ fontSize:14, fontWeight:700, color:'#1a1a1a', marginBottom:8 }}>💬 대응사항</div>

          {history.length > 0 ? (
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:14 }}>
              {history.map((h, i) => (
                <div key={i} style={{ padding:'10px 12px', background:'#f8fafc', borderRadius:8, fontSize:14, color:'#222' }}>
                  <div style={{ fontWeight:700, marginBottom:4, color: HIST_COLOR[h.type] || '#333' }}>
                    {HIST_LABEL[h.type] || h.type}
                  </div>
                  {h.content
                    ? <div style={{ whiteSpace:'pre-wrap', lineHeight:1.6, marginBottom:4 }}>{h.content}</div>
                    : <div style={{ color:'#999', marginBottom:4 }}>작성된 내용이 없습니다.</div>}
                  {h.file_ori && (
                    canDownload ? (
                      <a onClick={() => handleDownload(h.file_name, h.file_ori)}
                        style={{ display:'inline-block', fontSize:13, color:'#185FA5', fontWeight:600,
                          textDecoration:'none', marginBottom:4, cursor:'pointer' }}>
                        📎 {h.file_ori}
                      </a>
                    ) : (
                      <span style={{ display:'inline-block', fontSize:13, color:'#888', fontWeight:600, marginBottom:4 }}>
                        📎 {h.file_ori}
                      </span>
                    )
                  )}
                  {(h.by || h.at) && <div style={{ fontSize:12, color:'#888' }}>{h.by} · {h.at}</div>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize:13, color:'#aaa', marginBottom:14 }}>아직 대응 이력이 없습니다.</div>
          )}

          {isFinal ? null : isRequesterTurn ? (
            canConfirm ? (
              <>
                <textarea value={revisionNote} onChange={e => setRevisionNote(e.target.value)}
                  placeholder="수정요청 시 사유를 입력해주세요 (선택)"
                  style={{ width:'100%', height:60, padding:'10px 12px', border:'1px solid #ddd', borderRadius:8,
                    fontSize:14, boxSizing:'border-box', resize:'vertical', fontFamily:'inherit', marginBottom:8 }} />
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => handleRequesterAction('수정요청')} disabled={saving}
                    style={{ flex:1, padding:'10px', background:saving?'#eee':'#fff', color:'#B45309',
                      border:'1px solid #B45309', borderRadius:8, fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer' }}>
                    ↩ 수정요청
                  </button>
                  <button onClick={() => handleRequesterAction('완료')} disabled={saving}
                    style={{ flex:1, padding:'10px', background:saving?'#aaa':'#0F6E56', color:'#fff',
                      border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer' }}>
                    {saving ? '처리 중...' : '✅ 완료'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ fontSize:13, color:'#aaa' }}>요청자의 확인을 기다리고 있습니다.</div>
            )
          ) : isRecipientTurn && canRequestTask ? (
            <>
              <textarea value={responseText} onChange={e => setResponseText(e.target.value)}
                placeholder="처리 내용을 입력해주세요"
                style={{ width:'100%', height:80, padding:'10px 12px', border:'1px solid #ddd', borderRadius:8,
                  fontSize:14, boxSizing:'border-box', resize:'vertical', fontFamily:'inherit', marginBottom:8 }} />
              <FileDropZone fileName={file?.name} onFile={setFile} style={{ marginBottom:6 }} />
              {r.response_file_ori && !file && (
                <div style={{ fontSize:12.5, color:'#888', marginBottom:8 }}>
                  기존 첨부: <a onClick={() => handleDownload(r.response_file_name, r.response_file_ori)}
                    style={{ color:'#185FA5', cursor:'pointer' }}>{r.response_file_ori}</a>
                </div>
              )}
              <div style={{ display:'flex', gap:8, marginTop:4 }}>
                <button onClick={() => handleRecipientAction('보류')} disabled={saving}
                  style={{ flex:1, padding:'10px', background:saving?'#eee':'#fff', color:'#993556',
                    border:'1px solid #993556', borderRadius:8, fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer' }}>
                  ⏸ 보류
                </button>
                <button onClick={() => handleRecipientAction('대기')} disabled={saving}
                  style={{ flex:1, padding:'10px', background:saving?'#eee':'#fff', color:'#854F0B',
                    border:'1px solid #854F0B', borderRadius:8, fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer' }}>
                  ⏳ 대기
                </button>
                <button onClick={() => handleRecipientAction('확인요청')} disabled={saving}
                  style={{ flex:1, padding:'10px', background:saving?'#aaa':'#185FA5', color:'#fff',
                    border:'none', borderRadius:8, fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer' }}>
                  {saving ? '처리 중...' : '✔ 확인요청'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ fontSize:13, color:'#aaa' }}>아직 처리되지 않았습니다.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 요청내역 모달 ─────────────────────────────
// 업무관리 헤더의 "업무요청" 버튼을 누르면 이제 이 요청내역이 먼저 뜨고(예전엔 등록폼이 먼저
// 떴었음), 새 요청을 등록하려면 상단의 "+ 업무요청" 버튼을 눌러 등록폼을 그 위에 띄운다.
// 기본은 "내가 요청했거나 내가 요청받은" 내역만 보여준다(백엔드가 이름 기준으로 필터링).
// "업무요청 전체 조회" 세부권한이 있는 사용자에게만 전체보기 토글을 보여준다.
// 목록의 각 항목을 클릭하면 TaskRequestDetailModal에서 요청사항/대응사항을 확인·처리할 수 있다.
function TaskRequestListModal({ tasks, userList, receivedCount, openRequestId, onClose, onChanged }) {
  const { hasDetail } = usePermissions();
  const canViewAll = hasDetail('tasks', 'request_read_all');
  const canRequestTask = hasDetail('tasks', 'request_write_allowed');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [viewing, setViewing] = useState(null);     // 클릭한 요청(상세 모달로 열기)
  const [showRegister, setShowRegister] = useState(false); // 상단 "+ 업무요청" 버튼으로 여는 등록폼

  const load = () => {
    setLoading(true);
    API.get('/tasks/requests', { params: showAll ? { all: '1' } : {} })
      .then(r => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [showAll]); // eslint-disable-line

  // 상태바에서 특정 업무요청을 클릭해 이 모달이 열린 경우, 목록이 로드되면 그 요청의 상세를
  // 자동으로 연다. openedRef로 openRequestId값당 한 번만 열어서, 사용자가 상세를 닫은 뒤에도
  // rows가 다시 로드될 때(onUpdated 등)마다 재차 열리지 않게 한다.
  const openedRef = useRef(null);
  useEffect(() => {
    if (!openRequestId || rows.length === 0) return;
    if (openedRef.current === openRequestId) return;
    const found = rows.find(r => String(r.id) === String(openRequestId));
    if (found) {
      setViewing(found);
      openedRef.current = openRequestId;
    }
  }, [openRequestId, rows]);

  // 내가 이 요청을 "받은" 입장이면(request_manager===나), 이미 내가 담당자라는 걸 알고 있으니
  // "담당부서/담당자"를 다시 보여주는 대신 "누가 요청했는지"를 부서/직급과 함께 보여준다.
  // 회원목록(userList)에서 이름으로 부서/직급을 찾아 매칭한다(다른 화면의 담당자 표시와 동일한 방식).
  const findUser = (name) => (userList || []).find(u => u.name === name);

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:640, maxWidth:'94vw',
        maxHeight:'86vh', display:'flex', flexDirection:'column' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:700, margin:0, display:'flex', alignItems:'center', gap:8 }}>
            📋 요청내역
            {receivedCount > 0 && (
              <span style={{ fontSize:12, fontWeight:800, color:'#fff', background:'#dc2626',
                borderRadius:20, padding:'1px 8px' }}>요청받은 건 {receivedCount}</span>
            )}
          </h3>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {canViewAll && (
              <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:'#555', cursor:'pointer' }}>
                <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)}
                  style={{ accentColor:'#0F766E' }} />
                전체보기
              </label>
            )}
            <button onClick={onClose}
              style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        {/* 요청내역 상단의 "+ 업무요청" 버튼 — 등록 권한이 있는 사람에게만 노출 */}
        {canRequestTask && (
          <button onClick={() => setShowRegister(true)}
            style={{ marginBottom:12, padding:'10px', background:'#0F766E', color:'#fff', border:'none',
              borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
            + 업무요청
          </button>
        )}

        {!showAll && (
          <div style={{ fontSize:13, color:'#aaa', marginBottom:12, marginTop:-4 }}>
            내가 요청했거나 요청받은 내역만 표시됩니다.
          </div>
        )}

        <div style={{ overflowY:'auto', flex:1 }}>
          {loading ? (
            <div style={{ fontSize:14, color:'#aaa', textAlign:'center', padding:'30px 0' }}>불러오는 중...</div>
          ) : rows.length === 0 ? (
            <div style={{ fontSize:14, color:'#aaa', textAlign:'center', padding:'30px 0' }}>요청내역이 없습니다.</div>
          ) : (
            rows.map(r => {
              const isReceivedByMe = r.request_manager === user.name;
              const requester = isReceivedByMe ? findUser(r.created_by) : null;
              return (
                <div key={r.id} onClick={() => setViewing(r)}
                  style={{ padding:'12px 14px', background:'#f8fafc', borderRadius:10, marginBottom:8, cursor:'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0f6ff'}
                  onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4, gap:8 }}>
                    <span style={{ fontSize:15, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.title}</span>
                    <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                      <ReqStatusBadge r={r} />
                      <span style={{ fontSize:12, color:'#aaa' }}>{(r.request_date || (r.created_at||'').slice(0,10))}</span>
                    </div>
                  </div>
                  {isReceivedByMe ? (
                    <div style={{ fontSize:13, color:'#888' }}>
                      요청자 : {requester ? `${requester.department || ''} ${requester.position || ''} ${requester.name}`.replace(/\s+/g,' ').trim() : r.created_by}
                      {r.due_date && <span style={{ color:'#dc2626', fontWeight:600 }}> · 완료일 {r.due_date}</span>}
                    </div>
                  ) : (
                    <div style={{ fontSize:13, color:'#888' }}>
                      담당부서 {r.request_department || '-'} · 담당자 {r.request_manager || '-'}
                      {r.due_date && <span style={{ color:'#dc2626', fontWeight:600 }}> · 완료일 {r.due_date}</span>}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {showRegister && (
        <TaskRequestModal tasks={tasks}
          onClose={() => setShowRegister(false)}
          onSaved={() => { setShowRegister(false); load(); onChanged && onChanged(); }} />
      )}
      {viewing && (
        <TaskRequestDetailModal request={viewing} onClose={() => setViewing(null)}
          onUpdated={() => { load(); onChanged && onChanged(); }}
          onDelete={() => { setViewing(null); load(); onChanged && onChanged(); }} />
      )}
    </div>
  );
}

// ── 업무별 요청목록 모달 ────────────────────────────
// 업무 상세화면의 "업무요청" 버튼을 누르면 새 요청 작성 폼으로 바로 가는 대신, 이 업무에 걸린
// 기존 요청 목록이 먼저 보인다("업무요청을 누르면 해당업무에 업무요청된 리스트가 나옴"). 목록의
// 항목을 클릭하면 요청사항/대응사항을 확인·처리할 수 있고, 새 요청을 추가로 보낼 수도 있다.
function TaskLinkedRequestsModal({ linkedTask, onClose, onChanged }) {
  const { hasDetail } = usePermissions();
  const canRequestTask = hasDetail('tasks', 'request_write_allowed');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    setLoading(true);
    API.get('/tasks/requests', { params: { linked_task_id: linkedTask.id } })
      .then(r => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:560, maxWidth:'94vw',
        maxHeight:'86vh', display:'flex', flexDirection:'column' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <h3 style={{ fontSize:17, fontWeight:700, margin:0 }}>📨 업무요청</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <div style={{ fontSize:12, color:'#888', marginBottom:14 }}>· {linkedTask.title}</div>

        {canRequestTask && (
          <button onClick={() => setShowCreate(true)}
            style={{ marginBottom:12, padding:'9px', background:'#0F766E', color:'#fff', border:'none',
              borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer' }}>
            + 새 업무요청
          </button>
        )}

        <div style={{ overflowY:'auto', flex:1 }}>
          {loading ? (
            <div style={{ fontSize:13, color:'#aaa', textAlign:'center', padding:'30px 0' }}>불러오는 중...</div>
          ) : rows.length === 0 ? (
            <div style={{ fontSize:13, color:'#aaa', textAlign:'center', padding:'30px 0' }}>이 업무에 연결된 요청이 없습니다.</div>
          ) : (
            rows.map(r => (
              <div key={r.id} onClick={() => setViewing(r)}
                style={{ padding:'11px 14px', background:'#f8fafc', borderRadius:10, marginBottom:8, cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f0f6ff'}
                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:13.5, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.title}</span>
                  <ReqStatusBadge r={r} />
                </div>
                <div style={{ fontSize:11.5, color:'#888', marginTop:3 }}>
                  담당자 {r.request_manager || '-'} · 요청자 {r.created_by}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showCreate && (
        <TaskRequestModal linkedTask={linkedTask}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); onChanged && onChanged(); }} />
      )}
      {viewing && (
        <TaskRequestDetailModal request={viewing} onClose={() => setViewing(null)}
          onUpdated={() => { load(); onChanged && onChanged(); }}
          onDelete={() => { setViewing(null); load(); onChanged && onChanged(); }} />
      )}
    </div>
  );
}

// ── 검색 모달 ──────────────────────────────────
function TaskSearchModal({ initialValue, onClose, onSearch }) {
  const [value, setValue] = useState(initialValue || '');
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:420, maxWidth:'92vw' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:16, fontWeight:700, margin:0 }}>🔍 업무 검색</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <input autoFocus value={value} onChange={e => setValue(e.target.value)}
          onKeyDown={e => e.key==='Enter' && onSearch(value)}
          placeholder="제목, 담당자, 내용 검색..."
          style={{ width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:8,
            fontSize:14, boxSizing:'border-box', marginBottom:16, outline:'none' }} />
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => onSearch(value)}
            style={{ flex:1, padding:'10px', background:'#185FA5', color:'#fff',
              border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
            검색하기
          </button>
          <button onClick={onClose}
            style={{ flex:1, padding:'10px', background:'#f0f0f0', border:'none',
              borderRadius:8, fontSize:14, cursor:'pointer', color:'#555' }}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 회의록 읽기전용 보기 모달 ─────────────────────
// - 업무 상세보기의 회의록 목록에서 항목을 클릭하면 먼저 이 화면이 열림 (바로 수정화면으로 가지 않음)
// - 실제 저장/인쇄되는 엑셀 회의록 서식과 비슷한 모양으로 미리보기를 보여줌
// - 상단 버튼: 수정 / 삭제 / 인쇄 / 내보내기
function MeetingMinuteViewModal({ minute: m, userList, onClose, onEdit, onDeleted }) {
  const { hasDetail } = usePermissions();
  const canDownloadMinutes = hasDetail('tasks', 'download_allowed');
  const [deleting, setDeleting] = useState(false);
  // 회의 내용(이미지 포함 HTML) 안의 <img data-filename="..."> 는 저장 당시 브라우저의 임시
  // blob: URL이 src로 박제되어 있어(다른 세션/새로고침 후에는 이미 죽은 주소), 화면에 붙는
  // 시점마다 매번 인증을 거쳐 실제 파일을 새로 받아 src를 바꿔치기 해야 한다.
  // 이전에는 "콜백 ref + 1회성 플래그(contentHydratedRef)" 방식이었는데, 이 방식은 모달이
  // 언마운트되지 않고 다른 회의록으로 내용만(minute prop) 바뀌는 경우(예: 목록에서 다른 행을
  // 연달아 클릭) DOM 노드가 재사용되어 콜백 ref가 다시 호출되지 않아 새 내용의 이미지 교체가
  // 통째로 스킵되는 버그가 있었다 — "내보내기(PDF)는 되는데 미리보기에서는 안 보인다"는
  // 증상이 바로 이것이다(PDF는 매번 서버가 파일을 직접 읽어 새로 만들기 때문에 이 문제가 없음).
  // useEffect(dep: 회의록 id)로 바꿔서, 같은 모달 인스턴스가 재사용되어도 보고 있는 회의록이
  // 바뀔 때마다 항상 다시 교체되도록 한다.
  const contentPreviewRef = useRef(null);
  useEffect(() => {
    const node = contentPreviewRef.current;
    if (!node) return;
    // 2026-09: 예전에 작성된(크기 조절 래퍼 도입 이전) 회의록은 <img>에 폭 제한 스타일이
    // 전혀 없는 경우가 있어, 원본 해상도 그대로 삽입되면 모달 폭(860px)을 넘어 미리보기
    // 영역 밖으로 잘려 나가거나 스크롤 없이는 아예 안 보이는 것처럼 보였다. data-filename
    // 유무와 무관하게 모든 <img>에 항상 폭 제한을 강제하고, 로드 실패 시에도 빈 자리만
    // 남지 않도록 대체 문구를 보여준다.
    const allImgs = Array.from(node.querySelectorAll('img'));
    const applyBrokenFallback = (img) => {
      img.alt = '(이미지를 불러올 수 없습니다)';
      img.style.border = '1px dashed #ccc';
      img.style.minHeight = '40px';
    };
    allImgs.forEach((img) => {
      img.style.maxWidth = '100%';
      img.style.height = img.closest('.mm-img-resize-wrap') ? '100%' : (img.style.height || 'auto');
      img.onerror = () => applyBrokenFallback(img);
      // innerHTML이 이 useEffect보다 먼저(브라우저 파싱 시점에) 반영되므로, 죽은 blob: 주소를
      // 가진 <img>는 onerror 핸들러를 달기도 전에 이미 로드에 실패해 있는 경우가 많다 —
      // 그 경우 error 이벤트가 다시 발생하지 않아 onerror가 영영 호출되지 않고, 화면에는
      // 브라우저 기본 "깨진 이미지" 아이콘만 남는다(대체 문구 미적용). 지금 이미 깨진
      // 상태(complete=true인데 naturalWidth=0)인지 직접 확인해서 즉시 대체 문구를 적용한다.
      if (img.src && img.complete && img.naturalWidth === 0) applyBrokenFallback(img);
    });
    // 보기 전용 화면에서는 편집기에서만 필요한 크기조절 손잡이(드래그 리사이즈)가 보이지 않게 한다.
    Array.from(node.querySelectorAll('.mm-img-resize-wrap')).forEach(w => { w.style.resize = 'none'; w.style.maxWidth = '100%'; });

    const imgs = allImgs.filter(img => img.getAttribute('data-filename'));
    imgs.forEach(async (img) => {
      const filename = img.getAttribute('data-filename');
      if (!filename) return;
      try {
        const res = await API.get('/meeting-minutes/image/'+filename, { responseType:'blob' });
        // 위에서 이미 "깨진 상태"로 표시된 이미지도, 서버에서 실제 파일을 받아오는 데
        // 성공하면 대체 문구/점선 테두리를 지우고 정상 표시로 되돌린다.
        img.onload = () => { img.alt = ''; img.style.border = ''; img.style.minHeight = ''; };
        img.src = window.URL.createObjectURL(res.data);
      } catch (e) { /* 서버에도 파일이 없는 경우 — 위에서 적용한 대체 문구를 그대로 둔다 */ }
    });
  }, [m.id, m.content]);

  const printedAt = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  })();
  // 출력자는 회의록 작성자가 아니라, 지금 이 화면을 보고 있는(=인쇄/내보내기를 누르는) 로그인 계정 기준으로 표시
  const printUser = JSON.parse(sessionStorage.getItem('user') || '{}');
  const printedBy = formatPrintedBy(printUser); // 출력자: 부서 직급 이름 (중복값은 한 번만 표시)

  // 회의일시를 "YYYY년 MM월 DD일 HH:MM ~ HH:MM" 형태로 합쳐서 표시 (시간이 없으면 날짜만)
  const meetingDateTimeLabel = (() => {
    if (!m.meeting_date) return '-';
    const dm = String(m.meeting_date).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const dateLabel = dm ? `${dm[1]}년 ${dm[2]}월 ${dm[3]}일` : m.meeting_date;
    const timeLabel = m.meeting_start_time && m.meeting_end_time
      ? `${m.meeting_start_time} ~ ${m.meeting_end_time}`
      : (m.meeting_start_time || m.meeting_end_time || '');
    return timeLabel ? `${dateLabel}  ${timeLabel}` : dateLabel;
  })();

  const externalAttendees = (() => {
    if (!m.attendees_external) return [];
    try {
      const arr = JSON.parse(m.attendees_external);
      return Array.isArray(arr) ? arr.filter(a => a && ((a.name||'').trim() || (a.company||'').trim() || (a.position||'').trim())) : [];
    } catch { return []; }
  })();
  const internalNames = String(m.attendees || '').split(/[,\n]/).map(s => s.trim()).filter(Boolean);
  const totalAttendees = internalNames.length + externalAttendees.length;

  // 사내 참석자는 이름만 입력하지만, 회원목록에서 이름이 일치하는 사용자를 찾아 소속(부서명)/직급을 자동으로 채움
  const lookupInternalAttendee = (name) => {
    const u = (userList||[]).find(x => normName(x.name) === normName(name));
    return { name, company:(u && u.department) || '', position:(u && u.position) || '' };
  };

  // 참석자 확인란 미리보기용 통합 목록(사내는 회원목록 매칭, 사외는 소속/직급/성명 모두 직접입력값 표시)
  const attendeeRows = [
    ...internalNames.map(lookupInternalAttendee),
    ...externalAttendees.map(a => ({ name:a.name||'', company:a.company||'', position:a.position||'' })),
  ];
  const slotsG1 = attendeeRows.slice(0, 4);
  const slotsG2 = attendeeRows.slice(4, 8);
  while (slotsG1.length < 4) slotsG1.push({ name:'', company:'', position:'' });
  while (slotsG2.length < 4) slotsG2.push({ name:'', company:'', position:'' });

  const handleDelete = async () => {
    if (!window.confirm('이 회의록을 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.')) return;
    setDeleting(true);
    try {
      await API.delete('/meeting-minutes/'+m.id);
      onDeleted && onDeleted();
    } catch (err) {
      alert(err.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    if (!m.file_path) { alert('아직 저장된 엑셀 파일이 없습니다. 수정 후 "저장하기"를 눌러주세요.'); return; }
    try {
      const res = await API.get(`/meeting-minutes/${m.id}/download`, { responseType: 'blob' });
      // 서버가 PDF 변환에 성공하면 PDF를, 변환 도구가 없어 엑셀 원본으로 대체했으면 xlsx를 내려줌
      const isPdf = (res.headers['content-type'] || '').includes('pdf');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const exportFileName = `${(m.title||'회의록').replace(/[\\/:*?"<>|]/g,'_')}.${isPdf ? 'pdf' : 'xlsx'}`;
      a.download = exportFileName;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      logDownload('회의록', exportFileName);
    } catch (err) {
      alert('파일을 다운로드할 수 없습니다. 저장 여부를 확인해주세요.');
    }
  };

  const handlePrint = async () => {
    if (!m.file_path) { alert('아직 저장된 엑셀 파일이 없습니다. 수정 후 "저장하기"를 눌러주세요.'); return; }
    logPrint('회의록', m.title || '회의록');
    try {
      const res = await API.get(`/meeting-minutes/${m.id}/print`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
      iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = 'none';
      iframe.src = blobUrl;
      // iframe의 onload는 PDF뷰어(플러그인) 껍데기가 뜨는 시점일 뿐, 실제 페이지 크기에 맞춰
      // 확대/축소(fit-to-page)를 계산하는 내부 렌더링은 그 뒤에 비동기로 이어진다. onload되자마자
      // 바로 print()를 부르면 그 계산이 끝나기 전 상태(축소되기 전/덜 채워진 상태)가 그대로
      // 인쇄 미리보기에 잡혀버려서, 실제 저장된 PDF 파일은 정상인데 인쇄창 미리보기만 오른쪽에
      // 여백이 남아 보이는 원인이 된다. 약간의 지연을 줘서 PDF뷰어가 완전히 자리잡은 뒤 인쇄를
      // 호출한다.
      iframe.onload = () => { setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} }, 600); };
      document.body.appendChild(iframe);
    } catch (err) {
      alert('서버에 PDF 변환 도구가 설치되어 있지 않아 바로 인쇄할 수 없습니다. 대신 엑셀 파일을 다운로드합니다.');
      handleExport();
    }
  };

  const btn = { fontSize:12, padding:'6px 12px', borderRadius:6, fontWeight:600, cursor:'pointer' };
  // 두번째 첨부 서식 사진과 동일하게: 배경 음영 없이(흰 배경) 검정 계열 얇은 테두리만으로 표를 구성
  const cellLbl = { padding:'8px 10px', fontWeight:700, fontSize:12, color:'#000', border:'1px solid #000', whiteSpace:'nowrap', width:78, textAlign:'center', background:'#fff' };
  const cellVal = { padding:'8px 10px', fontSize:13, border:'1px solid #000', color:'#222', background:'#fff' };
  const thStyle = { padding:'6px 4px', fontSize:11, fontWeight:700, textAlign:'center', border:'1px solid #000', background:'#fff' };
  const tdStyle = { padding:'0 4px', height:25, fontSize:12, textAlign:'center', border:'1px solid #000', background:'#fff', boxSizing:'border-box' };
  const approvalLbl = { padding:'2px 6px', width:15, fontSize:12, fontWeight:700, textAlign:'center', border:'1px solid #000', background:'#fff', lineHeight:1.3 };
  const approvalHdr = { padding:'4px 12px', width:60, fontSize:12, fontWeight:700, textAlign:'center', border:'1px solid #000', background:'#fff', whiteSpace:'nowrap' };
  const approvalVal = { padding:'4px 10px', height:45, minWidth:56, border:'1px solid #000', background:'#fff' };
  const sectionBar = { padding:'7px 10px', fontWeight:700, fontSize:12, background:'#fff',
    borderTop:'1px solid #000', borderBottom:'1px solid #000', textAlign:'center' };

  // 작성자는 위 칸에 부서, 아래 칸에 "직급 이름"으로 표시(회원목록에서 작성자 이름으로 매칭 — 매칭 안되면 공란)
  const authorUser = (userList||[]).find(u => normName(u.name) === normName(m.created_by));
  const authorDepartment = (authorUser && authorUser.department) || '';
  const authorPosition = (authorUser && authorUser.position) || '';
  const authorName = m.created_by || '-';
  const authorPosName = authorPosition ? `${authorPosition} ${authorName}` : authorName;

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2050 }}>
      <div style={{ background:'#fff', borderRadius:12, width:860, maxWidth:'96vw', maxHeight:'90vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        {/* 상단 버튼 */}
        <div style={{ padding:'14px 20px', borderBottom:'1px solid #f0f0f0',
          display:'flex', justifyContent:'flex-end', alignItems:'center' }}>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            <button onClick={onEdit}
              style={{ ...btn, background:'#E6F1FB', color:'#185FA5', border:'1px solid #185FA5' }}>✏️ 수정</button>
            <button onClick={handleDelete} disabled={deleting}
              style={{ ...btn, background:'#fee2e2', color:'#dc2626', border:'1px solid #dc2626',
                cursor: deleting?'not-allowed':'pointer' }}>🗑 삭제</button>
            {canDownloadMinutes && (
              <button onClick={handleExport}
                style={{ ...btn, background:'#16a34a', color:'#fff', border:'none' }}>📥 내보내기</button>
            )}
            <button onClick={handlePrint}
              style={{ ...btn, background:'#475569', color:'#fff', border:'none' }}>🖨️ 인쇄</button>
            <button onClick={onClose}
              style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999', marginLeft:4 }}>✕</button>
          </div>
        </div>

        {/* 엑셀 서식 스타일 미리보기 (두번째 첨부 서식 사진과 동일하게: 배경 음영 없이 검정 얇은 테두리로만 구성) */}
        <div style={{ padding:24 }}>
          <div style={{ border:'2px solid #000' }}>
            {/* 제목 + 결재란 */}
            <div style={{ display:'flex', borderBottom:'2px solid #000' }}>
              <div style={{ flex:1, padding:'16px 0', textAlign:'center', fontSize:24, fontWeight:800,
                letterSpacing:10, background:'#fff' }}>
                회 의 록
              </div>
              <table style={{ borderCollapse:'collapse', borderLeft:'2px solid #000' }}>
                <tbody>
                  <tr>
                    <td rowSpan={2} style={approvalLbl}><div>결</div><div>재</div></td>
                    <td style={approvalHdr}>담당</td>
                    <td style={approvalHdr}>검토</td>
                    <td style={approvalHdr}>승인</td>
                  </tr>
                  <tr>
                    <td style={approvalVal}></td>
                    <td style={approvalVal}></td>
                    <td style={approvalVal}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <tbody>
                <tr>
                  <td style={cellLbl}>문서번호</td>
                  <td style={{ ...cellVal, width:190, textAlign:'center' }}>{m.document_no || '-'}</td>
                  <td style={{ ...cellLbl, width:80 }}>작성일</td>
                  <td style={{ ...cellVal, width:90, textAlign:'center' }}>{(m.created_at||'').slice(0,10) || '-'}</td>
                  <td rowSpan={2} style={{ ...cellLbl, width:93 }}>작성자</td>
                  <td style={{ ...cellVal, width:153, textAlign:'center' }}>{authorDepartment}</td>
                </tr>
                <tr>
                  <td style={cellLbl}>회의일시</td>
                  <td style={{ ...cellVal, textAlign:'center' }} colSpan={3}>{meetingDateTimeLabel}</td>
                  <td style={{ ...cellVal, width:153, textAlign:'center' }}>{authorPosName}</td>
                </tr>
                <tr>
                  <td style={cellLbl}>회의장소</td>
                  <td style={{ ...cellVal, textAlign:'center' }} colSpan={3}>{m.location || '-'}</td>
                  <td style={{ ...cellLbl, width:93 }}>참석인원</td>
                  <td style={{ ...cellVal, textAlign:'center' }}>{totalAttendees}명</td>
                </tr>
                <tr>
                  <td style={cellLbl}>회의안건</td>
                  <td style={cellVal} colSpan={5}>{m.title || '-'}</td>
                </tr>
              </tbody>
            </table>

            <div style={sectionBar}>회 의 내 용</div>
            <div style={{ minHeight:340 }}>
              {/<[a-z][\s\S]*>/i.test(m.content || '') ? (
                <div ref={contentPreviewRef} className="mm-content"
                  style={{ padding:'14px 16px', fontSize:13, lineHeight:'28px',
                    wordBreak:'break-word', overflowWrap:'anywhere', overflowX:'auto' }}
                  dangerouslySetInnerHTML={{ __html: m.content || '내용 없음' }} />
              ) : (
                <div ref={contentPreviewRef} className="mm-content"
                  style={{ padding:'14px 16px', fontSize:13, lineHeight:'28px', whiteSpace:'pre-wrap',
                    wordBreak:'break-word', overflowWrap:'anywhere' }}>
                  {m.content || '내용 없음'}
                </div>
              )}
            </div>

            <div style={sectionBar}>참 석 자 확 인</div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <tbody>
                <tr>
                  <td rowSpan={5} style={{ ...approvalLbl, width:56 }}><div>참석자</div><div>확인</div></td>
                  <th style={thStyle}>소속</th><th style={{...thStyle, width:70}}>직급</th><th style={{...thStyle, width:80}}>성명</th><th style={{...thStyle, width:80}}>서명</th>
                  <th style={thStyle}>소속</th><th style={{...thStyle, width:70}}>직급</th><th style={{...thStyle, width:80}}>성명</th><th style={{...thStyle, width:80}}>서명</th>
                </tr>
                {[0,1,2,3].map(i => (
                  <tr key={i}>
                    <td style={tdStyle}>{slotsG1[i].company}</td>
                    <td style={{ ...tdStyle, width:70 }}>{slotsG1[i].position}</td>
                    <td style={{ ...tdStyle, width:80 }}>{slotsG1[i].name}</td>
                    <td style={{ ...tdStyle, width:80 }}></td>
                    <td style={tdStyle}>{slotsG2[i].company}</td>
                    <td style={{ ...tdStyle, width:70 }}>{slotsG2[i].position}</td>
                    <td style={{ ...tdStyle, width:80 }}>{slotsG2[i].name}</td>
                    <td style={{ ...tdStyle, width:80 }}></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalAttendees > 8 && (
            <div style={{ fontSize:11, color:'#aaa', marginTop:6 }}>
              ※ 참석자 확인란은 최대 8명까지 표시됩니다. (전체 참석자 {totalAttendees}명)
            </div>
          )}
          {/* 실제 출력물 하단과 동일한 형태의 안내용 표시(진짜 출력일시/출력자는 인쇄 시점에 반영됨) */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
            marginTop:10, padding:'8px 12px', borderTop:'1px solid #ccc', fontSize:11, color:'#666' }}>
            <span>출력일시: {printedAt}</span>
            <span>1/1</span>
            <span>출력자: {printedBy}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 회의록 공개범위 선택 모달 (전체공개/부서공개/참조공개) ──────
function MeetingVisibilityPickerModal({ visibility, visibleDepartments, visibleUsers, deptList, userList, onClose, onConfirm }) {
  const [v, setV]         = useState(visibility);
  const [depts, setDepts] = useState(visibleDepartments || []);
  const [users, setUsers] = useState(visibleUsers || []);

  const toggleDept = (name) => setDepts(prev => prev.includes(name) ? prev.filter(d=>d!==name) : [...prev, name]);
  const toggleUser = (username) => setUsers(prev => prev.includes(username) ? prev.filter(u=>u!==username) : [...prev, username]);

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2500 }}>
      <div style={{ background:'#fff', borderRadius:16, width:440, maxWidth:'92vw', maxHeight:'86vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding:'20px 24px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontSize:16, fontWeight:700, margin:0 }}>🔐 공개범위 설정</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ padding:'16px 24px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:10 }}>
            {MEETING_VIS_LIST.map(opt => (
              <button key={opt.val} type="button" onClick={() => setV(opt.val)}
                style={{ padding:'10px 6px', border: v===opt.val?`2px solid ${opt.color}`:'1.5px solid #ddd',
                  borderRadius:8, cursor:'pointer', textAlign:'center', background: v===opt.val?opt.bg:'#fff' }}>
                <div style={{ fontSize:12, fontWeight:600, color: v===opt.val?opt.color:'#555' }}>{opt.label}</div>
              </button>
            ))}
          </div>
          <div style={{ fontSize:11, color:'#aaa', marginBottom:14 }}>
            {MEETING_VIS_LIST.find(o => o.val===v)?.desc}
          </div>

          {v === '부서공개' && (
            <div>
              <div style={{ fontSize:12, color:'#888', marginBottom:8 }}>
                📌 임원 등급과 최고관리자는 항상 볼 수 있습니다. 추가로 공개할 부서를 선택하세요.
              </div>
              {deptList.length === 0 ? (
                <div style={{ fontSize:12, color:'#aaa' }}>등록된 부서가 없습니다. (계정관리 &gt; 조직구성에서 추가)</div>
              ) : (
                <div style={{ border:'1px solid #eee', borderRadius:8, padding:'6px 12px', maxHeight:180, overflowY:'auto' }}>
                  {deptList.map(d => (
                    <label key={d.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', fontSize:14, cursor:'pointer' }}>
                      <input type="checkbox" checked={depts.includes(d.name)} onChange={() => toggleDept(d.name)}
                        style={{ width:16, height:16, cursor:'pointer' }} />
                      {d.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {v === '참조공개' && (
            <div>
              <div style={{ fontSize:12, color:'#888', marginBottom:8 }}>공개할 사람을 선택하세요.</div>
              {(userList||[]).length === 0 ? (
                <div style={{ fontSize:12, color:'#aaa' }}>불러올 회원 목록이 없습니다.</div>
              ) : (
                <div style={{ border:'1px solid #eee', borderRadius:8, padding:'6px 12px', maxHeight:220, overflowY:'auto' }}>
                  {(userList||[]).map(u => (
                    <label key={u.username} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', fontSize:14, cursor:'pointer' }}>
                      <input type="checkbox" checked={users.includes(u.username)} onChange={() => toggleUser(u.username)}
                        style={{ width:16, height:16, cursor:'pointer' }} />
                      {u.position ? `${u.position} ${u.name}` : u.name}
                      <span style={{ color:'#aaa', fontSize:12 }}>({u.username})</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding:'0 24px 20px', display:'flex', gap:8 }}>
          <button onClick={() => onConfirm(v, v==='부서공개'?depts:[], v==='참조공개'?users:[])}
            style={{ flex:1, padding:'11px', background:'#185FA5', color:'#fff', border:'none',
              borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            확인
          </button>
          <button onClick={onClose}
            style={{ flex:1, padding:'11px', background:'#f0f0f0', color:'#555', border:'none',
              borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

// 24시간 전용 커스텀 시간 입력기 (일정관리와 동일한 컴포넌트)
// — 네이티브 <input type="time">은 OS/브라우저 지역설정에 따라 오전/오후로 표시되고
//   이를 완전히 없앨 방법이 없어(지역설정 강제 시도 모두 실패), 겉모양만 기존 박스+시계아이콘
//   형태를 흉내낸 커스텀 컴포넌트로 대체합니다. 오전/오후 개념 자체가 코드에 없으므로
//   "13"을 입력하면 그대로 13시로 저장됩니다. (일정관리 Schedules.js의 TimeField24와 동일 로직)
function TimeField24({ value, onChange, style }) {
  const [hh, mm] = String(value || '').split(':');
  const hourRef = useRef(null);
  const minRef = useRef(null);
  // 버그 수정: 시(hour) 칸이 꽉 차 분(minute) 칸으로 자동 focus 이동할 때, 이 focus() 호출이
  // hour input의 blur를 동기적으로 즉시 발생시킨다. 이때 blur 핸들러가 렌더링 시점에
  // 구조분해된(오래된) hh 값을 참조하면 방금 입력한 값을 덮어써버리므로, blur 핸들러에서는
  // 항상 e.target.value(현재 DOM에 실제로 찍혀있는 값)를 직접 읽어 이 경쟁 상태를 없앤다.
  const handleHour = (e) => {
    let v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    if (v.length === 2 && parseInt(v, 10) > 23) v = '23';
    onChange(`${v}:${mm || ''}`);
    if (v.length === 2) minRef.current && minRef.current.focus();
  };
  const handleMinute = (e) => {
    let v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    if (v.length === 2 && parseInt(v, 10) > 59) v = '59';
    onChange(`${hh || ''}:${v}`);
  };
  const handleBlurHour = (e) => {
    const v = e.target.value;
    if (v) onChange(`${v.padStart(2, '0')}:${mm || ''}`);
  };
  const handleBlurMinute = (e) => {
    const v = e.target.value;
    if (v) onChange(`${hh || ''}:${v.padStart(2, '0')}`);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '6px 8px',
      border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box', ...style }}>
      <input ref={hourRef} value={hh || ''} onChange={handleHour} onBlur={handleBlurHour}
        placeholder="00" maxLength={2} inputMode="numeric"
        style={{ width: 16, border: 'none', outline: 'none', textAlign: 'center', fontSize: 13, padding: 0 }} />
      <span style={{ fontSize: 13, color: '#555' }}>:</span>
      <input ref={minRef} value={mm || ''} onChange={handleMinute} onBlur={handleBlurMinute}
        placeholder="00" maxLength={2} inputMode="numeric"
        style={{ width: 16, border: 'none', outline: 'none', textAlign: 'center', fontSize: 13, padding: 0 }} />
      <span style={{ marginLeft: 4, color: '#888', fontSize: 13 }}>🕐</span>
    </div>
  );
}

// ── 회의록 작성/수정 모달 ──────────────────────────
// - 업무관리 헤더의 "📝 회의록" 버튼, 또는 업무 상세보기 내 "+ 새 회의록"/기존 회의록 클릭으로 열림
// - "해당업무" 버튼으로 등록된 업무를 구분+업무내용으로 선택해 연결할 수 있음(연결 시 업무 상세보기에도 노출)
function MeetingMinutesModal({ tasks, userList, editData, presetTask, onClose, onSaved, onTasksChanged }) {
  const { catMeta } = useTaskCategories();
  const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
  const authorName = editData?.created_by || currentUser.name || '-'; // 수정 시엔 최초 작성자를 그대로 표시(엑셀도 동일하게 유지됨)
  const todayDisplay = getKstDateString(); // 작성일(엑셀 생성 시점 기준으로 항상 오늘 날짜가 반영됨)

  const [id, setId] = useState(editData?.id || null);
  const [form, setForm] = useState({
    title:              editData?.title              || '',
    meeting_date:       editData?.meeting_date        || getKstDateString(),
    meeting_start_time: editData?.meeting_start_time  || '',
    meeting_end_time:   editData?.meeting_end_time    || '',
    location:           editData?.location            || '',
    attendees:          editData?.attendees           || '',  // 사내 참석자 (이름, 쉼표로 구분)
    content:            editData?.content             || '',
    // 📅 일정관리 등록: 신규 작성은 기본 체크해제, 수정은 저장된 값을 그대로 따름
    add_to_schedule:    (editData?.add_to_schedule ?? 0) ? '1' : '0',
  });
  // 사외 참석자: [{ company, position, name }, ...]
  const [externalAttendees, setExternalAttendees] = useState(() => {
    if (!editData?.attendees_external) return [];
    try {
      const arr = JSON.parse(editData.attendees_external);
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  });
  const [linkedTask, setLinkedTask] = useState(
    editData
      ? (editData.task_id ? { id: editData.task_id, category: editData.task_category, title: editData.task_title } : null)
      : (presetTask || null)
  );
  const [meetingType, setMeetingType] = useState(editData?.meeting_type || null); // 주간회의/경영회의/일반회의 (해당 시)
  const [savedFilePath, setSavedFilePath] = useState(editData?.file_path || null); // 저장하기(완료) 시 생성된 엑셀 경로
  // 저장/임시저장을 한 번이라도 성공했으면, 그 뒤에 창을 닫을 때 "저장되지 않았습니다" 경고를 띄우지 않음
  const [savedOnce, setSavedOnce] = useState(false);
  // 공개범위: 계정관리 - 공개범위 설정에서 만든 공개구분 목록을 그대로 사용(업무관리/공지사항과 동일)
  const [visibility, setVisibility] = useState(editData?.visibility || '');
  const [scopeOptions, setScopeOptions] = useState([]);
  useEffect(() => {
    API.get('/disclosure/list').then(r => {
      const scopes = r.data.scopes || [];
      setScopeOptions(scopes);
      if (!editData) {
        const def = scopes.find(s => s.is_default);
        if (def) setVisibility(v => v || def.name);
      }
    }).catch(() => {});
  }, []);
  // 사외 참석자와 마찬가지로 예전 방식(부서공개/참조공개)으로 저장된 기존 회의록의 대상 정보는
  // 새로 만든 화면에서는 편집하지 않지만, 값 자체는 그대로 보존해서 함께 저장합니다.
  const [visibleDepartments, setVisibleDepartments] = useState(() => {
    try { const arr = JSON.parse(editData?.visible_departments || '[]'); return Array.isArray(arr) ? arr : []; }
    catch { return []; }
  });
  const [visibleUsers, setVisibleUsers] = useState(() => {
    try { const arr = JSON.parse(editData?.visible_users || '[]'); return Array.isArray(arr) ? arr : []; }
    catch { return []; }
  });
  const [scheduleId, setScheduleId] = useState(editData?.schedule_id || null); // 저장하기(완료) 시 일정관리에 자동등록된 일정 id
  const [pickerCategory, setPickerCategory] = useState('');
  const [pickerTaskId, setPickerTaskId]     = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  // 임시저장 목록
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);

  // 열릴 때 임시저장 목록을 미리 조회 — 목록이 있을 때만 "임시저장 목록" 버튼이 활성화되도록
  useEffect(() => {
    API.get('/meeting-minutes/drafts').then(r => setDrafts(r.data)).catch(() => setDrafts([]));
  }, []);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // 사외 참석자 행 추가/삭제/수정
  const addExternalRow = () => setExternalAttendees(list => [...list, { company:'', position:'', name:'' }]);
  const removeExternalRow = (idx) => setExternalAttendees(list => list.filter((_,i)=>i!==idx));
  const updateExternalRow = (idx, field, value) =>
    setExternalAttendees(list => list.map((row,i) => i===idx ? { ...row, [field]: value } : row));

  // 회의 내용 편집기 (이미지 붙여넣기 지원) — form.content 에는 HTML이 저장됨(이미지 태그 포함 가능)
  const contentRef = useRef(null);
  const contentLoadedRef = useRef(false);
  // 클립보드로 붙여넣은 이미지의 서버 업로드가 아직 끝나지 않은 채로 저장/임시저장 버튼을
  // 눌러버리면, 방금 붙인 <img>에 data-filename이 붙기 전 상태(죽은 blob: 주소만 있는 상태)로
  // 그대로 저장되어, 나중에 미리보기/인쇄에서 이미지가 깨진 아이콘으로 보이는 경쟁조건이
  // 있었다. 붙여넣기마다 만들어지는 업로드 Promise를 여기 모아뒀다가, 저장 직전(doSave)에
  // 전부 끝날 때까지 기다린 뒤 최신 DOM 내용을 기준으로 저장하도록 한다.
  const pendingImageUploadsRef = useRef([]);

  // 최초 진입 시(수정 등) 저장된 내용을 편집기에 채워넣고, 이미지가 있으면 인증이 필요한 서버 이미지를 다시 불러옴
  useEffect(() => {
    if (contentLoadedRef.current || !contentRef.current) return;
    contentLoadedRef.current = true;
    contentRef.current.innerHTML = form.content || '';
    hydrateContentImages();
  }, []);

  const hydrateContentImages = async () => {
    if (!contentRef.current) return;
    // 미리보기와 동일하게, 크기 조절 래퍼가 없는(예전에 작성된) 이미지도 항상 폭 제한을
    // 걸어 편집 영역 밖으로 넘치지 않도록 안전장치를 둔다.
    const applyBrokenFallback = (img) => {
      img.alt = '(이미지를 불러올 수 없습니다)';
      img.style.border = '1px dashed #ccc';
      img.style.minHeight = '40px';
    };
    Array.from(contentRef.current.querySelectorAll('img')).forEach((img) => {
      img.style.maxWidth = '100%';
      if (!img.closest('.mm-img-resize-wrap')) img.style.height = img.style.height || 'auto';
      img.onerror = () => applyBrokenFallback(img);
      // 편집기에 innerHTML을 채워넣는 시점(위 useEffect)에 브라우저가 곧바로 이미지 로드를
      // 시도하므로, 죽은 blob: 주소를 가진 <img>는 이 함수가 실행되기 전에 이미 로드 실패가
      // 끝나 있어 onerror가 다시 발생하지 않는 경우가 있다 — 지금 이미 깨진 상태인지 직접
      // 확인해서 즉시 대체 문구를 적용한다(미리보기 화면과 동일한 처리).
      if (img.src && img.complete && img.naturalWidth === 0) applyBrokenFallback(img);
    });
    const imgs = Array.from(contentRef.current.querySelectorAll('img[data-filename]'));
    for (const img of imgs) {
      const filename = img.getAttribute('data-filename');
      if (!filename) continue;
      try {
        const res = await API.get('/meeting-minutes/image/'+filename, { responseType:'blob' });
        img.onload = () => { img.alt = ''; img.style.border = ''; img.style.minHeight = ''; };
        img.src = window.URL.createObjectURL(res.data);
      } catch (e) { /* 서버에도 파일이 없는 경우 — 위에서 적용한 대체 문구를 그대로 둔다 */ }
    }
  };

  const handleContentInput = () => {
    if (contentRef.current) setForm(f => ({ ...f, content: contentRef.current.innerHTML }));
  };

  // 회의내용 작성 안내문구 노출 여부 — 태그를 걷어내고 실제 텍스트가 하나도 없을 때만(글 작성 전) 보여줌
  const contentIsEmpty = !String(form.content || '').replace(/<[^>]*>/g, '').trim();

  // 클립보드에 이미지가 있으면 바로 삽입(로컬 미리보기) 후 서버에 업로드해서 나중에도 다시 불러올 수 있게 함
  const handleContentPaste = (e) => {
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    let imageFile = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.startsWith('image/')) {
        imageFile = items[i].getAsFile();
        break;
      }
    }
    if (!imageFile) return; // 이미지가 아니면 기본 붙여넣기 동작 사용

    e.preventDefault();
    const localUrl = window.URL.createObjectURL(imageFile);
    document.execCommand('insertImage', false, localUrl);

    const imgs = contentRef.current ? contentRef.current.querySelectorAll(`img[src="${localUrl}"]`) : [];
    const insertedImg = imgs[imgs.length - 1];
    // 2026-09: "이미지 크기 조절" 요청 대응 — 붙여넣은 이미지를 크기 조절이 가능한(마우스 드래그)
    // <span>으로 감싼다. object-fit:contain으로 비율을 유지한 채 래퍼 크기에 맞춰 채워지고,
    // 사용자가 오른쪽 아래 모서리를 드래그하면 래퍼 크기가 바뀌면서 이미지도 함께 조절된다.
    if (insertedImg) {
      insertedImg.style.maxWidth = '100%';
      insertedImg.onerror = () => { insertedImg.alt = '(이미지를 불러올 수 없습니다)'; insertedImg.style.border = '1px dashed #ccc'; insertedImg.style.minHeight = '40px'; };
      const wrapperEl = document.createElement('span');
      wrapperEl.className = 'mm-img-resize-wrap';
      wrapperEl.contentEditable = 'false';
      wrapperEl.style.cssText = 'display:inline-block; resize:both; overflow:hidden; max-width:100%; width:320px; height:220px; vertical-align:top; margin:2px;';
      insertedImg.parentNode.insertBefore(wrapperEl, insertedImg);
      wrapperEl.appendChild(insertedImg);
      insertedImg.style.width = '100%';
      insertedImg.style.height = '100%';
      insertedImg.style.objectFit = 'contain';
      // 이미지가 실제로 로드되면 원본 비율에 가깝게 초기 크기를 맞춰준다(정사각형으로 찌그러져
      // 보이지 않도록). 이후에는 사용자가 자유롭게 드래그로 조절 가능.
      insertedImg.onload = () => {
        const nw = insertedImg.naturalWidth || 320;
        const nh = insertedImg.naturalHeight || 220;
        const maxW = 480;
        const scale = nw > maxW ? maxW / nw : 1;
        wrapperEl.style.width = Math.round(nw * scale) + 'px';
        wrapperEl.style.height = Math.round(nh * scale) + 'px';
      };
    }

    const uploadPromise = (async () => {
      try {
        const fd = new FormData();
        fd.append('image', imageFile, imageFile.name || 'pasted.png');
        const res = await API.post('/meeting-minutes/upload-image', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
        if (insertedImg && res.data.filename) insertedImg.setAttribute('data-filename', res.data.filename);
      } catch (err) {
        // 업로드에 실패해도 화면에는 미리보기가 남아있음(단, 나중에 다시 열면 안 보일 수 있음)
      }
      handleContentInput();
    })();
    pendingImageUploadsRef.current.push(uploadPromise);
  };

  // 주간회의/경영회의/일반회의를 고르면 실제 업무/일정 생성은 저장하기(완료) 시점으로 미룸
  // (회의록 한 건당 "회의유형 - 회의제목" 형태의 업무를 각각 새로 만들기 때문에, 이 시점엔 제목이 아직
  //  확정되지 않았을 수 있어 화면에 표시할 자리표시(placeholder)만 잡아둔다)
  // - 업무내용 선택 시 별도 "선택" 버튼 없이 바로 확정됨(선택하는 즉시 반영)
  const selectPickerTask = (value) => {
    if (!value) return;

    if (value.startsWith('special:')) {
      const mt = value.slice('special:'.length);
      setLinkedTask({ id: null, category: '일반업무', title: mt });
      setMeetingType(mt);
      return;
    }

    const t = (tasks||[]).find(x => String(x.id) === String(value));
    if (!t) return;
    setLinkedTask({ id: t.id, category: t.category, title: t.title });
    setMeetingType(null);
  };

  // 임시저장 목록 불러오기 (작성자 본인 또는 최고관리자만 서버에서 필터되어 내려옴)
  const openDraftsModal = async () => {
    setShowDraftsModal(true);
    setLoadingDrafts(true);
    try {
      const res = await API.get('/meeting-minutes/drafts');
      setDrafts(res.data);
    } catch (err) {
      setDrafts([]);
    } finally {
      setLoadingDrafts(false);
    }
  };

  // 임시저장 목록에서 항목을 선택하면 현재 작성창에 그대로 불러옴(수정 이어서 진행)
  const loadDraft = (d) => {
    setId(d.id);
    setForm({
      title:              d.title              || '',
      meeting_date:       d.meeting_date       || getKstDateString(),
      meeting_start_time: d.meeting_start_time || '',
      meeting_end_time:   d.meeting_end_time   || '',
      location:           d.location           || '',
      attendees:          d.attendees          || '',
      content:            d.content            || '',
    });
    setLinkedTask(d.task_id ? { id: d.task_id, category: d.task_category, title: d.task_title } : null);
    setMeetingType(d.meeting_type || null);
    setSavedFilePath(d.file_path || null);
    setVisibility(d.visibility || '부서공개');
    try { const arr = JSON.parse(d.visible_departments || '[]'); setVisibleDepartments(Array.isArray(arr) ? arr : []); } catch { setVisibleDepartments([]); }
    try { const arr = JSON.parse(d.visible_users || '[]'); setVisibleUsers(Array.isArray(arr) ? arr : []); } catch { setVisibleUsers([]); }
    setScheduleId(d.schedule_id || null);
    setExternalAttendees(() => {
      if (!d.attendees_external) return [];
      try {
        const arr = JSON.parse(d.attendees_external);
        return Array.isArray(arr) ? arr : [];
      } catch { return []; }
    });
    // 임시저장 목록에서 불러오면 편집기 화면(contentEditable)도 그 내용으로 다시 채워줌
    if (contentRef.current) {
      contentRef.current.innerHTML = d.content || '';
      hydrateContentImages();
    }
    setShowDraftsModal(false);
    setMsg('');
  };

  // 임시저장 목록에서 항목 삭제 (목록 클릭=불러오기와 겹치지 않도록 stopPropagation)
  const deleteDraft = async (e, draftId) => {
    e.stopPropagation();
    if (!window.confirm('이 임시저장 항목을 삭제하시겠습니까?')) return;
    try {
      await API.delete('/meeting-minutes/'+draftId);
      setDrafts(list => list.filter(x => x.id !== draftId));
      // 지금 작성창에 불러와 편집 중이던 항목을 삭제한 경우, 다시 저장할 때 새 항목으로 등록되도록 초기화
      if (id === draftId) { setId(null); setSavedFilePath(null); setScheduleId(null); }
    } catch (err) {
      alert(err.response?.data?.message || '삭제할 수 없습니다.');
    }
  };

  // opts.closeAfter=false 로 호출하면 완료 저장 후에도 모달을 닫지 않음(헤더의 "저장" 버튼에서 사용)
  const doSave = async (status, opts = {}) => {
    const closeAfter = opts.closeAfter !== false;
    if (!form.title.trim()) { setMsg('제목을 입력해주세요.'); return; }
    if (status === '완료' && !linkedTask) { setMsg('해당업무를 선택해주세요. (저장 위치를 정하는 데 필요합니다)'); return; }
    // 방금 붙여넣은 이미지의 업로드가 아직 진행 중이면(data-filename이 아직 안 붙었을 수 있음)
    // 끝날 때까지 기다린 뒤, 편집기의 최신 DOM 내용을 기준으로 저장한다(pendingImageUploadsRef 설명 참조).
    if (pendingImageUploadsRef.current.length) {
      await Promise.all(pendingImageUploadsRef.current);
      pendingImageUploadsRef.current = [];
    }
    const latestContent = contentRef.current ? contentRef.current.innerHTML : form.content;
    setSaving(true);
    try {
      const payload = {
        ...form,
        content: latestContent,
        attendees_external: JSON.stringify(
          externalAttendees.filter(r => (r.company||'').trim() || (r.position||'').trim() || (r.name||'').trim())
        ),
        task_id:       linkedTask?.id       || null,
        task_category: linkedTask?.category || null,
        task_title:    linkedTask?.title    || null,
        meeting_type:  meetingType || null,
        visibility,
        visible_departments: JSON.stringify(visibleDepartments),
        visible_users:       JSON.stringify(visibleUsers),
        status,
      };
      let res;
      if (id) {
        res = await API.put('/meeting-minutes/'+id, payload);
      } else {
        res = await API.post('/meeting-minutes', payload);
        setId(res.data.id);
      }
      if (res.data.file_path) setSavedFilePath(res.data.file_path);
      // 자동연동(주간회의 등)이었다면 이번에 새로 생성된 업무의 실제 id를 받아 반영
      // (그대로 두면 모달을 닫지 않고 다시 저장할 때마다 업무가 중복 생성됨)
      if (res.data.task_id) setLinkedTask(lt => lt ? { ...lt, id: res.data.task_id } : lt);
      // 완료 저장 시 일정관리에 자동등록/갱신된 일정의 id 반영 (재저장 시 새로 만들지 않고 그 일정을 갱신하도록)
      if (res.data.schedule_id) setScheduleId(res.data.schedule_id);
      onTasksChanged && onTasksChanged(); // 업무현황 목록에 즉시 반영
      setSavedOnce(true); // 저장/임시저장에 성공했으므로, 이후 닫을 때는 "저장 안 됨" 경고를 띄우지 않음
      if (status === '완료') {
        onSaved && onSaved();
        // 저장과 동시에 창이 닫히므로, 모달 안의 문구 대신 알림창으로 저장 완료를 알려줌
        alert('저장되었습니다.');
        if (closeAfter) onClose();
      } else {
        setMsg('임시 저장 되었습니다.');
      }
    } catch (err) {
      setMsg(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const dirty = !savedOnce && (form.title || form.content || form.location || form.attendees || externalAttendees.length > 0);
    if (dirty && !window.confirm('작성 중인 내용이 저장되지 않았습니다. 닫으시겠습니까?')) return;
    onClose();
  };

  // 헤더의 "저장" 버튼 — 완료 처리 + 엑셀 생성 후 창을 닫음
  const handleSaveFile = () => doSave('완료');

  // 저장된 엑셀 파일을 그대로 다운로드 (인쇄가 불가능한 환경일 때의 대체 수단)
  const downloadSavedExcel = async () => {
    try {
      const res = await API.get(`/meeting-minutes/${id}/download`, { responseType: 'blob' });
      // 서버가 PDF 변환에 성공하면 PDF를, 변환 도구가 없어 엑셀 원본으로 대체했으면 xlsx를 내려줌
      const isPdf = (res.headers['content-type'] || '').includes('pdf');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const exportFileName = `${(form.title||'회의록').replace(/[\\/:*?"<>|]/g,'_')}.${isPdf ? 'pdf' : 'xlsx'}`;
      a.download = exportFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      logDownload('회의록', exportFileName);
      setMsg(isPdf ? 'PDF 파일을 다운로드했습니다.' : '엑셀 파일을 다운로드했습니다. 엑셀에서 열어 인쇄해주세요.');
    } catch (err) {
      alert('파일을 다운로드할 수 없습니다. 저장 여부를 확인해주세요.');
    }
  };

  // 인쇄 — 저장된 엑셀 파일을 서버에서 PDF로 변환해 바로 인쇄창을 띄움
  //  (서버에 PDF 변환 도구가 없으면 엑셀 파일 다운로드로 대체)
  const handlePrintSaved = async () => {
    if (!id || !savedFilePath) {
      alert('저장 버튼을 눌러 회의록을 먼저 저장해주세요. (임시저장 상태에서는 인쇄할 파일이 없습니다)');
      return;
    }
    logPrint('회의록', form.title || '회의록');
    try {
      const res = await API.get(`/meeting-minutes/${id}/print`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0'; iframe.style.bottom = '0';
      iframe.style.width = '0'; iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = blobUrl;
      // (다른 인쇄 함수와 동일한 이유로) PDF뷰어의 fit-to-page 계산이 끝날 시간을 준다.
      iframe.onload = () => {
        setTimeout(() => {
          try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {}
        }, 600);
      };
      document.body.appendChild(iframe);
    } catch (err) {
      alert('서버에 PDF 변환 도구가 설치되어 있지 않아 바로 인쇄할 수 없습니다. 대신 엑셀 파일을 다운로드합니다.');
      downloadSavedExcel();
    }
  };

  // 참석자확인 표 라이브 미리보기 — 실제 엑셀에 채워질 순서/칸과 동일하게(사내 먼저, 그 다음 사외) 계산
  // 사내 참석자는 이름만 입력하지만, 회원목록에서 이름이 일치하는 사용자를 찾아 소속(부서명)/직급을 자동으로 채움
  const lookupInternalAttendeePreview = (name) => {
    const u = (userList||[]).find(x => normName(x.name) === normName(name));
    return { name, company:(u && u.department) || '', position:(u && u.position) || '' };
  };
  const internalNamesPreview = String(form.attendees || '').split(/[,\n]/).map(s => s.trim()).filter(Boolean);
  const externalPreview = externalAttendees.filter(r => (r.company||'').trim() || (r.position||'').trim() || (r.name||'').trim());
  const attendeeRowsPreview = [
    ...internalNamesPreview.map(lookupInternalAttendeePreview),
    ...externalPreview.map(a => ({ name:a.name||'', company:a.company||'', position:a.position||'' })),
  ];
  const totalAttendeesPreview = attendeeRowsPreview.length;
  const previewG1 = attendeeRowsPreview.slice(0,4);
  const previewG2 = attendeeRowsPreview.slice(4,8);
  while (previewG1.length < 4) previewG1.push({ name:'', company:'', position:'' });
  while (previewG2.length < 4) previewG2.push({ name:'', company:'', position:'' });

  const lbl = { fontSize:12, color:'#888', display:'block', margin:'10px 0 4px', fontWeight:500 };
  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
    fontSize:14, boxSizing:'border-box', outline:'none' };
  // 문서(엑셀서식) 스타일 — MeetingMinuteViewModal과 완전히 동일한 레이아웃/치수로 통일
  const docCellLbl = { padding:'8px 10px', fontWeight:700, fontSize:12, color:'#000', border:'1px solid #000', whiteSpace:'nowrap', textAlign:'center', background:'#fff' };
  const docCellVal = { padding:'8px 10px', fontSize:13, border:'1px solid #000', color:'#222', background:'#fff' };
  const docSectionBar = { padding:'7px 10px', fontWeight:700, fontSize:12, background:'#fff',
    borderTop:'1px solid #000', borderBottom:'1px solid #000', textAlign:'center' };
  const docApprovalLbl = { padding:'2px 6px', width:15, fontSize:12, fontWeight:700, textAlign:'center', border:'1px solid #000', background:'#fff', lineHeight:1.3 };
  const docApprovalHdr = { padding:'4px 12px', width:60, fontSize:12, fontWeight:700, textAlign:'center', border:'1px solid #000', background:'#fff', whiteSpace:'nowrap' };
  const docApprovalVal = { padding:'4px 10px', height:45, minWidth:56, border:'1px solid #000', background:'#fff' };
  const docThStyle = { padding:'6px 4px', fontSize:11, fontWeight:700, textAlign:'center', border:'1px solid #000', background:'#fff' };
  const docTdStyle = { padding:'0 4px', height:25, fontSize:12, textAlign:'center', border:'1px solid #000', background:'#fff', boxSizing:'border-box' };
  const docInput = { width:'100%', padding:'6px 8px', border:'1px solid #ddd', borderRadius:6,
    fontSize:13, boxSizing:'border-box', outline:'none' };

  // 작성자는 보기화면과 동일하게 위 칸엔 부서, 아래 칸엔 "직급 이름"으로 표시
  // 신규 작성 시엔 로그인 정보(sessionStorage)에 부서·직급이 이미 들어있어 바로 채울 수 있고(일반 회원도 가능),
  // 수정 시엔 최초 작성자와 지금 로그인한 사용자가 다를 수 있어 회원목록에서 이름으로 찾아 보완합니다.
  //  (단, 회원목록 조회는 관리자 권한에서만 내려오므로 일반 회원이 남의 글을 수정하는 경우엔 공란일 수 있음)
  const authorUserMatch = (userList||[]).find(u => normName(u.name) === normName(authorName));
  const authorDepartment = !editData
    ? (currentUser.department || '')
    : ((authorUserMatch && authorUserMatch.department) || currentUser.department || '');
  const authorPosition = !editData
    ? (currentUser.position || '')
    : ((authorUserMatch && authorUserMatch.position) || currentUser.position || '');
  const authorPosName = authorPosition ? `${authorPosition} ${authorName}` : authorName;

  // 보기화면과 동일한 하단 출력정보 표시용
  const previewPrintedAt = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  })();

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, width:860, maxWidth:'96vw', maxHeight:'90vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        {/* 헤더 */}
        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #f0f0f0',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <h3 style={{ fontSize:18, fontWeight:700, margin:0 }}>📝 회의록 작성</h3>
            <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:'#555',
              fontWeight:500, cursor:'pointer' }}>
              📅 일정관리 등록
              <input type="checkbox" checked={form.add_to_schedule==='1'}
                onChange={e => setForm(f => ({ ...f, add_to_schedule: e.target.checked ? '1' : '0' }))}
                style={{ accentColor:'#185FA5' }} />
            </label>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={openDraftsModal} disabled={drafts.length===0}
              style={{ background: drafts.length===0 ? '#cbd5e1' : '#0EA5E9', color:'#fff', border:'none', borderRadius:6,
                padding:'6px 12px', fontSize:12, fontWeight:600, cursor: drafts.length===0 ? 'not-allowed' : 'pointer' }}>
              📂 임시저장 목록
            </button>
            <button onClick={() => doSave('임시저장')} disabled={saving}
              style={{ background:'#854F0B', color:'#fff', border:'none', borderRadius:6,
                padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              💾 임시저장
            </button>
            <button onClick={handleSaveFile} disabled={saving}
              style={{ background:'#0F6E56', color:'#fff', border:'none', borderRadius:6,
                padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              💾 저장
            </button>
            <button onClick={handlePrintSaved}
              style={{ background:'#475569', color:'#fff', border:'none', borderRadius:6,
                padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              🖨️ 인쇄
            </button>
            <button onClick={handleCancel}
              style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        <div style={{ padding:'20px 24px' }}>
          {/* 공개범위 + 해당업무(구분/업무내용) 한 줄로 표기 */}
          <label style={lbl}>공개범위 / 해당업무</label>
          <div style={{ display:'flex', gap:8, marginBottom:4 }}>
            <select value={visibility} onChange={e => setVisibility(e.target.value)}
              style={{ ...inputStyle, flex:1, minWidth:0 }}>
              <option value="">공개범위</option>
              {scopeOptions.map(s => <option key={s.id} value={s.name}>{formatScopeLabel(s)}</option>)}
            </select>

            {linkedTask ? (
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'0 12px', flex:2, minWidth:0,
                background: meetingType ? '#FDF4E3' : '#E6F1FB', borderRadius:8, fontSize:13 }}>
                <span style={{ color: meetingType ? '#92400E' : '#185FA5', fontWeight:600, overflow:'hidden',
                  textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {meetingType ? `📌 ${meetingType} (자동연동)` : `🔗 [${linkedTask.category}] ${linkedTask.title}`}
                </span>
                <button onClick={() => { setLinkedTask(null); setMeetingType(null); }}
                  style={{ background:'none', border:'none', color: meetingType ? '#92400E' : '#185FA5',
                    cursor:'pointer', fontSize:12, marginLeft:'auto', flexShrink:0 }}>
                  해제
                </button>
              </div>
            ) : (
              <>
                <select value={pickerCategory} onChange={e => { setPickerCategory(e.target.value); setPickerTaskId(''); }}
                  style={{ ...inputStyle, flex:1, minWidth:0 }}>
                  <option value="">구분 선택</option>
                  {catMeta.fullList.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={pickerTaskId}
                  onChange={e => { setPickerTaskId(e.target.value); selectPickerTask(e.target.value); }}
                  disabled={!pickerCategory}
                  style={{ ...inputStyle, flex:1.4, minWidth:0 }}>
                  <option value="">업무내용 선택</option>
                  {(tasks||[]).filter(t=>t.category===pickerCategory).map(t =>
                    <option key={t.id} value={String(t.id)}>{t.title}</option>
                  )}
                  {pickerCategory === '일반업무' && (
                    <optgroup label="회의 유형">
                      {GENERAL_MEETING_TYPES.map(mt =>
                        <option key={'special:'+mt} value={'special:'+mt}>📌 {mt}</option>
                      )}
                    </optgroup>
                  )}
                </select>
              </>
            )}
          </div>
          <div style={{ fontSize:11, color:'#aaa', margin:'2px 0 14px' }}>
            ※ 주간회의 등 자동연동 업무를 새로 만들 때 이 공개범위가 함께 적용됩니다.
          </div>

          {/* ── 회의록 보기화면과 완전히 동일한 레이아웃의 문서형 작성 영역 ── */}
          <div style={{ border:'2px solid #000' }}>
            {/* 제목 + 결재란 */}
            <div style={{ display:'flex', borderBottom:'2px solid #000' }}>
              <div style={{ flex:1, padding:'16px 0', textAlign:'center', fontSize:24, fontWeight:800,
                letterSpacing:10, background:'#fff' }}>
                회 의 록
              </div>
              <table style={{ borderCollapse:'collapse', borderLeft:'2px solid #000' }}>
                <tbody>
                  <tr>
                    <td rowSpan={2} style={docApprovalLbl}><div>결</div><div>재</div></td>
                    <td style={docApprovalHdr}>담당</td>
                    <td style={docApprovalHdr}>검토</td>
                    <td style={docApprovalHdr}>승인</td>
                  </tr>
                  <tr>
                    <td style={docApprovalVal}></td>
                    <td style={docApprovalVal}></td>
                    <td style={docApprovalVal}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <tbody>
                <tr>
                  <td style={docCellLbl}>문서번호</td>
                  <td style={{ ...docCellVal, width:190, textAlign:'center' }}>-</td>
                  <td style={{ ...docCellLbl, width:80 }}>작성일</td>
                  <td style={{ ...docCellVal, width:90, textAlign:'center' }}>{todayDisplay}</td>
                  <td rowSpan={2} style={{ ...docCellLbl, width:93 }}>작성자</td>
                  <td style={{ ...docCellVal, width:148, textAlign:'center' }}>{authorDepartment}</td>
                </tr>
                <tr>
                  <td style={docCellLbl}>회의일시</td>
                  <td style={{ ...docCellVal, textAlign:'center' }} colSpan={3}>
                    <div style={{ display:'flex', gap:4, alignItems:'center', justifyContent:'center' }}>
                      <input type="date" name="meeting_date" value={form.meeting_date} onChange={handleChange} style={{ ...docInput, flex:'0 1 150px' }} />
                      <TimeField24 value={form.meeting_start_time} onChange={v => setForm(f => ({ ...f, meeting_start_time: v }))} style={{ width:70 }} />
                      <span style={{ color:'#888' }}>~</span>
                      <TimeField24 value={form.meeting_end_time} onChange={v => setForm(f => ({ ...f, meeting_end_time: v }))} style={{ width:70 }} />
                    </div>
                  </td>
                  <td style={{ ...docCellVal, width:148, textAlign:'center' }}>{authorPosName}</td>
                </tr>
                <tr>
                  <td style={docCellLbl}>회의장소</td>
                  <td style={docCellVal} colSpan={3}>
                    <input name="location" value={form.location} onChange={handleChange} placeholder="회의 장소" style={docInput} />
                  </td>
                  <td style={{ ...docCellLbl, width:93 }}>참석인원</td>
                  <td style={{ ...docCellVal, width:148, textAlign:'center' }}>{totalAttendeesPreview}명</td>
                </tr>
                <tr>
                  <td style={docCellLbl}>회의안건</td>
                  <td style={docCellVal} colSpan={5}>
                    <input name="title" value={form.title} onChange={handleChange}
                      placeholder="회의록 제목 (필수)" style={docInput} />
                  </td>
                </tr>
              </tbody>
            </table>

            <div style={docSectionBar}>회 의 내 용</div>
            <div style={{ minHeight:340 }}>
              {contentIsEmpty && (
                <div style={{ fontSize:11, color:'#aaa', margin:'14px 16px 10px', padding:'8px 10px',
                  background:'#f8fafc', borderRadius:6, lineHeight:1.6 }}>
                  📎 이미지(스크린샷 등)를 복사해서 그대로 붙여넣을 수 있습니다. (화면에는 보이지만 엑셀에는 텍스트로만 반영됩니다)
                  <br />
                  ✍️ 아래와 같은 형식으로 작성하면 보기 좋게 정리됩니다.
                  <div style={{ marginTop:4, padding:'6px 10px', background:'#fff', border:'1px solid #eee', borderRadius:6,
                    fontFamily:'monospace', whiteSpace:'pre', color:'#555' }}>
{`1. 대분류
  1.1 소분류
    -. 내용`}
                  </div>
                </div>
              )}
              <div
                ref={contentRef}
                contentEditable
                onInput={handleContentInput}
                onPaste={handleContentPaste}
                className="mm-content"
                style={{ minHeight:160, maxHeight:320, overflowY:'auto', overflowX:'auto', padding:'14px 16px', fontSize:13,
                  fontFamily:'inherit', lineHeight:'28px', cursor:'text', outline:'none' }}
              />
            </div>

            <div style={docSectionBar}>참 석 자 확 인</div>
            <div style={{ padding:'12px' }}>
              <label style={{ ...lbl, margin:'0 0 4px' }}>사내 참석자</label>
              <input name="attendees" value={form.attendees} onChange={handleChange}
                placeholder="예: 홍길동, 김철수" style={inputStyle} />
              <div style={{ fontSize:11, color:'#aaa', marginTop:2, marginBottom:10 }}>
                이름을 쉼표(,)로 구분해서 입력하면 회원목록에서 자동으로 소속(부서명)/직급을 찾아 채웁니다.
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'0 0 4px' }}>
                <label style={{ ...lbl, margin:0 }}>사외 참석자</label>
                <button type="button" onClick={addExternalRow}
                  style={{ fontSize:12, padding:'4px 10px', background:'#EDE9FE', color:'#7C3AED',
                    border:'1px solid #7C3AED', borderRadius:6, cursor:'pointer', fontWeight:600 }}>
                  + 추가
                </button>
              </div>
              {externalAttendees.length === 0 ? (
                <div style={{ fontSize:12, color:'#aaa', padding:'6px 0 10px' }}>
                  사외 참석자가 있으면 추가해주세요. (소속/직급/성명 입력)
                </div>
              ) : (
                <div style={{ marginBottom:10 }}>
                  {externalAttendees.map((row, idx) => (
                    <div key={idx} style={{ display:'flex', gap:6, marginBottom:6, alignItems:'center' }}>
                      <input placeholder="소속" value={row.company}
                        onChange={e => updateExternalRow(idx, 'company', e.target.value)}
                        style={{ ...inputStyle, flex:1 }} />
                      <input placeholder="직급" value={row.position}
                        onChange={e => updateExternalRow(idx, 'position', e.target.value)}
                        style={{ ...inputStyle, flex:1 }} />
                      <input placeholder="성명" value={row.name}
                        onChange={e => updateExternalRow(idx, 'name', e.target.value)}
                        style={{ ...inputStyle, flex:1.2 }} />
                      <button type="button" onClick={() => removeExternalRow(idx)}
                        style={{ background:'none', border:'none', color:'#dc2626', fontSize:16, cursor:'pointer',
                          padding:'0 4px', flexShrink:0 }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* 실제 엑셀/보기화면에 채워질 참석자확인 표와 동일한 모양의 라이브 미리보기 */}
              <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>
                아래는 입력하신 참석자가 실제 서식에 채워질 모습입니다 (최대 8명):
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <tbody>
                  <tr>
                    <td rowSpan={5} style={{ ...docApprovalLbl, width:56 }}><div>참석자</div><div>확인</div></td>
                    <th style={docThStyle}>소속</th><th style={{...docThStyle, width:70}}>직급</th><th style={{...docThStyle, width:80}}>성명</th><th style={{...docThStyle, width:80}}>서명</th>
                    <th style={docThStyle}>소속</th><th style={{...docThStyle, width:70}}>직급</th><th style={{...docThStyle, width:80}}>성명</th><th style={{...docThStyle, width:80}}>서명</th>
                  </tr>
                  {[0,1,2,3].map(i => (
                    <tr key={i}>
                      <td style={docTdStyle}>{previewG1[i].company}</td>
                      <td style={{ ...docTdStyle, width:70 }}>{previewG1[i].position}</td>
                      <td style={{ ...docTdStyle, width:80 }}>{previewG1[i].name}</td>
                      <td style={{ ...docTdStyle, width:80 }}></td>
                      <td style={docTdStyle}>{previewG2[i].company}</td>
                      <td style={{ ...docTdStyle, width:70 }}>{previewG2[i].position}</td>
                      <td style={{ ...docTdStyle, width:80 }}>{previewG2[i].name}</td>
                      <td style={{ ...docTdStyle, width:80 }}></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalAttendeesPreview > 8 && (
                <div style={{ fontSize:11, color:'#aaa', marginTop:4 }}>
                  ※ 참석자확인란은 최대 8명까지만 표시됩니다. (전체 참석자 {totalAttendeesPreview}명)
                </div>
              )}
            </div>
          </div>

          {/* 보기화면과 동일한 형태의 출력정보 표시(실제 값은 저장/인쇄 시점에 반영됨) */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
            marginTop:10, padding:'8px 12px', borderTop:'1px solid #ccc', fontSize:11, color:'#666' }}>
            <span>출력일시: {previewPrintedAt}</span>
            <span>1/1</span>
            <span>출력자: {formatPrintedBy(currentUser)}</span>
          </div>

          {msg && <div style={{ fontSize:13, color:'#185FA5', marginTop:8 }}>{msg}</div>}
        </div>
      </div>

      {/* 임시저장 목록 — 작성자 본인/최고관리자만 보이도록 서버에서 필터되어 내려옴 */}
      {showDraftsModal && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
          <div style={{ background:'#fff', borderRadius:12, width:480, maxWidth:'90vw', maxHeight:'80vh',
            overflowY:'auto', padding:20 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <h4 style={{ margin:0, fontSize:16, fontWeight:700 }}>📂 임시저장 목록</h4>
              <button onClick={() => setShowDraftsModal(false)}
                style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:'#999' }}>✕</button>
            </div>
            {loadingDrafts ? (
              <div style={{ color:'#999', fontSize:13, padding:'20px 0', textAlign:'center' }}>불러오는 중...</div>
            ) : drafts.length === 0 ? (
              <div style={{ color:'#999', fontSize:13, padding:'20px 0', textAlign:'center' }}>임시저장된 회의록이 없습니다.</div>
            ) : (
              drafts.map(d => (
                <div key={d.id} onClick={() => loadDraft(d)}
                  style={{ padding:'10px 12px', border:'1px solid #eee', borderRadius:8, marginBottom:8, cursor:'pointer',
                    display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}
                  onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:14 }}>{d.title || '(제목 없음)'}</div>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                      {d.meeting_date || '-'} · {d.created_by || '-'} · {(d.updated_at || d.created_at || '').slice(0,16)}
                    </div>
                  </div>
                  <button onClick={e => deleteDraft(e, d.id)}
                    style={{ flexShrink:0, background:'#fee2e2', color:'#dc2626', border:'1px solid #dc2626',
                      borderRadius:6, padding:'5px 9px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    🗑 삭제
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// 연구노트(전자연구노트/ELN 참고) — 업무관리 > 개발/지원 탭 전용 (요청사항 1~7)
// 회의록과 동일한 "목록 모달 + 작성 폼 + 보기 모달" 패턴을 따르되, 우선은
// 목적/재료및방법/실험절차/결과/고찰 5개 섹션의 자유기록형 리치텍스트로 구성한다.
// (요청사항 7: 우선 이렇게 구성 후 화면 확인 → 추가수정 예정, v1은 단순하게 유지)
// ══════════════════════════════════════════════════════════════════════════

const RN_SECTIONS = [
  { key: 'purpose',            label: '목적' },
  { key: 'materials_methods',  label: '재료 및 방법' },
  { key: 'procedure',          label: '실험절차/과정' },
  { key: 'results',            label: '결과' },
  { key: 'discussion',         label: '고찰 및 결론' },
];

// 연구노트 본문 섹션 안의 <img data-filename="..."> 를 실제 파일로 교체(회의록/업무관리와 동일 패턴)
function hydrateResearchNoteImages(node) {
  if (!node) return;
  const imgs = Array.from(node.querySelectorAll('img'));
  imgs.forEach(img => {
    img.style.maxWidth = '100%';
    const filename = img.getAttribute('data-filename');
    if (!filename) return;
    API.get('/research-notes/image/' + filename, { responseType: 'blob' })
      .then(res => { img.src = window.URL.createObjectURL(res.data); })
      .catch(() => {
        img.alt = '(이미지를 불러올 수 없습니다)';
        img.style.border = '1px dashed #ccc';
        img.style.minHeight = '40px';
      });
  });
}

// ── 연구노트 목록 모달 ──
// - 헤더 "🧪 연구노트" 버튼(요청 1/2): category만 채워서 호출 → 해당 탭(제품개발/지원사업) 전체 목록
// - 업무상세 모달의 "연구노트" 버튼(요청 3/4): taskId를 채워서 호출 → 그 업무에 연결된 목록만
function ResearchNotesListModal({ category, taskId, presetTask, canWrite, onClose, title }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list | form | view
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    const url = taskId
      ? '/research-notes/task/' + taskId
      : '/research-notes' + (category ? ('?category=' + encodeURIComponent(category)) : '');
    API.get(url).then(r => setList(r.data)).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(load, [category, taskId]);

  if (mode === 'form') {
    return (
      <ResearchNoteForm
        editData={selected}
        presetTask={presetTask}
        defaultCategory={category}
        onClose={() => { setMode('list'); setSelected(null); }}
        onSaved={() => { setMode('list'); setSelected(null); load(); }}
      />
    );
  }
  if (mode === 'view' && selected) {
    return (
      <ResearchNoteViewModal
        note={selected}
        canWrite={canWrite}
        onClose={() => { setMode('list'); setSelected(null); }}
        onEdit={() => setMode('form')}
        onDeleted={() => { setMode('list'); setSelected(null); load(); }}
      />
    );
  }

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:640, maxWidth:'92vw',
        maxHeight:'85vh', display:'flex', flexDirection:'column' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:18, fontWeight:700, margin:0 }}>🧪 {title || '연구노트'}</h3>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {canWrite && (
              <button onClick={() => { setSelected(null); setMode('form'); }}
                style={{ background:'#0891B2', color:'#fff', border:'none', borderRadius:8,
                  padding:'8px 14px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                + 연구노트 작성
              </button>
            )}
            <button onClick={onClose}
              style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        <div style={{ overflowY:'auto', flex:1 }}>
          {loading ? (
            <div style={{ textAlign:'center', color:'#999', padding:'30px 0' }}>불러오는 중...</div>
          ) : list.length === 0 ? (
            <div style={{ textAlign:'center', color:'#999', padding:'30px 0', fontSize:13 }}>
              작성된 연구노트가 없습니다.
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ borderBottom:'2px solid #e5e7eb', color:'#666' }}>
                  <th style={{ textAlign:'left', padding:'8px 6px' }}>제목</th>
                  <th style={{ textAlign:'center', padding:'8px 6px', width:100 }}>구분</th>
                  <th style={{ textAlign:'center', padding:'8px 6px', width:100 }}>작성일</th>
                  <th style={{ textAlign:'center', padding:'8px 6px', width:90 }}>작성자</th>
                </tr>
              </thead>
              <tbody>
                {list.map(n => (
                  <tr key={n.id} onClick={() => { setSelected(n); setMode('view'); }}
                    style={{ borderBottom:'1px solid #f1f1f1', cursor:'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding:'8px 6px', fontWeight:600, color:'#185FA5' }}>{n.title}</td>
                    <td style={{ padding:'8px 6px', textAlign:'center', color:'#666' }}>{n.task_category || '-'}</td>
                    <td style={{ padding:'8px 6px', textAlign:'center', color:'#666' }}>{n.note_date || '-'}</td>
                    <td style={{ padding:'8px 6px', textAlign:'center', color:'#666' }}>{n.created_by || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 연구노트 작성/수정 폼 ── (전자연구노트 참고: 목적/재료및방법/실험절차/결과/고찰 5섹션)
function ResearchNoteForm({ editData, presetTask, defaultCategory, onClose, onSaved }) {
  const { catMeta } = useTaskCategories();
  const isDevOrSupport = (c) => c === '제품개발' || c === '지원사업';

  const [form, setForm] = useState({
    title: editData?.title || '',
    note_date: editData?.note_date || getKstDateString(),
    task_category: editData?.task_category || (isDevOrSupport(defaultCategory) ? defaultCategory
      : (presetTask ? presetTask.category : (catMeta.fullList.find(isDevOrSupport) || ''))),
    task_id: editData?.task_id ?? (presetTask ? presetTask.id : null),
    task_title: editData?.task_title || (presetTask ? presetTask.title : ''),
    reference_note: editData?.reference_note || '',
  });
  const [sections, setSections] = useState(() => {
    const o = {};
    RN_SECTIONS.forEach(s => { o[s.key] = editData?.[s.key] || ''; });
    return o;
  });
  const [loading, setLoading] = useState(false);

  // 섹션별 contentEditable ref (5개) — TaskForm의 "붙여넣기 이미지" 패턴을 그대로 재사용
  const refs = useRef({});
  const loadedRef = useRef(false);
  const pendingUploadsRef = useRef([]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    RN_SECTIONS.forEach(s => {
      const node = refs.current[s.key];
      if (node) { node.innerHTML = sections[s.key] || ''; hydrateResearchNoteImages(node); }
    });
    // eslint-disable-next-line
  }, []);

  const handleInput = (key) => {
    const node = refs.current[key];
    if (node) setSections(s => ({ ...s, [key]: node.innerHTML }));
  };

  const handlePaste = (key) => (e) => {
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    let imageFile = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.startsWith('image/')) { imageFile = items[i].getAsFile(); break; }
    }
    if (!imageFile) return;
    e.preventDefault();
    const node = refs.current[key];
    const localUrl = window.URL.createObjectURL(imageFile);
    document.execCommand('insertImage', false, localUrl);
    const imgs = node ? node.querySelectorAll(`img[src="${localUrl}"]`) : [];
    const insertedImg = imgs[imgs.length - 1];
    if (insertedImg) insertedImg.style.maxWidth = '100%';

    const uploadPromise = (async () => {
      try {
        const fd = new FormData();
        fd.append('image', imageFile, imageFile.name || 'pasted.png');
        const res = await API.post('/research-notes/upload-image', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
        if (insertedImg && res.data.filename) insertedImg.setAttribute('data-filename', res.data.filename);
      } catch (e) { /* 미리보기는 유지되지만 다음에 다시 열면 안보일 수 있음 */ }
      handleInput(key);
    })();
    pendingUploadsRef.current.push(uploadPromise);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) return alert('제목을 입력해주세요.');
    if (pendingUploadsRef.current.length) {
      await Promise.all(pendingUploadsRef.current);
      pendingUploadsRef.current = [];
    }
    const latest = { ...sections };
    RN_SECTIONS.forEach(s => {
      const node = refs.current[s.key];
      if (node) latest[s.key] = node.innerHTML;
    });
    setLoading(true);
    try {
      const payload = { ...form, ...latest };
      if (editData) await API.put('/research-notes/' + editData.id, payload);
      else          await API.post('/research-notes', payload);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const inp = { width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:8,
    fontSize:14, boxSizing:'border-box', marginBottom:12, outline:'none' };
  const lbl = { fontSize:13, color:'#333', fontWeight:600, display:'block', marginBottom:6 };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:640, maxWidth:'92vw',
        maxHeight:'90vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ fontSize:18, fontWeight:700, margin:0 }}>🧪 {editData ? '연구노트 수정' : '연구노트 작성'}</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1.4fr', gap:10 }}>
          <div>
            <label style={lbl}>구분</label>
            <select value={form.task_category} onChange={e => setForm(f => ({ ...f, task_category: e.target.value }))}
              disabled={!!presetTask} style={inp}>
              {catMeta.fullList.filter(isDevOrSupport).map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>작성일</label>
            <input type="date" value={form.note_date} onChange={e => setForm(f => ({ ...f, note_date: e.target.value }))} style={inp} />
          </div>
          <div>
            <label style={lbl}>
              연동업무 {presetTask ? '' : '(선택)'}
            </label>
            <input value={form.task_title} disabled={!!presetTask}
              onChange={e => setForm(f => ({ ...f, task_title: e.target.value }))}
              placeholder="연결할 업무 제목 (선택)" style={inp} />
          </div>
        </div>

        <label style={lbl}>제목 *</label>
        <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="연구노트 제목" style={inp} />

        {RN_SECTIONS.map(s => (
          <div key={s.key}>
            <label style={lbl}>
              {s.label} <span style={{ fontSize:11, color:'#aaa', fontWeight:400 }}>(스크린샷 붙여넣기 가능)</span>
            </label>
            <div
              ref={el => { refs.current[s.key] = el; }}
              contentEditable
              suppressContentEditableWarning
              onInput={() => handleInput(s.key)}
              onPaste={handlePaste(s.key)}
              style={{ ...inp, height:80, overflowY:'auto', lineHeight:1.6, cursor:'text' }}
            />
          </div>
        ))}

        <label style={lbl}>참고사항/비고</label>
        <textarea value={form.reference_note} onChange={e => setForm(f => ({ ...f, reference_note: e.target.value }))}
          rows={2} style={{ ...inp, resize:'vertical', fontFamily:'inherit' }} />

        <div style={{ display:'flex', gap:8, marginTop:8 }}>
          <button onClick={onClose} disabled={loading}
            style={{ flex:1, padding:'11px 0', background:'#f1f1f1', color:'#555', border:'none',
              borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
            취소
          </button>
          <button onClick={handleSubmit} disabled={loading}
            style={{ flex:1, padding:'11px 0', background:'#0891B2', color:'#fff', border:'none',
              borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', opacity:loading?0.6:1 }}>
            {loading ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 연구노트 보기(읽기 전용) 모달 ──
function ResearchNoteViewModal({ note, canWrite, onClose, onEdit, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const refs = useRef({});

  useEffect(() => {
    RN_SECTIONS.forEach(s => hydrateResearchNoteImages(refs.current[s.key]));
    // eslint-disable-next-line
  }, [note.id]);

  const handleDelete = async () => {
    if (!window.confirm('이 연구노트를 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.')) return;
    setDeleting(true);
    try {
      await API.delete('/research-notes/' + note.id);
      onDeleted && onDeleted();
    } catch (err) {
      alert(err.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const lbl = { fontSize:13, color:'#333', fontWeight:600, display:'block', marginBottom:6 };
  const box = { padding:'10px 12px', border:'1px solid #eee', borderRadius:8, fontSize:14,
    lineHeight:1.6, marginBottom:14, minHeight:24, background:'#fafafa' };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:640, maxWidth:'92vw',
        maxHeight:'90vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <h3 style={{ fontSize:18, fontWeight:700, margin:0 }}>🧪 {note.title}</h3>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>
        <div style={{ fontSize:12, color:'#888', marginBottom:16 }}>
          {note.task_category || '-'} {note.task_title ? ' · ' + note.task_title : ''} · {note.note_date || '-'} · 작성자 {note.created_by || '-'}
          {note.updated_at ? ` (수정: ${note.updated_at})` : ''}
        </div>

        {RN_SECTIONS.map(s => (
          <div key={s.key}>
            <label style={lbl}>{s.label}</label>
            <div ref={el => { refs.current[s.key] = el; }} style={box}
              dangerouslySetInnerHTML={{ __html: note[s.key] || '<span style="color:#bbb">(내용 없음)</span>' }} />
          </div>
        ))}

        {note.reference_note && (
          <>
            <label style={lbl}>참고사항/비고</label>
            <div style={{ ...box, whiteSpace:'pre-wrap' }}>{note.reference_note}</div>
          </>
        )}

        <div style={{ display:'flex', gap:8, marginTop:8 }}>
          {canWrite && (
            <>
              <button onClick={onEdit}
                style={{ flex:1, padding:'11px 0', background:'#EFF6FF', color:'#185FA5', border:'1px solid #185FA5',
                  borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer' }}>
                수정
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ flex:1, padding:'11px 0', background:'#FEF2F2', color:'#dc2626', border:'1px solid #dc2626',
                  borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', opacity:deleting?0.6:1 }}>
                {deleting ? '삭제 중...' : '삭제'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
